/*!
 * Black Dashboard PRO React - v1.0.0
 * Product Page: https://www.creative-tim.com/product/black-dashboard-pro-react
 * Copyright 2019 Creative Tim (https://www.creative-tim.com)
 * Coded by Creative Tim
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 */
// This must be the first line in src/index.js
import 'react-app-polyfill/ie9';
/* eslint-disable no-unused-vars */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { Button, CardTitle, Label, FormGroup, Input, Row, Col, Tooltip } from 'reactstrap';

import './DropshipForm.css';

//! Mapbox AutoComplete
// import MapboxAutocomplete from '../../assets/react-mapbox-autocomplete/ReactMapboxAutocomplete';
// import '../../assets/react-mapbox-autocomplete/index.css';

//! Google Map AutoComplete
import GoogleMapAutoComplete, { isCoordinateFallbackLabel } from '../Helpers/GoogleMapAutoComplete';
import LocationMapPickerModal from '../Helpers/LocationMapPickerModal';
import '../Helpers/googleMapAutoComplete.css';

import * as shipment_urls from '../../urls/api-shipments/merchant';
import * as inventory_urls from '../../urls/api-inventory/admin';
import * as user_urls from '../../urls/api-users/admin';
import { applyStoredStoreDeliveryTypeOverride } from '../../services/storeDeliveryTypeOverrideService';
// import states from '../../us_states';
import canadianProvinces from '../../canadian_provinces';
import mexicanStates from '../../mexican_states';
import { formatTimestamp } from '../../helpers/formatter';
// import 'react-datetime/css/react-datetime.css';
import Loading from '../Global/Loading';
import PickupCustomerAutoComplete from './PickupCustomerAutoComplete';
import DropOffCustomerAutoComplete from './DropOffCustomerAutoComplete';

import WaypointCustomerOnlyDropAutoComplete from './WaypointCustomerOnlyDropAutoComplete';
import LineItems from './LineItems';

import FeedbackMessage from '../SharedComponents/FeedbackMessage';
import AIRecommendationButton, { removeExistingBrokerBlock, extractBrokerBlock } from '../SharedComponents/AIRecommendationButton';
import { formatShipmentVehicleTypeLabel, normalizeShipmentVehicleTypeForApi, requiresRangeQuoteForVehicleType, supportsLiftgateForVehicleType } from '../Dispatch/CreateOrderForMerchant/vehicleTypeApi';
import OptimizeRouteButton from '../SharedComponents/OptimizeRouteButton';
import WightOptions from '../SharedComponents/WeightOptions';
import AIResponseExplanation from '../SharedComponents/AIResponseExplanation';
import PriceChoices from '../SharedComponents/PriceChoices';
import PreferredDriverSelect from '../Dispatch/CreateOrderForMerchant/DirectComponents/PreferredDriverSelect';
import { useEntitlements } from '../../services/entitlementsService';
import { UpgradeHint } from '../SharedComponents/featureLock';
import VehicleChoices from '../SharedComponents/VehicleChoices';
import FileUploadBeforeShipment from '../SharedComponents/FileUploadBeforeShipment';
import config from '../../config';
import FedexLabelModal from '../SharedComponents/FedexLabelModal';
import PaymentRetryModal from '../SharedComponents/PaymentRetryModal';
import PaymentMethodDisplay from '../SharedComponents/PaymentMethodDisplay';
import { getCompanyPaymentTermPolicy } from '../../services/paymentTerms';
import { allowsNetTerms, getStoredStoreInvoiceFrequency, normalizeInvoiceFrequency, checkPaymentStatus, extractInvoiceLinks } from '../../services/paymentService';
import Divider from '../SharedComponents/Divider';
import SectionTitle from '../SharedComponents/SectionTitle';
import SectionProgressBar from '../SharedComponents/SectionProgressBar';
import LoadRequirements from '../SharedComponents/LoadRequirements';
import { buildSavedDraftResumeState, buildSavedDraftUpdateRequest } from './savedDraftResume';
import { resolveDashboardClientEnv } from '../../utils/environmentMode';
import { clearSavedDraftEditState } from '../../utils/savedDraftEditState';
import { buildAuthorizationHeader, notifySessionExpired, renewSessionSilently } from '../../Auth/session';

const clientEnv = resolveDashboardClientEnv();
const MERCHANT_RECENT_SHIPMENT_QUEUE_KEY = 'merchant_orders_recent_created_snapshots';
const MERCHANT_RECENT_SHIPMENT_QUEUE_LIMIT = 15;
const MERCHANT_RECENT_SHIPMENT_MAX_AGE_MS = 15 * 60 * 1000;
const ORDER_SUBMIT_LOCK_MS = 25000;
const LOCAL_QUOTE_RETRY_COOLDOWN_MS = 60000;
const QUOTE_REQUEST_TIMEOUT_MS = 8000;
const SHIPMENT_FILE_UPLOAD_TIMEOUT_MS = 20000;
const SHIPMENT_FILE_UPLOAD_ABORT_MS = 25000;
const PRODUCTION_DASHBOARD_HOST = 'dashboard.gophr.app';
const PRODUCTION_FILES_HOST = 'https://api-files.gophr.app';
const STORE_STRIPE_BILLING_SUPER_MERCHANT_ROLE = '200';

const isProductionDashboardBrowser = () =>
    typeof window !== 'undefined' &&
    window.location &&
    typeof window.location.hostname === 'string' &&
    (window.location.hostname === PRODUCTION_DASHBOARD_HOST || window.location.hostname.endsWith(`.${PRODUCTION_DASHBOARD_HOST}`));

const normalizeApiFilesHost = hostValue => {
    const host = String(hostValue || '').trim();
    if (!host) {
        return '';
    }

    if (typeof window !== 'undefined' && window.location && window.location.protocol === 'https:' && host.startsWith('http://')) {
        const insecureHost = host.slice('http://'.length);
        const isLocalInsecureHost = insecureHost.startsWith('localhost') || insecureHost.startsWith('127.0.0.1');

        if (!isLocalInsecureHost) {
            return `https://${insecureHost}`;
        }
    }

    return host;
};

const resolveUploadHostForBrowser = configuredHost => {
    const normalizedHost = normalizeApiFilesHost(configuredHost);

    if (!isProductionDashboardBrowser()) {
        return normalizedHost;
    }

    if (typeof normalizedHost === 'string' && normalizedHost.includes('dev-api-')) {
        return PRODUCTION_FILES_HOST;
    }

    return normalizedHost || PRODUCTION_FILES_HOST;
};

const getUploadHostAttempts = configuredHost => {
    const primaryHost = resolveUploadHostForBrowser(configuredHost);
    const productionHost = normalizeApiFilesHost(PRODUCTION_FILES_HOST);

    if (!primaryHost) {
        return [];
    }

    if (primaryHost !== productionHost && (primaryHost.includes('dev-api-files') || isProductionDashboardBrowser())) {
        return [primaryHost, productionHost];
    }

    return [primaryHost];
};

const extractUploadErrorMessage = error => {
    const responseData = error && error.response ? error.response.data : null;
    const statusCode = error && error.response && error.response.status ? error.response.status : null;
    const serverMessage = responseData ? (responseData.message || responseData.error || '') : '';
    const networkMessage = !error.response && error.message ? error.message : '';

    return {
        statusCode,
        message: serverMessage || networkMessage || 'Upload failed',
    };
};

const isUnauthorizedError = error => {
    const status = error && error.response ? error.response.status : null;
    return status === 401 || status === 403;
};

const runWithSessionRecovery = async requestFactory => {
    try {
        return await requestFactory();
    } catch (error) {
        if (!isUnauthorizedError(error)) {
            throw error;
        }

        try {
            await renewSessionSilently();
            return await requestFactory();
        } catch (recoveryError) {
            notifySessionExpired({ reason: 'merchant_order_submit_unauthorized' });
            throw error;
        }
    }
};

const getPreferredStoreBillingOwner = storeOwnerships => {
    if (!Array.isArray(storeOwnerships) || storeOwnerships.length === 0) {
        return null;
    }

    const normalizedOwnerships = storeOwnerships.filter(Boolean);
    const superMerchantOwner = normalizedOwnerships.find(owner => String(owner.role || '').trim() === STORE_STRIPE_BILLING_SUPER_MERCHANT_ROLE);

    return superMerchantOwner || normalizedOwnerships[0] || null;
};

const persistRecentShipmentSnapshot = snapshot => {
    if (typeof window === 'undefined' || !window.localStorage || !snapshot) {
        return;
    }

    const shipmentId = snapshot.shipment_id || (snapshot.shipment && snapshot.shipment.shipment_id) || '';
    const createdAtMs = snapshot.created_at ? Number(snapshot.created_at) : Date.now();

    if (!shipmentId || !createdAtMs || Number.isNaN(createdAtMs)) {
        return;
    }

    let storedSnapshots = [];

    try {
        const rawStoredSnapshots = window.localStorage.getItem(MERCHANT_RECENT_SHIPMENT_QUEUE_KEY);
        storedSnapshots = rawStoredSnapshots ? JSON.parse(rawStoredSnapshots) : [];
    } catch (_) {
        storedSnapshots = [];
    }

    const nextSnapshots = [];
    const seenShipmentIds = new Set();
    const snapshotsToPersist = [
        {
            ...snapshot,
            shipment_id: shipmentId,
            created_at: createdAtMs,
            shipment: snapshot.shipment
                ? {
                    ...snapshot.shipment,
                    shipment_id: shipmentId,
                }
                : snapshot.shipment,
        },
        ...(Array.isArray(storedSnapshots) ? storedSnapshots : [])
    ];

    snapshotsToPersist.forEach(candidateSnapshot => {
        const candidateShipmentId =
            (candidateSnapshot && candidateSnapshot.shipment_id) ||
            (candidateSnapshot && candidateSnapshot.shipment && candidateSnapshot.shipment.shipment_id) ||
            '';
        const candidateCreatedAtMs = candidateSnapshot && candidateSnapshot.created_at ? Number(candidateSnapshot.created_at) : 0;

        if (!candidateShipmentId || !candidateCreatedAtMs || Number.isNaN(candidateCreatedAtMs)) {
            return;
        }

        if (Date.now() - candidateCreatedAtMs > MERCHANT_RECENT_SHIPMENT_MAX_AGE_MS) {
            return;
        }

        if (seenShipmentIds.has(candidateShipmentId)) {
            return;
        }

        seenShipmentIds.add(candidateShipmentId);
        nextSnapshots.push({
            ...candidateSnapshot,
            shipment_id: candidateShipmentId,
            created_at: candidateCreatedAtMs,
            shipment: candidateSnapshot && candidateSnapshot.shipment
                ? {
                    ...candidateSnapshot.shipment,
                    shipment_id: candidateShipmentId,
                }
                : candidateSnapshot.shipment,
        });
    });

    const boundedSnapshots = nextSnapshots.slice(0, MERCHANT_RECENT_SHIPMENT_QUEUE_LIMIT);

    if (boundedSnapshots.length === 0) {
        window.localStorage.removeItem(MERCHANT_RECENT_SHIPMENT_QUEUE_KEY);
        return;
    }

    window.localStorage.setItem(MERCHANT_RECENT_SHIPMENT_QUEUE_KEY, JSON.stringify(boundedSnapshots));
};

const buildActiveEditPricingSignature = ({ pickup, dropoff, vehicleType, weight, numPeople, containsAlcohol, requirements, isScheduled, scheduledFor }) => {
    const locationSignature = location => {
        const safeLocation = location && typeof location === 'object' ? location : {};
        return [
            safeLocation.address || safeLocation.formatted_address || '',
            safeLocation.zip || '',
            safeLocation.state || '',
            safeLocation.country || '',
            safeLocation.lat == null ? null : Number(safeLocation.lat),
            safeLocation.lng == null ? null : Number(safeLocation.lng),
        ];
    };
    const scheduledTimestamp = scheduledFor && typeof scheduledFor.valueOf === 'function' ? scheduledFor.valueOf() : scheduledFor ? new Date(scheduledFor).getTime() : null;

    return JSON.stringify({
        pickup: locationSignature(pickup),
        dropoff: locationSignature(dropoff),
        vehicleType: normalizeShipmentVehicleTypeForApi(vehicleType),
        weight: Number(weight),
        numPeople: Number(numPeople),
        containsAlcohol: Boolean(containsAlcohol),
        requirements,
        isScheduled: Boolean(isScheduled),
        scheduledFor: Number.isFinite(scheduledTimestamp) ? scheduledTimestamp : null,
    });
};

// Merge the customer's typed notes with the hidden AI broker notes at submit so
// both persist (dashboard + driver app) without mixing in the form.
const mergeCustomerAndBrokerNotes = (customerNotes, brokerNotes) => {
  const customer = (customerNotes || '').trim();
  const broker = (brokerNotes || '').trim();
  if (!broker) {
    return customerNotes || '';
  }
  return customer ? `${customer}\n\n${broker}` : broker;
};

export default function DropshipForm(props) {
    var moment = require('moment');
    var tomorrow = moment().add(1, 'day');
    const prefilledStore = props.prefilledStore || null;
    const forceMultidropMode = props.forceMultidrop === true;
    const isAdminOnBehalfMode = props.isAdminOnBehalf === true;
    const onBehalfMerchantAuth0Id = props.onBehalfMerchantAuth0Id || null;
    // Paid-feature gates (skip when an admin is creating on a merchant's behalf).
    const dropshipEntitlements = useEntitlements();
    const multidropLocked = !isAdminOnBehalfMode && Boolean(dropshipEntitlements && dropshipEntitlements.features && dropshipEntitlements.features.multidrop === false);
    const preferredDriverLocked = !isAdminOnBehalfMode && Boolean(dropshipEntitlements && dropshipEntitlements.features && dropshipEntitlements.features.preferred_driver === false);
    const nearbyDriverLocked = !isAdminOnBehalfMode && Boolean(dropshipEntitlements && dropshipEntitlements.features && dropshipEntitlements.features.nearby_driver_count === false);
    const fedexLocked = !isAdminOnBehalfMode && Boolean(dropshipEntitlements && dropshipEntitlements.features && dropshipEntitlements.features.fedex_rates === false);
    // Free tier is prepay only (card charged when a driver claims) — no net/company terms. Trial keeps premium.
    const netTermsLocked = !isAdminOnBehalfMode && Boolean(dropshipEntitlements && dropshipEntitlements.tier === 'free' && !dropshipEntitlements.trial_active);
    const savedDraftEdit = props.savedDraftEdit || null;
    const abandonedPrefill = props.abandonedPrefill || null;
    const isActiveEdit = Boolean(savedDraftEdit && savedDraftEdit.isActiveEdit);
    const editingSavedShipmentId = savedDraftEdit && savedDraftEdit.shipment ? savedDraftEdit.shipment.shipment_id : null;
    const activeEditOrderIdsRef = useRef({});
    const activeEditPricingBaselineRef = useRef(null);
    const [activeEditOriginalPrice, setActiveEditOriginalPrice] = useState(null);
    const [hasActiveEditPricingChanges, setHasActiveEditPricingChanges] = useState(false);
    const hydratedSavedDraftIdRef = useRef(null);
    const abandonedPrefillAppliedRef = useRef(null);

    const flattenQuoteError = value => {
        if (!value) {
            return '';
        }

        if (typeof value === 'string') {
            return value;
        }

        if (Array.isArray(value)) {
            return value
                .map(flattenQuoteError)
                .filter(Boolean)
                .join(' ');
        }

        if (typeof value === 'object') {
            return Object.values(value)
                .map(flattenQuoteError)
                .filter(Boolean)
                .join(' ');
        }

        return String(value);
    };

    const buildQuoteError = (error, errorCode) => {
        const fallbackMessage = 'Something went wrong. Please refresh the page and try again.';
        const data = error && error.response ? error.response.data : null;
        const statusCode = error && error.response ? error.response.status : null;
        const requestUrl = error && error.config && typeof error.config.url === 'string' ? error.config.url : null;
        const networkCode = error && error.code ? String(error.code) : null;
        const runtimeErrorMessage = flattenQuoteError(error && (error.message || error));
        const errorMessage = [flattenQuoteError(data && (data.message || data.error)), flattenQuoteError(data && (data.details || data.errors))]
            .filter(Boolean)
            .filter((value, index, values) => values.indexOf(value) === index)
            .join(' ');

        const isLocalRequest = typeof requestUrl === 'string' && /localhost|127\.0\.0\.1/.test(requestUrl);
        const noResponseLocalMessage = !statusCode && isLocalRequest
            ? 'No response received from local API. Ensure local service is running and not crashing during request handling.'
            : '';

        const diagnosticPrefix = [
            statusCode ? `HTTP ${statusCode}` : null,
            requestUrl ? `URL: ${requestUrl}` : null,
            !statusCode && networkCode ? `Code: ${networkCode}` : null,
        ]
            .filter(Boolean)
            .join(' | ');

        const composedMessage = [diagnosticPrefix, errorMessage || runtimeErrorMessage || noResponseLocalMessage || fallbackMessage]
            .filter(Boolean)
            .join(' - ');

        return {
            errorMessage: composedMessage || fallbackMessage,
            errorCode
        };
    };

    const applyPpeQuoteFallback = quotePayload => {
        if (!isPPE || quotePayload == null) {
            return quotePayload;
        }

        const PPE_UPCHARGE = 15;

        if (typeof quotePayload === 'number') {
            return quotePayload + PPE_UPCHARGE;
        }

        if (typeof quotePayload === 'string') {
            const numericPayload = Number(quotePayload);
            return Number.isFinite(numericPayload) ? numericPayload + PPE_UPCHARGE : quotePayload;
        }

        if (typeof quotePayload !== 'object') {
            return quotePayload;
        }

        const calculated = quotePayload.calculated && typeof quotePayload.calculated === 'object' ? quotePayload.calculated : null;
        const hasPpeFeeFromBackend = calculated && typeof calculated.ppe_fee === 'number';

        if (hasPpeFeeFromBackend) {
            return quotePayload;
        }

        const addUpcharge = value => {
            const numericValue = Number(value);
            return Number.isFinite(numericValue) ? numericValue + PPE_UPCHARGE : value;
        };

        const addUpchargeToRange = range => {
            if (!range || typeof range !== 'object') {
                return range;
            }

            return {
                ...range,
                min: addUpcharge(range.min),
                max: addUpcharge(range.max),
            };
        };

        return {
            ...quotePayload,
            shipping_fee: addUpcharge(quotePayload.shipping_fee),
            shipping_fee_expedited: addUpcharge(quotePayload.shipping_fee_expedited),
            shipping_fee_super_expedited: addUpcharge(quotePayload.shipping_fee_super_expedited),
            shipping_fee_range: addUpchargeToRange(quotePayload.shipping_fee_range),
            shipping_fee_range_expedited: addUpchargeToRange(quotePayload.shipping_fee_range_expedited),
            shipping_fee_range_super_expedited: addUpchargeToRange(quotePayload.shipping_fee_range_super_expedited),
            ...(calculated
                ? {
                      calculated: {
                          ...calculated,
                          ppe_fee: PPE_UPCHARGE,
                      },
                  }
                : {}),
        };
    };

    const normalizeQuoteResponse = quoteResponse => {
        if (!quoteResponse || !quoteResponse.data) {
            return quoteResponse;
        }

        return {
            ...quoteResponse,
            data: {
                ...quoteResponse.data,
                payload: applyPpeQuoteFallback(quoteResponse.data.payload),
            },
        };
    };

    const postQuoteWithFallback = async (requestConfig, options = {}) => {
        const useLegacyQuoteEndpoint = options.useLegacyQuoteEndpoint === true;
        const shouldBypassLocalQuoteEndpoint = process.env.REACT_APP_ENV_MODE === 'local-debug'
            && process.env.REACT_APP_ENABLE_LOCAL_QUOTE_ENDPOINT !== 'true';
        const now = Date.now();
        const canTryLocalQuote = !postQuoteWithFallback.localRetryAt || postQuoteWithFallback.localRetryAt <= now;
        const requestHeaders = requestConfig && requestConfig.headers ? requestConfig.headers : {};
        const rawAuthorizationHeader = requestHeaders.Authorization || requestHeaders.authorization || '';
        const normalizedAuthorizationToken = String(rawAuthorizationHeader || '').replace(/^Bearer\s+/i, '').trim();
        const buildQuoteRequest = url => axios({
            ...requestConfig,
            timeout: QUOTE_REQUEST_TIMEOUT_MS,
            headers: {
                ...requestHeaders,
                Authorization: normalizedAuthorizationToken ? `Bearer ${normalizedAuthorizationToken}` : buildAuthorizationHeader(),
            },
            url
        }).then(normalizeQuoteResponse);

        const requestHostedWithRateQuote = () => buildQuoteRequest(shipment_urls.toHostedShipmentUrl(shipment_urls.getShipmentQuoteWithRate));
        const requestHostedLegacyQuote = () => buildQuoteRequest(shipment_urls.toHostedShipmentUrl(shipment_urls.getShipmentQuote));

        if (shouldBypassLocalQuoteEndpoint) {
            return (useLegacyQuoteEndpoint ? requestHostedLegacyQuote() : requestHostedWithRateQuote())
                .catch(() => requestHostedLegacyQuote());
        }

        if (!canTryLocalQuote) {
            return requestHostedWithRateQuote().catch(() => requestHostedLegacyQuote());
        }

        return buildQuoteRequest(useLegacyQuoteEndpoint ? shipment_urls.getShipmentQuote : shipment_urls.getShipmentQuoteWithRate).catch(error => {
            const status = error && error.response ? error.response.status : null;

            if (status === null || status === 404 || status >= 500) {
                postQuoteWithFallback.localRetryAt = Date.now() + LOCAL_QUOTE_RETRY_COOLDOWN_MS;
            }

            if (status && status < 500 && status !== 404) {
                throw error;
            }

            if (useLegacyQuoteEndpoint) {
                return requestHostedLegacyQuote();
            }

            return requestHostedWithRateQuote().catch(hostedError => {
                const hostedStatus = hostedError && hostedError.response ? hostedError.response.status : null;

                if (hostedStatus && hostedStatus < 500 && hostedStatus !== 404) {
                    throw hostedError;
                }

                return requestHostedLegacyQuote();
            });
        });
    };

    const extractQuoteAmountFromPayload = payload => {
        if (!payload) {
            return null;
        }

        if (typeof payload === 'number') {
            return payload;
        }

        if (typeof payload === 'string' && payload.trim() !== '') {
            return Number(payload);
        }

        if (typeof payload.shipping_fee === 'number') {
            return payload.shipping_fee;
        }

        if (typeof payload.shipping_fee === 'string' && payload.shipping_fee.trim() !== '') {
            return Number(payload.shipping_fee);
        }

        return null;
    };

    const buildRangePayload = (low, high) => {
        const min = Number(low);
        const max = Number(high);

        if (!Number.isFinite(min) || !Number.isFinite(max)) {
            return null;
        }

        return { min, max };
    };

    const buildSubmittedQuotePayload = () => {
        const regularRange = buildRangePayload(priceRangeLow, priceRangeHigh);
        const standardRange = buildRangePayload(standardPriceRangeLow, standardPriceRangeHigh) || regularRange;
        const expeditedRange = buildRangePayload(expeditedPriceRangeLow, expeditedPriceRangeHigh);
        const superExpeditedRange = buildRangePayload(superExpeditedPriceRangeLow, superExpeditedPriceRangeHigh);
        const activeRange = isSuperExpedited
            ? superExpeditedRange || expeditedRange || standardRange
            : isExpedited
            ? expeditedRange || standardRange
            : regularRange || standardRange;

        if (!activeRange) {
            return null;
        }

        return {
            shipping_fee_range: activeRange,
            ...(standardRange ? { shipping_fee_range_standard: standardRange } : {}),
            ...(expeditedRange ? { shipping_fee_range_expedited: expeditedRange } : {}),
            ...(superExpeditedRange ? { shipping_fee_range_super_expedited: superExpeditedRange } : {}),
        };
    };

    const refreshQuoteBeforeSubmit = async ({
        resolvedStoreMarketId,
        normalizedVehicleType,
        resolvedRoute,
        resolvedOrders,
        numOfDrops,
        itemsCountPerStop,
        itemNum,
        scheduledForData,
        resolvedDropOffState,
        submitUrl,
    }) => {
        if (!resolvedStoreMarketId || !normalizedVehicleType || !resolvedRoute || !Array.isArray(resolvedOrders) || resolvedOrders.length < 2) {
            return null;
        }

        const orderPoints = resolvedOrders.map(order => [order.location_blob.lat, order.location_blob.lng]);
        const stops = resolvedOrders.map((order, index) => {
            const isPickupStop = Boolean(order.actions && order.actions.pick_up);

            return {
                stopType: isPickupStop ? 'PICK' : 'DROP',
                stopOrder: index + 1,
                postalCode: order.location_blob.zip,
                countryCode: getCountryCode(order.location_blob.country || 'US')
            };
        });

        const quoteRequestData = {
            distance,
            weight,
            is_expedited: isExpedited,
            is_super_expedited: isSuperExpedited,
            quoted_time: resolvedRoute.duration / 60 / 60,
            num_of_gophrs: numPeople,
            vehicle_type: normalizedVehicleType,
            contains_alcohol: containsAlcohol,
            market_id: resolvedStoreMarketId,
            order_points: orderPoints,
            num_of_drops: numOfDrops,
            items_count_per_stop_array: itemsCountPerStop,
            item_count: itemNum,
            is_roundtrip: false,
            is_fork_lift: isForkLift,
            is_dolly: isDolly,
            is_hazmat: isHazmat,
            is_pallet_jack: isPalletJack,
            is_twic_card: isTWICCard,
            is_liftgate: isLiftgate,
            is_hand_loading: isHandLoadUnload,
            is_weather_protection: isWeatherProtection,
            is_medical: isMedical,
            is_personal_protective_equipment: isPPE,
            temperature,
            scheduled_for: scheduledForData || scheduledFor || tomorrow,
            load_value: loadValue,
            stops,
            commodity,
            do_tql_cal: true,
            recommend_vehicle: recommendedVehicleType,
            is_over_size: isOverSize,
            drop_off_state: resolvedDropOffState,
            is_market_base_quote: false
        };

        const isCreateWithRateSubmission = typeof submitUrl === 'string' && submitUrl.includes('/create_with_rate');

        const quoteResponse = await postQuoteWithFallback({
            method: 'post',
            withCredentials: true,
            headers: {
                'content-type': 'application/json',
                Authorization: ACCESS_TOKEN
            },
            data: quoteRequestData
        }, {
            // Create_with_rate routes currently verify with legacy fee calculation logic.
            useLegacyQuoteEndpoint: isCreateWithRateSubmission,
        });

        const quotePayload = quoteResponse && quoteResponse.data ? quoteResponse.data.payload : null;

        return {
            amount: extractQuoteAmountFromPayload(quotePayload),
            submittedQuotePayload: buildSubmittedQuotePayload(),
        };
    };

    const [address, setAddress] = useState('');
    const [containsAlcohol, setContainsAlcohol] = useState(false);
    const [sellsAlcohol, setSellsAlcohol] = useState(false);
    const [allowMultipleWaypoints, setAllowMultipleWaypoints] = useState(false);
    const [controlLat, setControlLat] = useState('30.21294');
    const [controlLong, setControlLong] = useState('-93.2187');
    // const [customerAddress, setCustomerAddress] = useState('');
    // const [customerAddress2, setCustomerAddress2] = useState('');
    // const [customerCity, setCustomerCity] = useState('');
    // const [customerEmail, setCustomerEmail] = useState('');
    // const [customerFirstName, setCustomerFirstName] = useState('');
    // const [customerLastName, setCustomerLastName] = useState('');
    // const [customerLat, setCustomerLat] = useState('');
    // const [customerLong, setCustomerLong] = useState('');
    // const [customerPhone, setCustomerPhone] = useState('');
    // const [customerState, setCustomerState] = useState('');
    // const [customerZip, setCustomerZip] = useState('');
    const [description, setDescription] = useState('');
    const [merchantOrderID, setMerchantOrderID] = useState(null);
    const [distance, setDistance] = useState('');
    const [dropAddress1, setDropAddress1] = useState(null);
    const [dropAddress2, setDropAddress2] = useState(null);
    const [dropCity, setDropCity] = useState(null);
    const [dropCountry, setDropCountry] = useState(null);
    // const [dropEmail, setDropEmail] = useState('');
    const [dropFirstname, setDropFirstname] = useState('');
    const [dropFullAddress, setDropFullAddress] = useState('');
    const [dropLastname, setDropLastname] = useState('');
    const [dropLat, setDropLat] = useState(null);
    const [dropLng, setDropLng] = useState(null);
    const [dropPhone, setDropPhone] = useState('');
    const [dropState, setDropState] = useState(null);
    const [dropZip, setDropZip] = useState(null);
    const [dropoff, setDropoff] = useState('');
    const [dropoffBusinessName, setDropoffBusinessName] = useState('');
    const [dropoffInfo, setDropoffInfo] = useState({});
    const [errorMessageDistance, setErrorMessageDistance] = useState({ errorMessage: '', errorCode: 0 });
    const [errorMessageSubmit, setErrorMessageSubmit] = useState({ errorMessage: '', errorCode: 0 });
    const [errors, setErrors] = useState({
        pickupFirstname: false,
        pickupLastname: false,
        // pickupEmail: false,
        pickupPhone: false,
        pickupAddress: false,
        dropoffFirstname: false,
        dropoffLastname: false,
        // dropoffEmail: false,
        dropoffPhone: false,
        description: false,
        weight: false,
        gophrs: false,
        size: false,
        price: false
    });
    const [waypointErrors, setWaypointErrors] = useState({
        waypointFirstname: false,
        waypointLastname: false,
        // waypointEmail: false,
        waypointPhone: false,
        waypointAddress: false,
        pickOrDrop: false
    });

    const [isDropoffStore, setIsDropoffStore] = useState(false);
    const [isExpedited, setIsExpedited] = useState(false);
    const [isSuperExpedited, setIsSuperExpedited] = useState(false);
    const [dynamicPricingInfo, setDynamicPricingInfo] = useState(null);
    const [oneDayThreePrice, setOneDayThreePrice] = useState(null);
    const [oneDayThreeQuotePayload, setOneDayThreeQuotePayload] = useState(null);
    const [standardQuotePayload, setStandardQuotePayload] = useState(null);
    const [expeditedQuotePayload, setExpeditedQuotePayload] = useState(null);
    const [superExpeditedQuotePayload, setSuperExpeditedQuotePayload] = useState(null);
    const [aiAutoTriggerCount, setAiAutoTriggerCount] = useState(0);
    const aiAutoDebounceRef = useRef(null);
    const lastAiTriggeredName = useRef('');
    const aiBlockRetriggerRef = useRef(false); // true while AI result is being applied
    const [isPickupStore, setIsPickupStore] = useState(true);
    const [isSaved, setIsSaved] = useState(false);
    const [isScheduled, setIsScheduled] = useState(false);
    const [loading, setLoading] = useState(true);
    const [numPeople, setNumPeople] = useState(1);
    const [orderStatus, setOrderStatus] = useState('awaiting_driver');
    const [pickAddress1, setPickAddress1] = useState(null);
    const [pickAddress2, setPickAddress2] = useState(null);
    const [pickCity, setPickCity] = useState(null);
    const [pickCountry, setPickCountry] = useState(null);
    // const [pickEmail, setPickEmail] = useState('');
    const [pickFirstname, setPickFirstname] = useState('');
    const [pickFullAddress, setPickFullAddress] = useState('');
    const [pickLastname, setPickLastname] = useState('');
    const [pickLat, setPickLat] = useState(null);
    const [pickLng, setPickLng] = useState(null);
    const [pickPhone, setPickPhone] = useState('');
    const [pickState, setPickState] = useState(null);
    const [pickZip, setPickZip] = useState(null);
    const [pickup, setPickup] = useState('');
    // AI broker notes kept hidden from the customer until the order is placed,
    // then merged into the order instructions at submit.
    const [brokerPickupNotes, setBrokerPickupNotes] = useState('');
    const [brokerDropoffNotes, setBrokerDropoffNotes] = useState('');
    const [pickBusinessName, setPickBusinessName] = useState('');
    const [pickupInfo, setPickupInfo] = useState({});
    const [preferredDriver, setPreferredDriver] = useState(null);
    const [price, setPrice] = useState(null);
    const [expeditedPrice, setExpeditedPrice] = useState('');
    const [superExpeditedPrice, setSuperExpeditedPrice] = useState('');
    const [priceRangeLow, setPriceRangeLow] = useState(null);
    const [priceRangeHigh, setPriceRangeHigh] = useState(null);
    const [standardPriceRangeLow, setStandardPriceRangeLow] = useState(0);
    const [standardPriceRangeHigh, setStandardPriceRangeHigh] = useState(0);
    const [expeditedPriceRangeLow, setExpeditedPriceRangeLow] = useState(0);
    const [expeditedPriceRangeHigh, setExpeditedPriceRangeHigh] = useState(0);
    const [superExpeditedPriceRangeLow, setSuperExpeditedPriceRangeLow] = useState(0);
    const [superExpeditedPriceRangeHigh, setSuperExpeditedPriceRangeHigh] = useState(0);
    const [route, setRoute] = useState({});
    const [scheduledFor, setScheduledFor] = useState(null);
    const [scheduledDate, setScheduledDate] = useState(tomorrow);
    const [scheduledTime, setScheduledTime] = useState(null);
    const [shipmentStatus, setShipmentStatus] = useState('shipment_ready_for_driver');
    // const [showAlert, setShowAlert] = useState(false);
    const [showForm, setShowForm] = useState(props.showForm);
    const [showForm3, setShowForm3] = useState(false);
    const [standardPrice, setStandardPrice] = useState('');
    const [quoteVolumeDiscountPercent, setQuoteVolumeDiscountPercent] = useState(0);
    const [quoteVolumeDiscountAmount, setQuoteVolumeDiscountAmount] = useState(0);
    const [store, setStore] = useState(null);
    const [merchantName, setMerchantName] = useState('');
    const [storeAddress, setStoreAddress] = useState('');
    const [storeAddress1, setStoreAddress1] = useState('');
    const [storeAddress2, setStoreAddress2] = useState('');
    const [storeCity, setStoreCity] = useState('');
    const [storeCountry, setStoreCountry] = useState('');
    const [storeEmail, setStoreEmail] = useState(null);
    const [storeFirstName, setStoreFirstName] = useState(null);
    const [storeId, setStoreId] = useState('');
    const [storeLastName, setStoreLastName] = useState(null);
    const [storeMerchantAuth0Id, setStoreMerchantAuth0Id] = useState(null);
    const [storeMerchantAuth0Ids, setStoreMerchantAuth0Ids] = useState([]);
    const [storeLat, setStoreLat] = useState(null);
    const [storeLng, setStoreLng] = useState(null);
    const [storeName, setStoreName] = useState('');
    const [storePhone, setStorePhone] = useState(null);
    const [storeState, setStoreState] = useState('');
    const [storeZip, setStoreZip] = useState('');
    const [vehicleType, setVehicleType] = useState('');
    const [vendorLat, setVendorLat] = useState('');
    const [vendorLong, setVendorLong] = useState('');
    const [weight, setWeight] = useState(0);
    const [storeMarketId, setStoreMarketId] = useState('');
    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
    const [selectedPaymentMode, setSelectedPaymentMode] = useState('company_terms');
    // Free tier can't use net terms — force prepay so it never defaults to company terms.
    useEffect(() => { if (netTermsLocked) { setSelectedPaymentMode('card'); } }, [netTermsLocked]);
    const [paymentProfileState, setPaymentProfileState] = useState({
        loaded: false,
        fetchFailed: false,
        allowsNetTerms: false,
        hasSavedPaymentMethods: false,
        invoiceFrequency: null,
        billingAuth0Id: null,
        netTermsBillingAuth0Id: null,
        cardBillingAuth0Id: null,
        defaultPaymentMethodId: null,
        selectedPaymentMethodId: null,
        selectedCardBillingAuth0Id: null
    });

    //! autofill customer
    const [pickupCustomerSuggestions, setPickupCustomerSuggestions] = useState([]);
    const [dropCustomerSuggestions, setDropCustomerSuggestions] = useState([]);
    const [waypointCustomerSuggestions, setWaypointCustomerSuggestions] = useState([]);
    const [isPickupAutoFilled, setIsPickupAutoFilled] = useState(false);
    const [isDropOffAutoFilled, setIsDropOffAutoFilled] = useState(false);
    const [isPickupPinModalOpen, setIsPickupPinModalOpen] = useState(false);
    const [isDropoffPinModalOpen, setIsDropoffPinModalOpen] = useState(false);
    const [isPickupPinnedLocation, setIsPickupPinnedLocation] = useState(false);
    const [isDropoffPinnedLocation, setIsDropoffPinnedLocation] = useState(false);
    const [isWaypointAutoFilled, setIsWaypointAutoFilled] = useState(false);
    const [activeWaypointPinIndex, setActiveWaypointPinIndex] = useState(-1);

    const [isProceeded, setIsProceeded] = useState(false);
    const [isShipTomorrow, setIsShipTomorrow] = useState(false);

    //! Regular 2 stops
    const [twoStopsError, setTwoStopsError] = useState(false);
    const [defaultItems, setDefaultItems] = useState([{ quantity: 1, detail: '', unit: '', weight: 1, length: 1, width: 1, height: 1, nmfc: '' }]);
    const [defaultItemsError, setDefaultItemsError] = useState([false]);
    const [pickupItems, setPickupItems] = useState([{ quantity: 1, detail: '', unit: '', weight: 1, length: 1, width: 1, height: 1, nmfc: '' }]);
    const [dropoffItems, setDropoffItems] = useState([{ quantity: 1, detail: '', unit: '', weight: 1, length: 1, width: 1, height: 1, nmfc: '' }]);
    const [infiniteStopsError, setInfiniteStopsError] = useState(false);
    const [pickupItemsError, setPickupItemsError] = useState([false]);
    const [dropoffItemsError, setDropoffItemsError] = useState([false]);

    //! Infinite Pickup and Dropoff waypoints
    const [isInfiniteWaypointsSelected, setIsInfiniteWaypointsSelected] = useState(false);
    const [pickAndDropWaypointsArray, setPickAndDropWaypointsArray] = useState([
        {
            waypointKey: `waypoint-${Date.now()}-0`,
            waypointFirstname: '',
            waypointLastname: '',
            waypointPhone: '',
            waypointAddress1: null,
            waypointAddress2: null,
            waypointFullAddress: '',
            waypointLat: '',
            waypointLng: '',
            waypointCity: '',
            waypointState: '',
            waypointCountry: '',
            waypointZip: '',
            waypointPickupInstruction: '',
            waypointDropoffInstruction: '',
            waypointBusinessName: '',
            waypointCustomerSuggestions: [],
            isWaypointAutoFilled: false,
            pickItems: [{ quantity: 1, detail: '', unit: '', weight: 1, length: 1, width: 1, height: 1, nmfc: '' }],
            dropItems: [{ quantity: 1, detail: '', unit: '', weight: 1, length: 1, width: 1, height: 1, nmfc: '' }],
            pickItemsError: [false],
            dropItemsError: [false],
            isWaypointPickup: false,
            isWaypointDropoff: false
        }
    ]);

    const [infinitePickAndDropWaypointsErrors, setInfinitePickAndDropWaypointsErrors] = useState([
        {
            waypointFirstname: false,
            waypointLastname: false,
            waypointPhone: false,
            waypointAddress: false
        }
    ]);

    const [infinitePickAndDropsInfo, setInfinitePickAndDropsInfo] = useState([]);
    const [itemsMatchError, setItemsMatchError] = useState(false);

    //! Equipments and Tools
    const [isForkLift, setIsForkLift] = useState(false);
    const [isDolly, setIsDolly] = useState(false);
    const [isHandLoadUnload, setIsHandLoadUnload] = useState(false);
    const [isHazmat, setIsHazmat] = useState(false);
    const [isTWICCard, setIsTWICCard] = useState(false);
    const [isLiftgate, setIsLiftgate] = useState(false);
    const [isPalletJack, setIsPalletJack] = useState(false);
    const [isWeatherProtection, setIsWeatherProtection] = useState(false);
    const [isMedical, setIsMedical] = useState(false);
    const [isPPE, setIsPPE] = useState(false);
    const [isHandLoadUnloadDisabled, setIsHandLoadUnloadDisabled] = useState(false);
    const [isLiftgateDisabled, setIsLiftgateDisabled] = useState(false);

    //! Order submit button control
    const [isOrderSubmitDisabled, setIsOrderSubmitDisabled] = useState(false);
    const orderSubmitLockStartedAtRef = useRef(0);
    const orderSubmitUnlockTimeoutRef = useRef(null);

    //! TQL related
    const [temperature, setTemperature] = useState(20);
    const [loadValue, setLoadValue] = useState('36000');
    const [commodity, setCommodity] = useState('items');
    const [hasTqlRateValue, setHasTqlRateValue] = useState(false);
    const [tqlRateBlob, setTqlRateBlob] = useState({});

    const [isOverSize, setIsOverSize] = useState(false);

    //! Order submission indication
    const [submissionMessage, setSubmissionMessage] = useState('');

    //! AI recommendation explanation
    const [AIExplanation, setAIExplanation] = useState('');
    const [showExplanation, setShowExplanation] = useState(false);
    const [LTLPrice, setLTLPrice] = useState(0);
    const [LTLLeadTime, setLTLLeadTime] = useState(0);
    const [isLTL, setIsLTL] = useState(false);
    const [recommendedVehicleType, setRecommendedVehicleType] = useState('');
    const [recommendedWeight, setRecommendedWeight] = useState('');

    //! New generated shipment id for uploading documents
    const [selectedFile, setSelectedFile] = useState(null);

    //! Fedex rates
    const [fedexPricesOptions, setFedexPricesOptions] = useState([]);
    const [isFedex, setIsFedex] = useState(false);
    const [fedexSelection, setFedexSelection] = useState({});
    const [showFedexLabelModal, setShowFedexLabelModal] = useState(false);
    const [currentShipmentId, setCurrentShipmentId] = useState(null);

    //! Pre-pay payment modal
    const [paymentRetryModalOpen, setPaymentRetryModalOpen] = useState(false);
    const [currentShipmentData, setCurrentShipmentData] = useState(null);
    const [currentShipmentFriendlyId, setCurrentShipmentFriendlyId] = useState(null);
    const [hasPendingFedexLabel, setHasPendingFedexLabel] = useState(false);
    const [showInlinePaymentRetry, setShowInlinePaymentRetry] = useState(false);
    const [inlinePaymentRetryMessage, setInlinePaymentRetryMessage] = useState('');

    // For expanding all line items after AI recommendation
    const [forceExpandAllLineItems, setForceExpandAllLineItems] = useState(0);

    const MAPBOX_ENDPOINT = 'https://api.mapbox.com/directions/v5/mapbox/driving/';
    const MAPBOX_TOKEN = 'pk.eyJ1IjoiZ29waHItZW5naW5lZXJpbmciLCJhIjoiY2syYzd6NjdrMmo3azNtcGU1cXZzMTh1MSJ9.cHMxwcG_MoQk-l4ZO3bdEA';
    const HASURA_ENDPOINT = inventory_urls.hasuraHost;
    const jwtDecodeModule = require('jwt-decode');
    const resolveJwtDecode = () => {
        if (typeof jwtDecodeModule === 'function') {
            return jwtDecodeModule;
        }

        if (jwtDecodeModule && typeof jwtDecodeModule.jwtDecode === 'function') {
            return jwtDecodeModule.jwtDecode;
        }

        if (jwtDecodeModule && typeof jwtDecodeModule.default === 'function') {
            return jwtDecodeModule.default;
        }

        return null;
    };

    const safeDecodeAccessToken = rawToken => {
        const decode = resolveJwtDecode();

        if (!rawToken || typeof rawToken !== 'string' || typeof decode !== 'function') {
            return {};
        }

        const normalizedToken = rawToken.replace(/^Bearer\s+/i, '').trim();

        if (!normalizedToken || normalizedToken.split('.').length !== 3) {
            return {};
        }

        try {
            return decode(normalizedToken) || {};
        } catch (_error) {
            return {};
        }
    };

    const ACCESS_TOKEN = typeof window !== 'undefined' && window.localStorage
        ? window.localStorage.getItem('access_token')
        : '';
    const DECODED_ACCESS = safeDecodeAccessToken(ACCESS_TOKEN);
    const SUB = DECODED_ACCESS && DECODED_ACCESS.sub ? DECODED_ACCESS.sub : null;
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const toggleTooltip = () => setTooltipOpen(!tooltipOpen);

    useEffect(() => {
        return () => {
            if (orderSubmitUnlockTimeoutRef.current) {
                window.clearTimeout(orderSubmitUnlockTimeoutRef.current);
            }
        };
    }, []);

    const lockOrderSubmit = () => {
        orderSubmitLockStartedAtRef.current = Date.now();
        setIsOrderSubmitDisabled(true);

        if (orderSubmitUnlockTimeoutRef.current) {
            window.clearTimeout(orderSubmitUnlockTimeoutRef.current);
            orderSubmitUnlockTimeoutRef.current = null;
        }
    };

    const releaseOrderSubmitWhenLockExpires = () => {
        const remainingLockMs = Math.max(0, ORDER_SUBMIT_LOCK_MS - (Date.now() - orderSubmitLockStartedAtRef.current));

        if (orderSubmitUnlockTimeoutRef.current) {
            window.clearTimeout(orderSubmitUnlockTimeoutRef.current);
        }

        orderSubmitUnlockTimeoutRef.current = window.setTimeout(() => {
            setIsOrderSubmitDisabled(false);
            orderSubmitUnlockTimeoutRef.current = null;
        }, remainingLockMs);
    };

    useEffect(() => {
        if (isLiftgate) {
            setIsHandLoadUnloadDisabled(true);
        } else {
            setIsHandLoadUnloadDisabled(false);
        }
        setIsHandLoadUnload(false);
    }, [isLiftgate]);

    useEffect(() => {
        if (vehicleType) {
            setIsLiftgateDisabled(!supportsLiftgateForVehicleType(vehicleType));
            setIsLiftgate(false);
        }
    }, [vehicleType]);

    useEffect(() => {
        if (!isLTL) {
            if (standardPrice) {
                if (isExpedited) {
                    setPrice(expeditedPrice);
                } else if (isSuperExpedited) {
                    setPrice(superExpeditedPrice);
                } else {
                    setPrice(standardPrice);
                }
            }
        }
    }, [standardPrice, expeditedPrice, superExpeditedPrice, isLTL]);

    useEffect(() => {
        if (standardPriceRangeLow && standardPriceRangeHigh) {
            if (isExpedited) {
                setPriceRangeLow(expeditedPriceRangeLow);
                setPriceRangeHigh(expeditedPriceRangeHigh);
            } else if (isSuperExpedited) {
                setPriceRangeLow(superExpeditedPriceRangeLow);
                setPriceRangeHigh(superExpeditedPriceRangeHigh);
            } else {
                setPriceRangeLow(standardPriceRangeLow);
                setPriceRangeHigh(standardPriceRangeHigh);
            }
        }
    }, [
        standardPriceRangeLow,
        standardPriceRangeHigh,
        isExpedited,
        isSuperExpedited,
        expeditedPriceRangeLow,
        expeditedPriceRangeHigh,
        superExpeditedPriceRangeLow,
        superExpeditedPriceRangeHigh
    ]);

    // Fully resets the component
    const reset = () => {
        let radWeight = document.getElementsByName('exampleRadio1');
        let radNumPeople = document.getElementsByName('exampleRadio2');
        let radIsExpedited = document.getElementsByName('exampleRadio3');
        let radScheduleTime = document.getElementsByName('exampleRadio4');
        let scheduleCheck = document.getElementById('schedule-check');

        props.handleForm(false, false);
        if (editingSavedShipmentId) {
            clearSavedDraftEditState();
        }
        if (typeof props.onSavedDraftEditClose === 'function') {
            props.onSavedDraftEditClose();
        }

        setPickupInfo({});
        setDropoffInfo({});
        setAddress('');
        setContainsAlcohol(false);
        setDescription('');
        setMerchantOrderID('');
        setDropoff('');
        setBrokerDropoffNotes('');
        setDropAddress1(null);
        setDropAddress2(null);
        setDropCity(null);
        setDropCountry(null);
        // setDropEmail('');
        setDropFirstname('');
        setDropFullAddress('');
        setDropLastname('');
        setDropLat(null);
        setDropLng(null);
        setDropPhone('');
        setDropState(null);
        setDropZip(null);
        setErrors({
            pickupFirstname: false,
            pickupLastname: false,
            pickupPhone: false,
            pickupAddress: false,
            dropoffFirstname: false,
            dropoffLastname: false,
            dropoffPhone: false,
            description: false,
            weight: false,
            gophrs: false,
            size: false,
            price: false
        });
        setWaypointErrors({
            waypointFirstname: false,
            waypointLastname: false,
            waypointPhone: false,
            waypointAddress: false,
            pickOrDrop: false
        });
        setExpeditedPrice(null);
        setIsExpedited(false);
        setIsSaved(false);
        setIsScheduled(false);
        setNumPeople(1);
        setOrderStatus('awaiting_driver');
        setPickFullAddress('');
        setPickAddress1(null);
        setPickAddress2(null);
        setPickCity(null);
        setPickCountry(null);
        setPickFirstname('');
        setPickLastname('');
        setPickLat(null);
        setPickLng(null);
        setPickPhone('');
        setPickState(null);
        setPickZip(null);
        setPickup('');
        setBrokerPickupNotes('');

        setScheduledFor(null);
        setShipmentStatus('shipment_ready_for_driver');
        // setShowForm(props.showForm);
        // setShowForm1(true);
        // setShowForm2(props.showForm2);
        setShowForm3(false);
        setStandardPrice(null);
        // setStoreAddress1("");
        // setStoreAddress2("");
        // setStoreCity("");
        // setStoreCountry("");
        // setStoreId("");
        // setStoreName("");
        // setStoreZip("");
        setVehicleType('');
        setWeight(0);
        setShowInlinePaymentRetry(false);
        setInlinePaymentRetryMessage('');
        setCurrentShipmentId(null);
        setCurrentShipmentFriendlyId(null);
        setCurrentShipmentData(null);
        setHasPendingFedexLabel(false);
        setPaymentRetryModalOpen(false);

        setIsPickupStore(true);
        setIsDropoffStore(false);
        props.handleErrorDistance({ errorMessage: '', errorCode: 0 });
        props.handleErrorSubmit({ errorMessage: '', errorCode: 0 });

        // Clear the radio bubbles/checkboxes
        radWeight.forEach(element => (element.checked = false));
        radNumPeople.forEach(element => (element.checked = false));
        radIsExpedited.forEach(element => (element.checked = false));
        radScheduleTime.forEach(element => (element.checked = false));
        scheduleCheck.checked = false;

        // Clear the address input
        document.getElementById('mapbox-autocomplete').value = '';
        // ! need a way to clear mapbox
        // if (hasThirdWaypoint) {
        //   document.getElementById('mapbox-autocomplete2').value = '';
        //   document.getElementById('mapbox-autocomplete3').value = '';
        // }
        document.getElementsByClassName('react-mapbox-ac-input')[0].value = '';
    };

    const toggleAddWaypoint = () => {
        setIsInfiniteWaypointsSelected(!isInfiniteWaypointsSelected);
    };

    const handleWeight = event => {
        setWeight(event.target.value);
    };

    useEffect(() => {
        if (isScheduled && scheduledTime && scheduledDate) {
            setScheduledFor(
                moment(
                    moment(scheduledDate)
                        .startOf('day')
                        .add(scheduledTime, 'hours')
                )
            );
            setIsProceeded(true);
        } else if (isScheduled && (!scheduledTime || !scheduledDate)) {
            setIsProceeded(false);
        } else if (!isScheduled) {
            setIsProceeded(true);
            setScheduledFor(null);
        }
    }, [isScheduled, scheduledDate, scheduledTime]);

    // Same Day price is always 5% above 1-3 Day to incentivize flexible scheduling
    useEffect(() => {
        if (!isScheduled && oneDayThreePrice && oneDayThreePrice > 0) {
            setStandardPrice(parseFloat((oneDayThreePrice * 1.05).toFixed(2)));
        }
    }, [isScheduled, oneDayThreePrice]);

    useEffect(() => {
        setOneDayThreePrice(null);
        setOneDayThreeQuotePayload(null);
    }, [vehicleType]);

    useEffect(() => {
        setStandardQuotePayload(null);
        setExpeditedQuotePayload(null);
        setSuperExpeditedQuotePayload(null);
    }, [isScheduled, scheduledFor]);

    // After AI trigger fires, block re-triggering for 30 seconds
    useEffect(() => {
        if (aiAutoTriggerCount > 0) {
            aiBlockRetriggerRef.current = true;
            const t = setTimeout(() => { aiBlockRetriggerRef.current = false; }, 30000);
            return () => clearTimeout(t);
        }
    }, [aiAutoTriggerCount]);

    // Auto-trigger AI when item name reaches 3+ chars (merchant app pattern: 650ms debounce)
    useEffect(() => {
        const firstName = defaultItems && defaultItems[0] ? (defaultItems[0].detail || '').trim() : '';
        if (aiAutoDebounceRef.current) clearTimeout(aiAutoDebounceRef.current);
        if (
            !isActiveEdit &&
            !abandonedPrefill &&
            firstName.length >= 3 &&
            pickupInfo && pickupInfo.address && pickupInfo.lat &&
            dropoffInfo && dropoffInfo.address && dropoffInfo.lat &&
            !aiBlockRetriggerRef.current &&
            firstName !== lastAiTriggeredName.current
        ) {
            aiAutoDebounceRef.current = setTimeout(() => {
                lastAiTriggeredName.current = firstName;
                setAiAutoTriggerCount(prev => prev + 1);
            }, 650);
        }
        return () => { if (aiAutoDebounceRef.current) clearTimeout(aiAutoDebounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultItems, pickupInfo, dropoffInfo]);

    useEffect(() => {
        if (!loading && store) {
            const primaryStoreOwner = getPreferredStoreBillingOwner(store.StoreOwnerships);
            const ownerMerchantAuth0Ids = primaryStoreOwner && primaryStoreOwner.merchant_id ? [primaryStoreOwner.merchant_id] : [];
            const allowsMultipleWaypoints = Array.isArray(store.direct_type)
                ? store.direct_type.some(type => type === 'multidrop' || type === 'waypoint')
                : false;
            const shouldAllowMultipleWaypoints = forceMultidropMode || allowsMultipleWaypoints;

            setVendorLat(store.latitude.toString());
            setVendorLong(store.longitude.toString());
            setStoreAddress1(store.address_1);
            setStoreAddress2(store.address_2);
            setStoreCity(store.city);
            setStoreCountry(store.country);
            setStoreName(store.name);
            setStoreId(store.store_id);
            setStoreZip(store.zip.toString());
            setStoreFirstName((primaryStoreOwner && primaryStoreOwner.firstname) || null);
            setStoreLastName((primaryStoreOwner && primaryStoreOwner.lastname) || null);
            setStoreMerchantAuth0Id((primaryStoreOwner && primaryStoreOwner.merchant_id) || SUB);
            setStoreMerchantAuth0Ids(ownerMerchantAuth0Ids);
            setStoreEmail((primaryStoreOwner && primaryStoreOwner.email) || null);
            setStorePhone(store.phone);
            setStoreState(store.state);
            setStoreAddress(store.address_1 + ', ' + store.city + ', ' + store.state + ' ' + store.zip + ', ' + store.country);
            setStoreLat(store.latitude.toString());
            setStoreLng(store.longitude.toString());
            setSellsAlcohol(store.sells_alcohol);
            setAllowMultipleWaypoints(shouldAllowMultipleWaypoints);

            // Don't clear the infinite-waypoints toggle while hydrating an active multidrop edit;
            // the resume effect turns it on and this store effect can race after it.
            if (!allowsMultipleWaypoints && !(savedDraftEdit && savedDraftEdit.isActiveEdit)) {
                setIsInfiniteWaypointsSelected(false);
            }
        } else if (!loading) {
            setAllowMultipleWaypoints(forceMultidropMode);
            if (!(savedDraftEdit && savedDraftEdit.isActiveEdit)) {
                setIsInfiniteWaypointsSelected(false);
            }
        }
    }, [loading, store, SUB, forceMultidropMode]);

    useEffect(() => {
        let storeInfo = {
            name: storeFirstName + ' ' + storeLastName,
            first_name: storeFirstName,
            last_name: storeLastName,
            phone: storePhone,
            address: storeAddress,
            address_1: storeAddress1,
            address_2: storeAddress2,
            city: storeCity,
            zip: storeZip,
            state: storeState,
            country: storeCountry,
            business_name: storeName,
            lat: parseFloat(storeLat),
            lng: parseFloat(storeLng)
        };
        let dropInfo = {
            name: dropFirstname + ' ' + dropLastname,
            first_name: dropFirstname,
            last_name: dropLastname,
            phone: dropPhone,
            address: dropFullAddress,
            address_1: dropAddress1,
            address_2: dropAddress2,
            city: dropCity,
            zip: dropZip,
            state: dropState,
            country: dropCountry,
            business_name: dropoffBusinessName,
            lat: parseFloat(dropLat),
            lng: parseFloat(dropLng)
        };
        let pickInfo = {
            name: pickFirstname + ' ' + pickLastname,
            first_name: pickFirstname,
            last_name: pickLastname,
            phone: pickPhone,
            address: pickFullAddress,
            address_1: pickAddress1,
            address_2: pickAddress2,
            city: pickCity,
            zip: pickZip,
            state: pickState,
            country: pickCountry,
            business_name: pickBusinessName,
            lat: parseFloat(pickLat),
            lng: parseFloat(pickLng)
        };

        if (isPickupStore) {
            setPickupInfo(storeInfo);
            setDropoffInfo(dropInfo);
        } else if (isDropoffStore) {
            setDropoffInfo(storeInfo);
            setPickupInfo(pickInfo);
        } else {
            setPickupInfo(pickInfo);
            setDropoffInfo(dropInfo);
        }
    }, [
        pickLat,
        pickLng,
        dropLat,
        dropLng,
        storeLat,
        storeLng,
        isPickupStore,
        isDropoffStore,
        pickFirstname,
        pickLastname,
        pickPhone,
        pickBusinessName,
        dropPhone,
        dropFirstname,
        dropLastname,
        dropoffBusinessName
    ]);

    useEffect(() => {
        if (allowMultipleWaypoints && isInfiniteWaypointsSelected) {
            setInfinitePickAndDropsInfo(
                pickAndDropWaypointsArray.map(dr => {
                    return {
                        name: dr.waypointFirstname + ' ' + dr.waypointLastname,
                        first_name: dr.waypointFirstname,
                        last_name: dr.waypointLastname,
                        phone: dr.waypointPhone,
                        address: dr.waypointFullAddress,
                        address_1: dr.waypointAddress1,
                        address_2: dr.waypointAddress2,
                        city: dr.waypointCity,
                        zip: dr.waypointZip,
                        state: dr.waypointState,
                        country: pickCountry || 'US',
                        business_name: dr.waypointBusinessName ? dr.waypointBusinessName : '',
                        lat: parseFloat(dr.waypointLat),
                        lng: parseFloat(dr.waypointLng)
                    };
                })
            );
        }
    }, [allowMultipleWaypoints, isInfiniteWaypointsSelected, pickAndDropWaypointsArray]);

    const usePrevious = value => {
        const ref = useRef();
        useEffect(() => {
            ref.current = value;
        });
        return ref.current;
    };

    const normalizeCoordinateValue = value => {
        if (typeof value === 'number') {
            return Number.isNaN(value) ? null : value;
        }

        if (value === '' || value === null || typeof value === 'undefined') {
            return null;
        }

        const parsedValue = Number(value);
        return Number.isNaN(parsedValue) ? null : parsedValue;
    };

    const buildOrderLocationBlob = (info, fallback) => {
        const safeInfo = info && typeof info === 'object' ? info : {};
        const safeFallback = fallback && typeof fallback === 'object' ? fallback : {};
        const resolvedAddress1 = safeInfo.address_1 || safeFallback.address1 || null;
        const resolvedAddress = safeInfo.address || safeFallback.fullAddress || resolvedAddress1;

        return {
            name: safeInfo.name || safeFallback.name || null,
            first_name: safeInfo.first_name || safeFallback.firstName || null,
            last_name: safeInfo.last_name || safeFallback.lastName || null,
            phone: safeInfo.phone || safeFallback.phone || null,
            email: safeInfo.email || safeFallback.email || null,
            address: resolvedAddress,
            address_1: resolvedAddress1,
            address_2: safeInfo.address_2 || safeFallback.address2 || null,
            city: safeInfo.city || safeFallback.city || null,
            zip: safeInfo.zip || safeFallback.zip || null,
            state: safeInfo.state || safeFallback.state || null,
            country: safeInfo.country || safeFallback.country || 'US',
            business_name: safeInfo.business_name || safeFallback.businessName || '',
            lat: normalizeCoordinateValue(typeof safeInfo.lat !== 'undefined' ? safeInfo.lat : safeFallback.lat),
            lng: normalizeCoordinateValue(typeof safeInfo.lng !== 'undefined' ? safeInfo.lng : safeFallback.lng),
        };
    };

    const previousNumPeople = usePrevious(numPeople);
    const previousVehicleType = usePrevious(vehicleType);
    const previousWeight = usePrevious(weight);
    const previousDrop = usePrevious(dropoffInfo);
    const previousPick = usePrevious(pickupInfo);

    const previousAlc = usePrevious(containsAlcohol);

    const previousPickAndDropWaypointsArray = usePrevious(pickAndDropWaypointsArray);

    const previousForkLift = usePrevious(isForkLift);
    const previousDolly = usePrevious(isDolly);
    const previousHazmat = usePrevious(isHazmat);
    const previousTWICCard = usePrevious(isTWICCard);
    const previousLiftgate = usePrevious(isLiftgate);
    const previousPalletJack = usePrevious(isPalletJack);
    const previousHandLoad = usePrevious(isHandLoadUnload);
    const previousWeatherProtection = usePrevious(isWeatherProtection);
    const previousMedical = usePrevious(isMedical);
    const previousPPE = usePrevious(isPPE);
    const previousOverSize = usePrevious(isOverSize);
    const previousIsScheduled = usePrevious(isScheduled);
    const previousScheduledFor = usePrevious(scheduledFor);

    useEffect(() => {
        const prefilledStoreRecord = prefilledStore ? applyStoredStoreDeliveryTypeOverride(prefilledStore) : null;

        if (prefilledStoreRecord) {
            const primaryStoreOwner = getPreferredStoreBillingOwner(prefilledStoreRecord.StoreOwnerships);

            setStore(prefilledStoreRecord);
            setStoreAddress(
                prefilledStoreRecord.address_1 +
                    ', ' +
                    prefilledStoreRecord.city +
                    ', ' +
                    prefilledStoreRecord.state +
                    ' ' +
                    prefilledStoreRecord.zip +
                    ', ' +
                    prefilledStoreRecord.country
            );
            setStoreMarketId(prefilledStoreRecord.market_id);
            // placed_by name is resolved from the logged-in user, not the store owner
            setLoading(false);
            return;
        }

        // Fetch relevant store information from Hasura
        axios({
            method: 'post',
            headers: { Authorization: 'Bearer ' + localStorage.getItem('access_token'), 'content-type': 'application/json' },
            url: user_urls.hasuraHost,
            data: {
                query: `{Users(where: {auth0_id: {_eq: "${SUB}"}}) {store_id firstname lastname}}`
            }
        })
            .then(userResponse => {
                const userRecord = userResponse && userResponse.data && userResponse.data.data && userResponse.data.data.Users ? userResponse.data.data.Users[0] : null;
                const resolvedStoreId = userRecord && userRecord.store_id ? userRecord.store_id : '';
                const currentUserDisplayName = userRecord ? [userRecord.firstname, userRecord.lastname].filter(Boolean).join(' ') : '';

                if (currentUserDisplayName) {
                    setMerchantName(currentUserDisplayName);
                }

                if (!resolvedStoreId) {
                    setLoading(false);
                    return null;
                }

                return axios({
                    method: 'post',
                    headers: { Authorization: 'Bearer ' + localStorage.getItem('access_token'), 'content-type': 'application/json' },
                    url: HASURA_ENDPOINT,
                    data: {
                        query: `{Stores(where: {store_id: {_eq: "${resolvedStoreId}"}}) {state phone latitude longitude name address_1 address_2 city country zip store_id market_id sells_alcohol direct_type itemized_price is_market_base_quote StoreOwnerships {firstname lastname email merchant_id role }}}`
                    }
                });
            })
            .then(response => {
                if (!response) {
                    return;
                }

                const resolvedStoreRecord = response && response.data && response.data.data && response.data.data.Stores ? response.data.data.Stores[0] : null;
                const resolvedStore = applyStoredStoreDeliveryTypeOverride(resolvedStoreRecord);
                const primaryStoreOwner = resolvedStore ? getPreferredStoreBillingOwner(resolvedStore.StoreOwnerships) : null;

                if (!resolvedStore) {
                    setLoading(false);
                    return;
                }

                setStore(resolvedStore);
                setStoreAddress(
                    resolvedStore.address_1 +
                        ', ' +
                        resolvedStore.city +
                        ', ' +
                        resolvedStore.state +
                        ' ' +
                        resolvedStore.zip +
                        ', ' +
                        resolvedStore.country
                );
                setStoreMarketId(resolvedStore.market_id);
                setLoading(false);
            })
            .catch(function(error) {
                setLoading(false);
            });
    }, [prefilledStore, SUB]);

    // Auto-populate the order's placed-by name from the logged-in user only (never a store owner).
    useEffect(() => {
        if (!SUB) {
            return undefined;
        }
        let cancelled = false;
        axios({
            method: 'post',
            headers: { Authorization: 'Bearer ' + localStorage.getItem('access_token'), 'content-type': 'application/json' },
            url: user_urls.hasuraHost,
            data: {
                query: `{Users(where: {auth0_id: {_eq: "${SUB}"}}) {firstname lastname username email}}`
            }
        })
            .then(userResponse => {
                if (cancelled) {
                    return;
                }
                const userRecord = userResponse && userResponse.data && userResponse.data.data && userResponse.data.data.Users ? userResponse.data.data.Users[0] : null;
                if (!userRecord) {
                    return;
                }
                const loggedInName = [userRecord.firstname, userRecord.lastname].filter(Boolean).join(' ').trim() || userRecord.username || userRecord.email || '';
                if (loggedInName) {
                    setMerchantName(loggedInName);
                }
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [SUB]);

    useEffect(() => {
        const resumeState = buildSavedDraftResumeState(savedDraftEdit);

        if (!resumeState || hydratedSavedDraftIdRef.current === resumeState.shipmentId) {
            return;
        }

        // A multidrop shipment's stops come from its Orders, which may not be loaded on the
        // first render. Don't commit (or mark hydrated) an empty multidrop hydration — doing so
        // locks in blank line items and skips re-hydration once the Orders actually arrive.
        if (resumeState.isMultidrop && !(Array.isArray(resumeState.stops) && resumeState.stops.length >= 2)) {
            return;
        }

        hydratedSavedDraftIdRef.current = resumeState.shipmentId;

        // Resolve the main pickup/dropoff + any intermediate waypoints. Multidrop stops come from the
        // ordered Orders list (first = main pickup, last = main dropoff, middle = extra waypoints);
        // single-drop uses the pickup/dropoff pair.
        const defaultWaypointItem = () => ({ quantity: 1, detail: '', unit: '', weight: 1, length: 1, width: 1, height: 1, nmfc: '' });
        const isMultidropResume = Boolean(resumeState.isMultidrop && Array.isArray(resumeState.stops) && resumeState.stops.length >= 2);
        const stops = isMultidropResume ? resumeState.stops : [];
        const firstStop = isMultidropResume ? stops[0] : null;
        const lastStop = isMultidropResume ? stops[stops.length - 1] : null;
        const middleStops = isMultidropResume ? stops.slice(1, -1) : [];

        const pickupLocation = isMultidropResume ? (firstStop.location || {}) : resumeState.pickup;
        const dropoffLocation = isMultidropResume ? (lastStop.location || {}) : resumeState.dropoff;
        const mainPickupItems = isMultidropResume ? firstStop.items : resumeState.items;
        const mainDropoffItems = isMultidropResume ? lastStop.items : resumeState.items;
        const defaultItemsResume = isMultidropResume ? (mainPickupItems || [defaultWaypointItem()]) : resumeState.items;
        const pickupInstructionResume = isMultidropResume ? (firstStop.pickupInstruction || '') : resumeState.pickupInstruction;
        const dropoffInstructionResume = isMultidropResume ? (lastStop.dropoffInstruction || '') : resumeState.dropoffInstruction;

        activeEditOrderIdsRef.current = {
            pickupOrderId: isMultidropResume ? (firstStop.orderId || null) : (resumeState.pickupOrderId || null),
            dropoffOrderId: isMultidropResume ? (lastStop.orderId || null) : (resumeState.dropoffOrderId || null),
            waypointOrderIds: middleStops.map(stop => stop.orderId || null),
        };
        if (savedDraftEdit && savedDraftEdit.isActiveEdit) {
            setActiveEditOriginalPrice(Number.isFinite(resumeState.price) ? resumeState.price : null);
            activeEditPricingBaselineRef.current = buildActiveEditPricingSignature({
                pickup: pickupLocation,
                dropoff: dropoffLocation,
                vehicleType: resumeState.vehicleType,
                weight: resumeState.weight,
                numPeople: resumeState.numPeople,
                containsAlcohol: resumeState.containsAlcohol,
                requirements: {
                    isDolly: resumeState.isDolly,
                    isHazmat: resumeState.isHazmat,
                    isTwicCard: resumeState.isTwicCard,
                    isLiftgate: resumeState.isLiftgate,
                    isPalletJack: resumeState.isPalletJack,
                    isHandLoading: resumeState.isHandLoading,
                    isWeatherProtection: resumeState.isWeatherProtection,
                    isMedical: resumeState.isMedical,
                    isPpe: resumeState.isPpe,
                },
                isScheduled: resumeState.isScheduled,
                scheduledFor: resumeState.scheduledFor,
            });
            setHasActiveEditPricingChanges(false);
        }

        if (isMultidropResume) {
            // Multi-waypoint editing: turn on the infinite-waypoints mode and rebuild the stop list.
            setAllowMultipleWaypoints(true);
            setIsInfiniteWaypointsSelected(true);
            setPickupItems(mainPickupItems);
            setPickupItemsError(mainPickupItems.map(() => false));
            setDropoffItems(mainDropoffItems);
            setDropoffItemsError(mainDropoffItems.map(() => false));
            setPickAndDropWaypointsArray(
                middleStops.map((stop, index) => {
                    const loc = stop.location || {};
                    const stopItems = Array.isArray(stop.items) && stop.items.length ? stop.items : [defaultWaypointItem()];
                    const pickItems = stop.isPickup ? stopItems : [defaultWaypointItem()];
                    const dropItems = stop.isPickup ? [defaultWaypointItem()] : stopItems;
                    return {
                        waypointKey: `waypoint-resume-${resumeState.shipmentId}-${index}`,
                        waypointFirstname: loc.first_name || '',
                        waypointLastname: loc.last_name || '',
                        waypointPhone: loc.phone || '',
                        waypointAddress1: loc.address_1 || null,
                        waypointAddress2: loc.address_2 || null,
                        waypointFullAddress: loc.address || loc.formatted_address || '',
                        waypointLat: loc.lat != null ? String(loc.lat) : '',
                        waypointLng: loc.lng != null ? String(loc.lng) : '',
                        waypointCity: loc.city || '',
                        waypointState: loc.state || '',
                        waypointCountry: loc.country || 'US',
                        waypointZip: loc.zip || '',
                        waypointPickupInstruction: stop.pickupInstruction || '',
                        waypointDropoffInstruction: stop.dropoffInstruction || '',
                        waypointBusinessName: loc.business_name || '',
                        waypointCustomerSuggestions: [],
                        isWaypointAutoFilled: true,
                        isPinnedLocation: false,
                        pickItems,
                        dropItems,
                        pickItemsError: pickItems.map(() => false),
                        dropItemsError: dropItems.map(() => false),
                        isWaypointPickup: Boolean(stop.isPickup),
                        isWaypointDropoff: !stop.isPickup,
                        waypointOrderId: stop.orderId || null,
                    };
                })
            );
            // Keep the parallel per-waypoint errors array the same length as the waypoints, or the
            // render (infinitePickAndDropWaypointsErrors[index].waypointFirstname) throws.
            setInfinitePickAndDropWaypointsErrors(
                middleStops.map(() => ({
                    waypointFirstname: false,
                    waypointLastname: false,
                    waypointPhone: false,
                    waypointAddress: false,
                }))
            );
        }

        setPickupInfo(pickupLocation);
        setPickFirstname(pickupLocation.first_name || '');
        setPickLastname(pickupLocation.last_name || '');
        setPickPhone(pickupLocation.phone || '');
        setPickFullAddress(pickupLocation.address || pickupLocation.formatted_address || '');
        setPickAddress1(pickupLocation.address_1 || '');
        setPickAddress2(pickupLocation.address_2 || '');
        setPickCity(pickupLocation.city || '');
        setPickState(pickupLocation.state || '');
        setPickZip(pickupLocation.zip || '');
        setPickCountry(pickupLocation.country || 'US');
        setPickLat(pickupLocation.lat || null);
        setPickLng(pickupLocation.lng || null);
        setPickBusinessName(pickupLocation.business_name || '');
        setDropoffInfo(dropoffLocation);
        setDropFirstname(dropoffLocation.first_name || '');
        setDropLastname(dropoffLocation.last_name || '');
        setDropPhone(dropoffLocation.phone || '');
        setDropFullAddress(dropoffLocation.address || dropoffLocation.formatted_address || '');
        setDropAddress1(dropoffLocation.address_1 || '');
        setDropAddress2(dropoffLocation.address_2 || '');
        setDropCity(dropoffLocation.city || '');
        setDropState(dropoffLocation.state || '');
        setDropZip(dropoffLocation.zip || '');
        setDropCountry(dropoffLocation.country || 'US');
        setDropLat(dropoffLocation.lat || null);
        setDropLng(dropoffLocation.lng || null);
        setDropoffBusinessName(dropoffLocation.business_name || '');
        setDefaultItems(defaultItemsResume);
        setDefaultItemsError(defaultItemsResume.map(() => false));
        setPickup(removeExistingBrokerBlock(pickupInstructionResume));
        setBrokerPickupNotes(extractBrokerBlock(pickupInstructionResume));
        setDropoff(removeExistingBrokerBlock(dropoffInstructionResume));
        setBrokerDropoffNotes(extractBrokerBlock(dropoffInstructionResume));
        setMerchantOrderID(resumeState.merchantOrderId);
        setVehicleType(normalizeShipmentVehicleTypeForApi(resumeState.vehicleType));
        setNumPeople(resumeState.numPeople);
        setWeight(resumeState.weight);
        setIsSaved(true);
        setIsScheduled(resumeState.isScheduled);
        setScheduledFor(resumeState.scheduledFor ? moment(resumeState.scheduledFor) : null);
        setIsExpedited(resumeState.isExpedited);
        setIsSuperExpedited(resumeState.isSuperExpedited);
        setIsShipTomorrow(resumeState.isShipTomorrow);
        setDistance(resumeState.distance);
        setPrice(resumeState.price);
        setStandardPrice(resumeState.price);
        setPriceRangeLow(resumeState.priceRangeLow);
        setPriceRangeHigh(resumeState.priceRangeHigh);
        setRoute(resumeState.route);
        setContainsAlcohol(resumeState.containsAlcohol);
        setIsForkLift(resumeState.isForkLift);
        setIsDolly(resumeState.isDolly);
        setIsHazmat(resumeState.isHazmat);
        setIsPalletJack(resumeState.isPalletJack);
        setIsTWICCard(resumeState.isTwicCard);
        setIsLiftgate(resumeState.isLiftgate);
        setIsHandLoadUnload(resumeState.isHandLoading);
        setIsWeatherProtection(resumeState.isWeatherProtection);
        setIsMedical(resumeState.isMedical);
        setIsPPE(resumeState.isPpe);
        setShowForm3(false);
        // Editing: reveal the pricing step immediately with the order's existing price (no AI quote).
        props.handleForm(true, Boolean(savedDraftEdit && savedDraftEdit.isActiveEdit));
    }, [savedDraftEdit]);

    // Prefill a NEW win-back order from an abandoned-cart quote: seed the quote's pickup/dropoff
    // points + vehicle/weight/accessorials so the normal create flow re-quotes and shows the price.
    // Runs once, only in create mode (never during a saved/active edit).
    useEffect(() => {
        if (!abandonedPrefill || savedDraftEdit) {
            return;
        }
        const prefillKey = abandonedPrefill.lead_id || abandonedPrefill.quote_id || 'abandoned';
        if (abandonedPrefillAppliedRef.current === prefillKey) {
            return;
        }
        const pickup = abandonedPrefill.pickup || {};
        const dropoff = abandonedPrefill.dropoff || {};
        if (pickup.lat == null || pickup.lng == null || dropoff.lat == null || dropoff.lng == null) {
            return; // need both geocoded points to drive a quote; leave for manual entry
        }
        abandonedPrefillAppliedRef.current = prefillKey;

        const customer = abandonedPrefill.customer || {};

        // Use the quote's actual pickup/dropoff points (not the store) so the re-quote matches the lead.
        setIsPickupStore(false);
        setIsDropoffStore(false);

        setPickFirstname('');
        setPickLastname('');
        setPickPhone('');
        setPickFullAddress(pickup.address || '');
        setPickAddress1(pickup.address_1 || '');
        setPickAddress2(pickup.address_2 || '');
        setPickCity(pickup.city || '');
        setPickState(pickup.state || '');
        setPickZip(pickup.zip || '');
        setPickCountry(pickup.country || 'US');
        setPickLat(pickup.lat);
        setPickLng(pickup.lng);
        setPickBusinessName('');

        setDropFirstname(customer.first_name || '');
        setDropLastname(customer.last_name || '');
        setDropPhone(customer.phone || '');
        setDropFullAddress(dropoff.address || '');
        setDropAddress1(dropoff.address_1 || '');
        setDropAddress2(dropoff.address_2 || '');
        setDropCity(dropoff.city || '');
        setDropState(dropoff.state || '');
        setDropZip(dropoff.zip || '');
        setDropCountry(dropoff.country || 'US');
        setDropLat(dropoff.lat);
        setDropLng(dropoff.lng);
        setDropoffBusinessName('');

        if (abandonedPrefill.vehicle_type) {
            setVehicleType(normalizeShipmentVehicleTypeForApi(abandonedPrefill.vehicle_type));
        }
        setNumPeople(abandonedPrefill.num_of_gophrs != null ? abandonedPrefill.num_of_gophrs : 1);
        if (abandonedPrefill.weight != null && abandonedPrefill.weight !== '') {
            setWeight(abandonedPrefill.weight);
        }
        if (abandonedPrefill.scheduled_for) {
            setIsScheduled(true);
            setScheduledFor(moment(abandonedPrefill.scheduled_for));
        }
        if (abandonedPrefill.quoted_fee != null) {
            setPrice(Number(abandonedPrefill.quoted_fee));
            setStandardPrice(Number(abandonedPrefill.quoted_fee));
        }
        const acc = abandonedPrefill.accessorials || {};
        setIsExpedited(Boolean(acc.is_expedited));
        setIsSuperExpedited(Boolean(acc.is_super_expedited));
        setContainsAlcohol(Boolean(acc.contains_alcohol));
        setIsForkLift(Boolean(acc.is_fork_lift));
        setIsDolly(Boolean(acc.is_dolly));
        setIsHazmat(Boolean(acc.is_hazmat));
        setIsPalletJack(Boolean(acc.is_pallet_jack));
        setIsTWICCard(Boolean(acc.is_twic_card));
        setIsLiftgate(Boolean(acc.is_liftgate));
        setIsHandLoadUnload(Boolean(acc.is_hand_loading));
        setIsWeatherProtection(Boolean(acc.is_weather_protection));
        setIsMedical(Boolean(acc.is_medical));
        setIsPPE(Boolean(acc.is_personal_protective_equipment));

        props.handleForm(true, false);
    }, [abandonedPrefill, savedDraftEdit]);

    useEffect(() => {
        if (isSaved) {
            setShipmentStatus('shipment_saved');
            setOrderStatus('order_saved');
        } else {
            setShipmentStatus('shipment_ready_for_driver');
            setOrderStatus('awaiting_driver');
        }

        setShowForm(props.showForm);
        setErrorMessageSubmit(props.errorSubmit);
        setErrorMessageDistance(props.errorDistance);

        //! This method used to compare equality of array of objects
        var isEqual = function(value, other) {
            // Get the value type
            var type = Object.prototype.toString.call(value);

            // If the two objects are not the same type, return false
            if (type !== Object.prototype.toString.call(other)) return false;

            // If items are not an object or array, return false
            if (['[object Array]', '[object Object]'].indexOf(type) < 0) return false;

            // Compare the length of the length of the two items
            var valueLen = type === '[object Array]' ? value.length : Object.keys(value).length;
            var otherLen = type === '[object Array]' ? other.length : Object.keys(other).length;
            if (valueLen !== otherLen) return false;

            // Compare two items
            var compare = function(item1, item2) {
                // Get the object type
                var itemType = Object.prototype.toString.call(item1);

                // If an object or array, compare recursively
                if (['[object Array]', '[object Object]'].indexOf(itemType) >= 0) {
                    if (!isEqual(item1, item2)) return false;
                }

                // Otherwise, do a simple comparison
                else {
                    // If the two items are not the same type, return false
                    if (itemType !== Object.prototype.toString.call(item2)) return false;

                    // Else if it's a function, convert to a string and compare
                    // Otherwise, just compare
                    if (itemType === '[object Function]') {
                        if (item1.toString() !== item2.toString()) return false;
                    } else {
                        if (item1 !== item2) return false;
                    }
                }
            };

            // Compare properties
            if (type === '[object Array]') {
                for (var i = 0; i < valueLen; i++) {
                    if (compare(value[i], other[i]) === false) return false;
                }
            } else {
                for (var key in value) {
                    if (value.hasOwnProperty(key)) {
                        if (compare(value[key], other[key]) === false) return false;
                    }
                }
            }

            // If nothing failed, return true
            return true;
        };

        // Preserve the stored quote on open, then resume normal quoting after a pricing input changes.
        if (isActiveEdit) {
            const currentPricingSignature = buildActiveEditPricingSignature({
                pickup: pickupInfo,
                dropoff: dropoffInfo,
                vehicleType,
                weight,
                numPeople,
                containsAlcohol,
                requirements: {
                    isDolly,
                    isHazmat,
                    isTwicCard: isTWICCard,
                    isLiftgate,
                    isPalletJack,
                    isHandLoading: isHandLoadUnload,
                    isWeatherProtection,
                    isMedical,
                    isPpe: isPPE,
                },
                isScheduled,
                scheduledFor,
            });

            if (!activeEditPricingBaselineRef.current || currentPricingSignature === activeEditPricingBaselineRef.current) {
                return;
            }

            if (!hasActiveEditPricingChanges) {
                setHasActiveEditPricingChanges(true);
            }
        }

        if (allowMultipleWaypoints && isInfiniteWaypointsSelected) {
            if ((!isScheduled || scheduledFor) && (
                previousPick !== pickupInfo ||
                previousNumPeople !== numPeople ||
                previousVehicleType !== vehicleType ||
                previousWeight !== weight ||
                !isEqual(previousPickAndDropWaypointsArray, pickAndDropWaypointsArray) ||
                previousDolly !== isDolly ||
                previousHazmat !== isHazmat ||
                previousTWICCard !== isTWICCard ||
                previousLiftgate !== isLiftgate ||
                previousPalletJack !== isPalletJack ||
                previousHandLoad !== isHandLoadUnload ||
                previousWeatherProtection !== isWeatherProtection ||
                previousMedical !== isMedical ||
                previousPPE !== isPPE ||
                previousOverSize !== isOverSize ||
                previousIsScheduled !== isScheduled ||
                previousScheduledFor !== scheduledFor
            )) {
                let start = pickupInfo.lng + ',' + pickupInfo.lat;
                let end = ';' + dropoffInfo.lng + ',' + dropoffInfo.lat;
                let coordinates = '';
                let orderPoints = [];
                let waypointPoints = [];
                let orderDropPoints = [];
                let dropsArrayFilled = true;
                let itemsCountPerStop = [];

                let tqlMultiDropStops = [];
                tqlMultiDropStops.push({
                    stopType: 'PICK',
                    stopOrder: 1,
                    postalCode: pickupInfo.zip,
                    countryCode: getCountryCode(pickCountry || 'US')
                });
                let dropWaypointCount = 0;
                pickAndDropWaypointsArray.forEach((ar, index) => {
                    // Check if waypoint is marked as pickup or dropoff and validate coordinates
                    const isActiveWaypoint = ar.isWaypointPickup || ar.isWaypointDropoff;
                    if (ar.isWaypointPickup) {
                        waypointPoints.push([ar.waypointLat, ar.waypointLng]);
                    }
                    if (ar.isWaypointDropoff) {
                        dropWaypointCount++;
                        waypointPoints.push([ar.waypointLat, ar.waypointLng]);
                    }
                    const hasValidCoordinates = ar.waypointLng && ar.waypointLat;

                    if (isActiveWaypoint && !hasValidCoordinates) {
                        dropsArrayFilled = false;
                    }

                    if (hasValidCoordinates) {
                        coordinates += ';' + ar.waypointLng + ',' + ar.waypointLat;
                        orderPoints.push([ar.waypointLat, ar.waypointLng]);
                        orderDropPoints.push({ lat: ar.waypointLat, lng: ar.waypointLng });
                        let count = 0;
                        ar.dropItems.forEach(it => (count += it.quantity));
                        itemsCountPerStop.push(count);
                        tqlMultiDropStops.push({
                            stopType: 'DROP',
                            stopOrder: index + 2,
                            postalCode: ar.waypointZip,
                            countryCode: getCountryCode(ar.waypointCountry || 'US')
                        });
                    }
                });
                tqlMultiDropStops.push({
                    stopType: 'DROP',
                    stopOrder: pickAndDropWaypointsArray.length + 2,
                    postalCode: dropoffInfo.zip,
                    countryCode: getCountryCode(dropCountry || 'US')
                });

                //! have not find the way to optimize the routes and it depends on the order you put the waypoint
                let requestEndpointInfiniteDrops = MAPBOX_ENDPOINT + start + coordinates + end + '?alternatives=true&geometries=geojson&access_token=' + MAPBOX_TOKEN;

                if (pickupInfo.lat && pickupInfo.lng && dropoffInfo.lat && dropoffInfo.lng && vehicleType && weight && dropsArrayFilled) {
                    axios
                        .get(requestEndpointInfiniteDrops)
                        .then(response => {
                            var route = response.data.routes[0];
                            for (let i = 1; i < response.data.routes.length; i++) {
                                if (response.data.routes[i].duration < route.duration) {
                                    route = response.data.routes[i];
                                }
                            }
                            if (route) {
                                setDistance(route.distance / 1609.344);
                                setRoute(route);
                                let realMiles = route.distance / 1609.344;
                                let roundMiles = Math.round(realMiles);
                                let itemNum = 0;
                                pickupItems.forEach(it => {
                                    itemNum += it.quantity;
                                });
                                let standardData = {
                                    distance: realMiles,
                                    weight: weight,
                                    is_expedited: false,
                                    is_super_expedited: false,
                                    quoted_time: route.duration / 60 / 60,
                                    num_of_gophrs: numPeople,
                                    vehicle_type: normalizeShipmentVehicleTypeForApi(vehicleType),
                                    contains_alcohol: containsAlcohol,
                                    market_id: (store && store.market_id) || storeMarketId,
                                    order_points: [[pickupInfo.lat, pickupInfo.lng], ...waypointPoints, [dropoffInfo.lat, dropoffInfo.lng]],
                                    num_of_drops: dropWaypointCount + 1,
                                    items_count_per_stop_array: itemsCountPerStop,
                                    item_count: itemNum,
                                    is_roundtrip: false,
                                    is_fork_lift: isForkLift,
                                    is_dolly: isDolly,
                                    is_hazmat: isHazmat,
                                    is_pallet_jack: isPalletJack,
                                    is_twic_card: isTWICCard,
                                    is_liftgate: isLiftgate,
                                    is_hand_loading: isHandLoadUnload,
                                    is_weather_protection: isWeatherProtection,
                                    is_medical: isMedical,
                                    is_personal_protective_equipment: isPPE,
                                    temperature,
                                    scheduled_for: scheduledFor || tomorrow, // add one day to get the quote, sometimes can not get the quote today, update later
                                    load_value: loadValue,
                                    stops: tqlMultiDropStops,
                                    commodity,
                                    do_tql_cal: true,
                                    is_over_size: isOverSize,
                                    drop_off_state: pickAndDropWaypointsArray[pickAndDropWaypointsArray.length - 1].state,
                                    is_market_base_quote: false,
                                    service_level: (() => {
                                        if (isExpedited || isSuperExpedited) return 'EXPEDITED';
                                        if (isScheduled && scheduledFor) {
                                            const hoursAhead = (new Date(scheduledFor).getTime() - Date.now()) / (1000 * 60 * 60);
                                            const isTomorrow = new Date(scheduledFor).toDateString() === new Date(Date.now() + 86400000).toDateString();
                                            if (hoursAhead >= 24) return isTomorrow ? 'SHIP_TOMORROW' : 'SCHEDULED';
                                        }
                                        return 'SAME_DAY';
                                    })()
                                };
                                let expeditedData = {
                                    ...standardData,
                                    is_expedited: true
                                };
                                let superExpeditedData = {
                                    ...standardData,
                                    is_super_expedited: true
                                };
                                if (vehicleType === '18_wheeler_dry_van' || vehicleType === '18_wheeler_flat_bed' || vehicleType === '18_wheeler_reefer') {
                                    postQuoteWithFallback({
                                        method: 'post',
                                        withCredentials: true,
                                        headers: { 'content-type': 'application/json', Authorization: ACCESS_TOKEN },
                                        data: { ...standardData, distance: roundMiles }
                                    })
                                        .then(response => {
                                            // console.log('000 multi ', response);
                                            props.handleErrorDistance({ errorMessage: '', errorCode: 0 });

                                            const respPayload = response.data.payload;
                                            if (typeof respPayload.volume_discount_percent === 'number' && respPayload.volume_discount_percent > 0) {
                                                setQuoteVolumeDiscountPercent(respPayload.volume_discount_percent);
                                                setQuoteVolumeDiscountAmount(Number(respPayload.volume_discount_amount) || 0);
                                            } else {
                                                setQuoteVolumeDiscountPercent(0);
                                                setQuoteVolumeDiscountAmount(0);
                                            }

                                            if (respPayload.hasOwnProperty('shipping_fee_range')) {
                                                setHasTqlRateValue(false);
                                                setStandardPriceRangeLow(respPayload.shipping_fee_range.min);
                                                setStandardPriceRangeHigh(respPayload.shipping_fee_range.max);
                                                setStandardPrice(respPayload.shipping_fee);
                                                setExpeditedPriceRangeLow(respPayload.shipping_fee_range_expedited.min);
                                                setExpeditedPriceRangeHigh(respPayload.shipping_fee_range_expedited.max);
                                                setExpeditedPrice(respPayload.shipping_fee);
                                                setSuperExpeditedPriceRangeLow(respPayload.shipping_fee_range_super_expedited.min);
                                                setSuperExpeditedPriceRangeHigh(respPayload.shipping_fee_range_super_expedited.max);
                                                setSuperExpeditedPrice(respPayload.shipping_fee);
                                            } else {
                                                setHasTqlRateValue(true);
                                                setStandardPriceRangeLow(0);
                                                setStandardPriceRangeHigh(0);
                                                setStandardPrice(respPayload.shipping_fee);
                                                setExpeditedPriceRangeLow(0);
                                                setExpeditedPriceRangeHigh(0);
                                                setExpeditedPrice(respPayload.shipping_fee_expedited);
                                                setSuperExpeditedPriceRangeLow(0);
                                                setSuperExpeditedPriceRangeHigh(0);
                                                setSuperExpeditedPrice(respPayload.shipping_fee_super_expedited);
                                                setTqlRateBlob(response.data);
                                            }
                                            props.handleForm(true, true);
                                        })
                                        .catch(error => {
                                            props.handleErrorDistance(buildQuoteError(error, 2));
                                            props.notify('tc', 'distance');
                                        });
                                } else {
                                    setHasTqlRateValue(false);
                                    postQuoteWithFallback({
                                        method: 'post',
                                        withCredentials: true,
                                        headers: { 'content-type': 'application/json', Authorization: ACCESS_TOKEN },
                                        data: standardData
                                    })
                                        .then(response => {
                                            props.handleErrorDistance({ errorMessage: '', errorCode: 0 });

                                            if (requiresRangeQuoteForVehicleType(vehicleType)) {
                                                setStandardPriceRangeLow(response.data.payload.shipping_fee_range.min);
                                                setStandardPriceRangeHigh(response.data.payload.shipping_fee_range.max);
                                                setStandardPrice(response.data.payload.shipping_fee);
                                            } else {
                                                setStandardPrice(response.data.payload.shipping_fee);
                                            }
                                            // Capture dynamic pricing info
                                            if (response.data.payload && response.data.payload.dynamic_pricing) {
                                                console.log('[dynamic-pricing] badge data:', response.data.payload.dynamic_pricing);
                                                setDynamicPricingInfo(response.data.payload.dynamic_pricing);
                                            } else {
                                                console.log('[dynamic-pricing] no dynamic_pricing in response payload', Object.keys(response.data.payload || {}));
                                            }
                                            props.handleForm(true, true);
                                        })
                                        .catch(error => {
                                            props.handleErrorDistance(buildQuoteError(error, 2));
                                            props.notify('tc', 'distance');
                                        });
                                    postQuoteWithFallback({
                                        method: 'post',
                                        withCredentials: true,
                                        headers: {
                                            'content-type': 'application/json',
                                            Authorization: 'Bearer ' + window.localStorage.getItem('access_token')
                                        },
                                        data: expeditedData
                                    })
                                        .then(response => {
                                            props.handleErrorDistance({ errorMessage: '', errorCode: 0 });
                                            if (requiresRangeQuoteForVehicleType(vehicleType)) {
                                                setExpeditedPriceRangeLow(response.data.payload.shipping_fee_range.min);
                                                setExpeditedPriceRangeHigh(response.data.payload.shipping_fee_range.max);
                                                setExpeditedPrice(response.data.payload.shipping_fee);
                                            } else {
                                                setExpeditedPrice(response.data.payload.shipping_fee);
                                            }
                                            props.handleForm(true, true);
                                        })
                                        .catch(error => {
                                            props.handleErrorDistance(buildQuoteError(error, 3));
                                        });
                                    postQuoteWithFallback({
                                        method: 'post',
                                        withCredentials: true,
                                        headers: {
                                            'content-type': 'application/json',
                                            Authorization: 'Bearer ' + window.localStorage.getItem('access_token')
                                        },
                                        data: superExpeditedData
                                    })
                                        .then(response => {
                                            props.handleErrorDistance({ errorMessage: '', errorCode: 0 });
                                            if (requiresRangeQuoteForVehicleType(vehicleType)) {
                                                setSuperExpeditedPriceRangeLow(response.data.payload.shipping_fee_range.min);
                                                setSuperExpeditedPriceRangeHigh(response.data.payload.shipping_fee_range.max);
                                                setSuperExpeditedPrice(response.data.payload.shipping_fee);
                                            } else {
                                                setSuperExpeditedPrice(response.data.payload.shipping_fee);
                                            }
                                            // setPrice(response.data.payload);
                                            props.handleForm(true, true);
                                        })
                                        .catch(error => {
                                            props.handleErrorDistance(buildQuoteError(error, 4));
                                        });
                                }
                            }
                        })
                        .catch(error => {
                            props.handleErrorDistance(buildQuoteError(error, 1));
                            props.notify('tc', 'distance');
                        });
                }
            }
        } else {
            if ((!isScheduled || scheduledFor) && (
                previousDrop !== dropoffInfo ||
                previousAlc !== containsAlcohol ||
                previousPick !== pickupInfo ||
                previousNumPeople !== numPeople ||
                previousVehicleType !== vehicleType ||
                previousWeight !== weight ||
                previousDolly !== isDolly ||
                previousHazmat !== isHazmat ||
                previousTWICCard !== isTWICCard ||
                previousLiftgate !== isLiftgate ||
                previousPalletJack !== isPalletJack ||
                previousHandLoad !== isHandLoadUnload ||
                previousWeatherProtection !== isWeatherProtection ||
                previousMedical !== isMedical ||
                previousPPE !== isPPE ||
                previousOverSize !== isOverSize ||
                previousIsScheduled !== isScheduled ||
                previousScheduledFor !== scheduledFor
            )) {
                // Check if form entries are valid
                let end = dropoffInfo.lng + ',' + dropoffInfo.lat;
                let start = pickupInfo.lng + ',' + pickupInfo.lat;
                //! This distance check does not make sense somehow, did not cover all cases even for 2 stops
                let requestEndpointDistanceCheck = MAPBOX_ENDPOINT + controlLong + ',' + controlLat + ';' + end + '?geometries=geojson&access_token=' + MAPBOX_TOKEN;
                let requestEndpointRegular = MAPBOX_ENDPOINT + start + ';' + end + '?alternatives=true&geometries=geojson&access_token=' + MAPBOX_TOKEN;

                if (dropoffInfo.lat && dropoffInfo.lng && pickupInfo.lat && pickupInfo.lng && numPeople && vehicleType && weight) {
                    // Get distance from MapBox
                    axios
                        .get(requestEndpointRegular)
                        .then(response => {
                            var route = response.data.routes[0];
                            for (let i = 1; i < response.data.routes.length; i++) {
                                if (response.data.routes[i].duration < route.duration) {
                                    route = response.data.routes[i];
                                }
                            }
                            if (route) {
                                setDistance(route.distance / 1609.344);
                                setRoute(route);
                                let realMiles = route.distance / 1609.344;
                                let roundMiles = Math.round(realMiles);
                                let standardData = {
                                    distance: realMiles,
                                    weight: weight,
                                    is_expedited: false,
                                    is_super_expedited: false,
                                    quoted_time: route.duration / 60 / 60,
                                    num_of_gophrs: numPeople,
                                    vehicle_type: normalizeShipmentVehicleTypeForApi(vehicleType),
                                    contains_alcohol: containsAlcohol,
                                    market_id: (store && store.market_id) || storeMarketId,
                                    order_points: [[pickupInfo.lat, pickupInfo.lng], [dropoffInfo.lat, dropoffInfo.lng]],
                                    num_of_drops: 1,
                                    is_roundtrip: false,
                                    is_fork_lift: isForkLift,
                                    is_dolly: isDolly,
                                    is_hazmat: isHazmat,
                                    is_pallet_jack: isPalletJack,
                                    is_twic_card: isTWICCard,
                                    is_liftgate: isLiftgate,
                                    is_hand_loading: isHandLoadUnload,
                                    is_weather_protection: isWeatherProtection,
                                    is_medical: isMedical,
                                    is_personal_protective_equipment: isPPE,
                                    temperature,
                                    scheduled_for: scheduledFor || tomorrow, // add one day to get the quote, sometimes can not get the quote today, update later
                                    load_value: loadValue,
                                    stops: [
                                        {
                                            stopType: 'PICK',
                                            stopOrder: 1,
                                            postalCode: pickupInfo.zip,
                                            countryCode: getCountryCode(pickCountry || 'US')
                                        },
                                        {
                                            stopType: 'DROP',
                                            stopOrder: 2,
                                            postalCode: dropoffInfo.zip,
                                            countryCode: getCountryCode(dropCountry || 'US')
                                        }
                                    ],
                                    commodity: defaultItems.detail || commodity,
                                    do_tql_cal: true,
                                    is_over_size: isOverSize,
                                    drop_off_state: dropoffInfo.state,
                                    is_market_base_quote: false,
                                    service_level: (() => {
                                        if (isExpedited || isSuperExpedited) return 'EXPEDITED';
                                        if (isScheduled && scheduledFor) {
                                            const hoursAhead = (new Date(scheduledFor).getTime() - Date.now()) / (1000 * 60 * 60);
                                            const isTomorrow = new Date(scheduledFor).toDateString() === new Date(Date.now() + 86400000).toDateString();
                                            if (hoursAhead >= 24) return isTomorrow ? 'SHIP_TOMORROW' : 'SCHEDULED';
                                        }
                                        return 'SAME_DAY';
                                    })()
                                };
                                // console.log('standardData ', standardData);
                                let expeditedData = {
                                    ...standardData,
                                    is_expedited: true
                                };
                                let superExpeditedData = {
                                    ...standardData,
                                    is_super_expedited: true
                                };
                                if (vehicleType === '18_wheeler_dry_van' || vehicleType === '18_wheeler_flat_bed' || vehicleType === '18_wheeler_reefer') {
                                    axios.get(requestEndpointDistanceCheck).then(response => {
                                        let quickDistance = route.distance / 1609.344;
                                        if (quickDistance > 10000) {
                                            props.handleErrorDistance({
                                                errorMessage:
                                                    "We're sorry, but your delivery distance of " +
                                                    parseInt(quickDistance) +
                                                    ' miles is too far.  Please, contact a Gophr representative for more information.',
                                                errorCode: 1
                                            });
                                        } else {
                                            // Use distance to get both price quotes
                                            props.handleErrorDistance({ errorMessage: '', errorCode: 0 });
                                            postQuoteWithFallback({
                                                method: 'post',
                                                withCredentials: true,
                                                headers: { 'content-type': 'application/json', Authorization: ACCESS_TOKEN },
                                                data: { ...standardData, distance: roundMiles }
                                            })
                                                .then(response => {
                                                    props.handleErrorDistance({ errorMessage: '', errorCode: 0 });
                                                    // console.log('000 ', response);
                                                    if (response.data.payload.hasOwnProperty('shipping_fee_range')) {
                                                        setHasTqlRateValue(false);
                                                        setStandardPriceRangeLow(response.data.payload.shipping_fee_range.min);
                                                        setStandardPriceRangeHigh(response.data.payload.shipping_fee_range.max);
                                                        setStandardPrice(response.data.payload.shipping_fee);
                                                        setExpeditedPriceRangeLow(response.data.payload.shipping_fee_range_expedited.min);
                                                        setExpeditedPriceRangeHigh(response.data.payload.shipping_fee_range_expedited.max);
                                                        setExpeditedPrice(response.data.payload.shipping_fee);
                                                        setSuperExpeditedPriceRangeLow(response.data.payload.shipping_fee_range_super_expedited.min);
                                                        setSuperExpeditedPriceRangeHigh(response.data.payload.shipping_fee_range_super_expedited.max);
                                                        setSuperExpeditedPrice(response.data.payload.shipping_fee);
                                                    } else {
                                                        setHasTqlRateValue(true);
                                                        setStandardPriceRangeLow(0);
                                                        setStandardPriceRangeHigh(0);
                                                        setStandardPrice(response.data.payload.shipping_fee);
                                                        setExpeditedPriceRangeLow(0);
                                                        setExpeditedPriceRangeHigh(0);
                                                        setExpeditedPrice(response.data.payload.shipping_fee_expedited);
                                                        setSuperExpeditedPriceRangeLow(0);
                                                        setSuperExpeditedPriceRangeHigh(0);
                                                        setSuperExpeditedPrice(response.data.payload.shipping_fee_super_expedited);
                                                        setTqlRateBlob(response.data);
                                                    }
                                                    props.handleForm(true, true);
                                                })
                                                .catch(error => {
                                                    props.handleErrorDistance(buildQuoteError(error, 2));
                                                    props.notify('tc', 'distance');
                                                });
                                        }
                                    });
                                } else {
                                    setHasTqlRateValue(false);
                                    axios.get(requestEndpointDistanceCheck).then(response => {
                                        let quickDistance = route.distance / 1609.344;
                                        if (quickDistance > 10000) {
                                            props.handleErrorDistance({
                                                errorMessage:
                                                    "We're sorry, but your delivery distance of " +
                                                    parseInt(quickDistance) +
                                                    ' miles is too far.  Please, contact a Gophr representative for more information.',
                                                errorCode: 1
                                            });
                                        } else {
                                            // Use distance to get both price quotes
                                            props.handleErrorDistance({ errorMessage: '', errorCode: 0 });
                                            postQuoteWithFallback({
                                                method: 'post',
                                                withCredentials: true,
                                                headers: { 'content-type': 'application/json', Authorization: ACCESS_TOKEN },
                                                data: standardData
                                            })
                                                .then(response => {
                                                    props.handleErrorDistance({ errorMessage: '', errorCode: 0 });
                                                    if (requiresRangeQuoteForVehicleType(vehicleType)) {
                                                        setStandardPriceRangeLow(response.data.payload.shipping_fee_range.min);
                                                        setStandardPriceRangeHigh(response.data.payload.shipping_fee_range.max);
                                                        setStandardPrice(response.data.payload.shipping_fee);
                                                    } else {
                                                        // Only set standard price from SAME_DAY if no 1-3 Day price available yet
                                                        // (the useEffect watching oneDayThreePrice will override this with +5%)
                                                        setStandardPrice(response.data.payload.shipping_fee);
                                                        setStandardQuotePayload(response.data.payload);
                                                        if (isScheduled && !isExpedited && !isSuperExpedited) {
                                                            setPrice(response.data.payload.shipping_fee);
                                                        }
                                                        // Check if fedex_rates exists before filtering (FedEx only supports US routes)
                                                        if (response.data.payload.fedex_rates && Array.isArray(response.data.payload.fedex_rates)) {
                                                            const filteredFedexRates = response.data.payload.fedex_rates.filter(rate =>
                                                                ['FEDEX_GROUND', 'FEDEX_2_DAY_AM', 'FEDEX_2_DAY', 'FEDEX_EXPRESS_SAVER'].includes(rate.serviceType)
                                                            );
                                                            setFedexPricesOptions(filteredFedexRates);
                                                        } else {
                                                            // No FedEx rates available (international routes not supported)
                                                            setFedexPricesOptions([]);
                                                        }
                                                    }
                                    // Capture dynamic pricing info for badge display
                                    if (response.data.payload && response.data.payload.dynamic_pricing) {
                                        setDynamicPricingInfo(response.data.payload.dynamic_pricing);
                                    }
                                                    props.handleForm(true, true);
                                                })
                                                .catch(error => {
                                                    props.handleErrorDistance(buildQuoteError(error, 2));
                                                    props.notify('tc', 'distance');
                                                });
                                            // 1-3 Day (SHIP_TOMORROW) quote
                                            if (!isScheduled) postQuoteWithFallback({
                                                method: 'post',
                                                withCredentials: true,
                                                headers: { 'content-type': 'application/json', Authorization: ACCESS_TOKEN },
                                                data: { ...standardData, is_ship_tomorrow: true, service_level: 'SHIP_TOMORROW' }
                                            })
                                                .then(response => {
                                                    if (response.data.payload && typeof response.data.payload.shipping_fee === 'number') {
                                                        setOneDayThreePrice(response.data.payload.shipping_fee);
                                                        setOneDayThreeQuotePayload(response.data.payload);
                                                        // Standard (Same Day) price is set by the useEffect below watching oneDayThreePrice
                                                    }
                                                })
                                                .catch(() => { /* non-critical — 1-3 day fallback silently skipped */ });
                                            postQuoteWithFallback({
                                                method: 'post',
                                                withCredentials: true,
                                                headers: {
                                                    'content-type': 'application/json',
                                                    Authorization: 'Bearer ' + window.localStorage.getItem('access_token')
                                                },
                                                data: expeditedData
                                            })
                                                .then(response => {
                                                    props.handleErrorDistance({ errorMessage: '', errorCode: 0 });
                                                    if (requiresRangeQuoteForVehicleType(vehicleType)) {
                                                        setExpeditedPriceRangeLow(response.data.payload.shipping_fee_range.min);
                                                        setExpeditedPriceRangeHigh(response.data.payload.shipping_fee_range.max);
                                                        setExpeditedPrice(response.data.payload.shipping_fee);
                                                    } else {
                                                        setExpeditedPrice(response.data.payload.shipping_fee);
                                                        setExpeditedQuotePayload(response.data.payload);
                                                        if (isScheduled && isExpedited && !isSuperExpedited) {
                                                            setPrice(response.data.payload.shipping_fee);
                                                        }
                                                    }
                                                    props.handleForm(true, true);
                                                })
                                                .catch(error => {
                                                    props.handleErrorDistance(buildQuoteError(error, 3));
                                                });
                                            postQuoteWithFallback({
                                                method: 'post',
                                                withCredentials: true,
                                                headers: {
                                                    'content-type': 'application/json',
                                                    Authorization: 'Bearer ' + window.localStorage.getItem('access_token')
                                                },
                                                data: superExpeditedData
                                            })
                                                .then(response => {
                                                    props.handleErrorDistance({ errorMessage: '', errorCode: 0 });
                                                    if (requiresRangeQuoteForVehicleType(vehicleType)) {
                                                        setSuperExpeditedPriceRangeLow(response.data.payload.shipping_fee_range.min);
                                                        setSuperExpeditedPriceRangeHigh(response.data.payload.shipping_fee_range.max);
                                                        setSuperExpeditedPrice(response.data.payload.shipping_fee);
                                                    } else {
                                                        setSuperExpeditedPrice(response.data.payload.shipping_fee);
                                                        setSuperExpeditedQuotePayload(response.data.payload);
                                                        if (isScheduled && isSuperExpedited) {
                                                            setPrice(response.data.payload.shipping_fee);
                                                        }
                                                    }
                                                    // setPrice(response.data.payload);
                                                    props.handleForm(true, true);
                                                })
                                                .catch(error => {
                                                    props.handleErrorDistance(buildQuoteError(error, 3));
                                                });
                                        }
                                    });
                                }
                            }
                        })
                        .catch(error => {
                            props.handleErrorDistance(buildQuoteError(error, 3));
                            props.notify('tc', 'distance');
                        });
                }
            }
        }
    });

    // function that returns true if value is a valid email, false otherwise
    // const validateEmail = email => {
    //     var emailRex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    //     return emailRex.test(String(email.toLowerCase().trim()));
    // };

    // function that returns true if value is a valid phone number, false otherwise
    const validatePhone = phone => {
        var phoneRex = /((?:\+|00)[17](?: |-)?|(?:\+|00)[1-9]\d{0,2}(?: |-)?|(?:\+|00)1-\d{3}(?: |-)?)?(0\d|\([0-9]{3}\)|[1-9]{0,3})(?:((?: |-)[0-9]{2}){4}|((?:[0-9]{2}){4})|((?: |-)[0-9]{3}(?: |-)[0-9]{4})|([0-9]{10}))/;

        if (phone.replace(/[^0-9]/g, '').length > 9 && phone.replace(/[^0-9]/g, '').length < 14) {
            return phoneRex.test(String(phone));
        }
    };

    const handleSubmit = () => {
        // Yaboi has had a few and this is how he's error checking...
        // big yikes

        const checkItemsError = (items, itemsError, setItemsError) => {
            let hasError = false;
            let itemsErrorArray = [...itemsError];
            items.forEach((pi, index) => {
                let detailError = !pi.detail || pi.detail === '';
                let unitError = !pi.unit || pi.unit === '';
                itemsErrorArray[index] = { detail: detailError, unit: unitError };
                if (detailError || unitError) hasError = true;
            });
            setItemsError(itemsErrorArray);
            return hasError;
        };

        const genericMatchCheck = (totalPickupItems, totalDropoffItems) => {
            let hasError = false;
            let pickCount = 0;
            let dropCount = 0;

            // Compare item details case-insensitively and ignoring surrounding whitespace so
            // visually-identical items (e.g. "Widget" vs "widget ") are not falsely flagged.
            const normalizeDetail = value => String(value === null || value === undefined ? '' : value).trim().toLowerCase();

            totalPickupItems.forEach(item => {
                pickCount += Number(item.quantity) || 0;
            });
            totalDropoffItems.forEach(item => {
                dropCount += Number(item.quantity) || 0;
            });

            if (pickCount !== dropCount) {
                hasError = true;
                setItemsMatchError(hasError);
                return hasError;
            }

            let totalPickupItemsCopy = [...totalPickupItems].map(item => normalizeDetail(item.detail));
            let totalDropoffItemsCopy = [...totalDropoffItems].map(item => normalizeDetail(item.detail));
            const uniqueArray = array => [...new Set(array.map(item => JSON.stringify(item)))].map(itemBack => JSON.parse(itemBack));

            let totalPickupItemsSet = uniqueArray(totalPickupItemsCopy);
            let totalDropoffItemsSet = uniqueArray(totalDropoffItemsCopy);

            if (!(totalPickupItemsSet.every(item => totalDropoffItemsSet.includes(item)) && totalDropoffItemsSet.every(item => totalPickupItemsSet.includes(item)))) {
                hasError = true;
                setItemsMatchError(hasError);
                return hasError;
            }

            totalPickupItemsSet.forEach(item => {
                let countPick = 0;
                let countDrop = 0;
                totalPickupItems.forEach(pick => {
                    if (item === normalizeDetail(pick.detail)) {
                        countPick += Number(pick.quantity) || 0;
                    }
                });
                totalDropoffItems.forEach(drop => {
                    if (item === normalizeDetail(drop.detail)) {
                        countDrop += Number(drop.quantity) || 0;
                    }
                });
                if (countPick !== countDrop) {
                    hasError = true;
                    setItemsMatchError(hasError);
                    return hasError;
                }
            });

            setItemsMatchError(hasError);
            return hasError;
        };

        const checkInfiniteDropMatchError = () => {
            // Start with initial pickup items
            let totalPickupItems = pickupItems.filter(item => {
                return item.detail !== '';
            });

            // Collect waypoint pickup and dropoff items in one loop
            let totalWaypointPickupItems = [];
            let totalWaypointDropoffItems = [];
            pickAndDropWaypointsArray.forEach(waypoint => {
                if (waypoint.isWaypointPickup && waypoint.pickItems) {
                    waypoint.pickItems.forEach(item => {
                        if (item.detail !== '') {
                            totalWaypointPickupItems.push(item);
                        }
                    });
                }
                if (waypoint.isWaypointDropoff && waypoint.dropItems) {
                    waypoint.dropItems.forEach(item => {
                        if (item.detail !== '') {
                            totalWaypointDropoffItems.push(item);
                        }
                    });
                }
            });

            // Add final dropoff items
            let totalDropoffItems = dropoffItems.filter(item => {
                return item.detail !== '';
            });

            // Combine all pickups and all dropoffs
            let allPickupItems = [...totalPickupItems, ...totalWaypointPickupItems];
            let allDropoffItems = [...totalWaypointDropoffItems, ...totalDropoffItems];

            return genericMatchCheck(allPickupItems, allDropoffItems);
        };

        let updatedErrors = errors;
        let updateInfiniteDropsErrors = infinitePickAndDropWaypointsErrors;

        if (allowMultipleWaypoints) {
            if (!isPickupStore) {
                if (pickFirstname) {
                    updatedErrors.pickupFirstname = false;
                } else {
                    updatedErrors.pickupFirstname = true;
                }
                if (pickLastname) {
                    updatedErrors.pickupLastname = false;
                } else {
                    updatedErrors.pickupLastname = true;
                }

                if (validatePhone(pickPhone)) {
                    updatedErrors.pickupPhone = false;
                } else {
                    updatedErrors.pickupPhone = true;
                }
                if (pickLat && pickLng) {
                    updatedErrors.pickupAddress = false;
                } else {
                    updatedErrors.pickupAddress = true;
                }
            } else {
                updatedErrors.pickupFirstname = false;
                updatedErrors.pickupLastname = false;
                updatedErrors.pickupPhone = false;
                updatedErrors.pickupAddress = false;
            }

            // Only validate waypoints if infinite waypoints is selected
            if (isInfiniteWaypointsSelected) {
                infinitePickAndDropWaypointsErrors.forEach((dr, i) => {
                    let drop = pickAndDropWaypointsArray[i];
                    if (drop.waypointFirstname) {
                        updateInfiniteDropsErrors[i].waypointFirstname = false;
                    } else {
                        updateInfiniteDropsErrors[i].waypointFirstname = true;
                    }
                    if (drop.waypointLastname) {
                        updateInfiniteDropsErrors[i].waypointLastname = false;
                    } else {
                        updateInfiniteDropsErrors[i].waypointLastname = true;
                    }

                    if (validatePhone(drop.waypointPhone)) {
                        updateInfiniteDropsErrors[i].waypointPhone = false;
                    } else {
                        updateInfiniteDropsErrors[i].waypointPhone = true;
                    }
                    if (drop.waypointLat && drop.waypointLng) {
                        updateInfiniteDropsErrors[i].waypointAddress = false;
                    } else {
                        updateInfiniteDropsErrors[i].waypointAddress = true;
                    }
                });
            }
        } else {
            if (!isPickupStore) {
                if (pickFirstname) {
                    updatedErrors.pickupFirstname = false;
                } else {
                    updatedErrors.pickupFirstname = true;
                }
                if (pickLastname) {
                    updatedErrors.pickupLastname = false;
                } else {
                    updatedErrors.pickupLastname = true;
                }
                // if (validateEmail(pickEmail)) {
                //   updatedErrors.pickupEmail = false;
                // } else {
                //   updatedErrors.pickupEmail = true;
                // }
                if (validatePhone(pickPhone)) {
                    updatedErrors.pickupPhone = false;
                } else {
                    updatedErrors.pickupPhone = true;
                }
                if (pickLat && pickLng) {
                    updatedErrors.pickupAddress = false;
                } else {
                    updatedErrors.pickupAddress = true;
                }
            } else {
                updatedErrors.pickupFirstname = false;
                updatedErrors.pickupLastname = false;
                // updatedErrors.pickupEmail = false;
                updatedErrors.pickupPhone = false;
                updatedErrors.pickupAddress = false;
            }
            if (!isDropoffStore) {
                if (dropFirstname) {
                    updatedErrors.dropoffFirstname = false;
                } else {
                    updatedErrors.dropoffFirstname = true;
                }
                if (dropLastname) {
                    updatedErrors.dropoffLastname = false;
                } else {
                    updatedErrors.dropoffLastname = true;
                }
                // if (validateEmail(dropEmail)) {
                //   updatedErrors.dropoffEmail = false;
                // } else {
                //   updatedErrors.dropoffEmail = true;
                // }
                if (validatePhone(dropPhone)) {
                    updatedErrors.dropoffPhone = false;
                } else {
                    updatedErrors.dropoffPhone = true;
                }
                if (dropLat && dropLng) {
                    updatedErrors.dropoffAddress = false;
                } else {
                    updatedErrors.dropoffAddress = true;
                }
            } else {
                updatedErrors.dropoffFirstname = false;
                updatedErrors.dropoffLastname = false;
                // updatedErrors.dropoffEmail = false;
                updatedErrors.dropoffPhone = false;
                updatedErrors.dropoffAddress = false;
            }
        }

        // if (description === null || description === '') {
        //   updatedErrors.description = true;
        // } else {
        //   updatedErrors.description = false;
        // }
        if (weight === null || weight === '') {
            updatedErrors.weight = true;
        } else {
            updatedErrors.weight = false;
        }
        if (numPeople === null || numPeople === '') {
            updatedErrors.gophrs = true;
        } else {
            updatedErrors.gophrs = false;
        }
        if (vehicleType === null || vehicleType === '') {
            updatedErrors.size = true;
        } else {
            updatedErrors.size = false;
        }
        if (price || priceRangeLow) {
            updatedErrors.price = false;
        } else {
            updatedErrors.price = true;
        }

        if (allowMultipleWaypoints) {
            setErrors(updatedErrors);
            setInfinitePickAndDropWaypointsErrors(updateInfiniteDropsErrors);
            let regularCheck = Object.values(errors).includes(true);

            // Only check waypoint errors if infinite waypoints is selected
            let infiniteDropsCheck = false;
            if (isInfiniteWaypointsSelected) {
                infinitePickAndDropWaypointsErrors.forEach(er => {
                    if (Object.values(er).includes(true)) {
                        infiniteDropsCheck = true;
                    }
                });
            }

            // Check items based on whether infinite waypoints is selected
            let itemsCheck = false;
            if (isInfiniteWaypointsSelected) {
                let pickupItemsCheck = checkItemsError(pickupItems, pickupItemsError, setPickupItemsError);
                let dropoffItemsCheck = checkItemsError(dropoffItems, dropoffItemsError, setDropoffItemsError);
                let pickAndDropMatchError = checkInfiniteDropMatchError();
                itemsCheck = pickupItemsCheck || dropoffItemsCheck || pickAndDropMatchError;
            } else {
                // For regular 2 stops, use defaultItems
                itemsCheck = checkItemsError(defaultItems, defaultItemsError, setDefaultItemsError);
            }

            if (!(regularCheck || infiniteDropsCheck || itemsCheck)) {
                // ! open confirm form
                props.handleForm(false, true);
                setShowForm3(true);
                setInfiniteStopsError(false);
            } else {
                setInfiniteStopsError(true);
            }
        } else {
            setErrors(updatedErrors);
            let regularCheck = Object.values(errors).includes(true);
            let defaultItemsCheck = checkItemsError(defaultItems, defaultItemsError, setDefaultItemsError);
            if (!regularCheck && !defaultItemsCheck) {
                setTwoStopsError(false);
                // setShowForm1(false);
                // ! open confirm form
                props.handleForm(false, true);
                setShowForm3(true);
            } else {
                setTwoStopsError(true);
            }
        }
    };

    const handleBack = () => {
        setShowForm3(false);
        setShowInlinePaymentRetry(false);
        setInlinePaymentRetryMessage('');
        // setShowForm1(true);
        props.handleForm(true, true);
    };

    const blockOrderSubmission = errorMessage => {
        props.handleErrorSubmit({
            errorMessage,
            errorCode: 10
        });
        props.notify('tc', null, errorMessage);
        setSubmissionMessage(errorMessage);
    };

    const getInlinePaymentRetryMessage = shipmentData => {
        const billingDetails = shipmentData && shipmentData.billing_details ? shipmentData.billing_details : null;
        const invoicingErrors = shipmentData && shipmentData.invoicing_errors ? shipmentData.invoicing_errors : null;

        if (billingDetails && typeof billingDetails.message === 'string' && billingDetails.message.trim().length > 0) {
            return billingDetails.message.trim();
        }

        if (invoicingErrors && typeof invoicingErrors.message === 'string' && invoicingErrors.message.trim().length > 0) {
            return invoicingErrors.message.trim();
        }

        if (invoicingErrors && typeof invoicingErrors.error === 'string' && invoicingErrors.error.trim().length > 0) {
            return invoicingErrors.error.trim();
        }

        if (billingDetails && typeof billingDetails.code === 'string' && billingDetails.code.trim().length > 0) {
            return 'Payment failed (' + billingDetails.code.trim() + '). Please retry payment.';
        }

        return 'Payment failed for this order. Retry payment to activate it.';
    };

    const currentInvoiceLinks = extractInvoiceLinks(currentShipmentData);

    const hasCompletedBillingForLanding = shipmentData => {
        if (!shipmentData) {
            return false;
        }

        const billingDetails = shipmentData.billing_details && typeof shipmentData.billing_details === 'object'
            ? shipmentData.billing_details
            : null;
        const invoiceStatus = billingDetails && typeof billingDetails.status === 'string'
            ? billingDetails.status.trim().toLowerCase()
            : '';

        return (billingDetails && billingDetails.paid === true) || invoiceStatus === 'paid';
    };

    const getDisplayLandingStatus = shipmentData => {
        const status = shipmentData && shipmentData.status ? shipmentData.status : '';

        if ((status === 'shipment_saved_pending_payment_retry' || status === 'shipment_pending_payment') && hasCompletedBillingForLanding(shipmentData)) {
            return 'shipment_ready_for_driver';
        }

        return status;
    };

    const persistMerchantOrderLanding = shipmentData => {
        const status = getDisplayLandingStatus(shipmentData);
        const pendingStatuses = ['shipment_created', 'shipment_pending', 'shipment_pending_schedule', 'shipment_pending_payment', 'shipment_saved_pending_payment_retry'];
        const inProgressStatuses = ['shipment_ready_for_driver', 'shipment_assigned', 'shipment_claimed'];
        const completedStatuses = ['shipment_completed', 'shipment_enroute'];
        const savedStatuses = ['shipment_saved', 'shipment_pending_saved', 'shipment_scheduled'];

        let selectedTab = 'inProgress';
        if (pendingStatuses.includes(status)) {
            selectedTab = 'pending';
        } else if (savedStatuses.includes(status)) {
            selectedTab = 'saved';
        } else if (completedStatuses.includes(status)) {
            selectedTab = 'completed';
        } else if (inProgressStatuses.includes(status)) {
            selectedTab = 'inProgress';
        }

        localStorage.setItem('merchant_orders_initial_tab', selectedTab);
        const resolvedShipmentId =
            (shipmentData && shipmentData.shipment_id) ||
            (shipmentData && shipmentData.Shipment && shipmentData.Shipment.shipment_id) ||
            (shipmentData && shipmentData.shipment && shipmentData.shipment.shipment_id) ||
            '';

        if (resolvedShipmentId) {
            localStorage.setItem('merchant_orders_last_created_shipment_id', resolvedShipmentId);
            localStorage.setItem('merchant_orders_last_created_at', String(Date.now()));

            const baseCustomerBlob = shipmentData && shipmentData.customer_blob && typeof shipmentData.customer_blob === 'object'
                ? shipmentData.customer_blob
                : {};

            const toDisplayItems = items =>
                Array.isArray(items)
                    ? items
                        .filter(item => item && item.detail)
                        .map(item => `${item.quantity || 1}-${item.detail}`)
                    : [];

            const pickupOrderLocationBlob = buildOrderLocationBlob(pickupInfo, {
                name: `${pickFirstname || ''} ${pickLastname || ''}`.trim() || null,
                firstName: pickFirstname,
                lastName: pickLastname,
                phone: pickPhone,
                fullAddress: pickFullAddress,
                address1: pickAddress1,
                address2: pickAddress2,
                city: pickCity,
                state: pickState,
                zip: pickZip,
                country: pickCountry,
                businessName: pickBusinessName,
                lat: pickLat,
                lng: pickLng,
            });

            const dropoffOrderLocationBlob = buildOrderLocationBlob(dropoffInfo, {
                name: `${dropFirstname || ''} ${dropLastname || ''}`.trim() || null,
                firstName: dropFirstname,
                lastName: dropLastname,
                phone: dropPhone,
                fullAddress: dropFullAddress,
                address1: dropAddress1,
                address2: dropAddress2,
                city: dropCity,
                state: dropState,
                zip: dropZip,
                country: dropCountry,
                businessName: dropoffBusinessName,
                lat: dropLat,
                lng: dropLng,
            });

            const pickupOrder = {
                order_id: `local-${resolvedShipmentId}-pickup`,
                shipment_id: resolvedShipmentId,
                store_id: storeId || null,
                order_friendly_id: 1,
                status: orderStatus || 'awaiting_driver',
                location_blob: pickupOrderLocationBlob,
                actions: {
                    pick_up: toDisplayItems(allowMultipleWaypoints && isInfiniteWaypointsSelected ? pickupItems : defaultItems),
                },
                description: JSON.stringify(allowMultipleWaypoints && isInfiniteWaypointsSelected ? pickupItems : defaultItems),
                pick_up_instructions: mergeCustomerAndBrokerNotes(pickup, brokerPickupNotes) || null,
                drop_off_instructions: null,
                weight: weight || null,
                actual_action: 'pickup',
                photos: [],
                estimated_arrival_time: null,
                completed_at: null,
            };

            const dropoffOrder = {
                order_id: `local-${resolvedShipmentId}-dropoff`,
                shipment_id: resolvedShipmentId,
                store_id: storeId || null,
                order_friendly_id: 2,
                status: orderStatus || 'awaiting_driver',
                location_blob: dropoffOrderLocationBlob,
                actions: {
                    drop_off: toDisplayItems(allowMultipleWaypoints && isInfiniteWaypointsSelected ? dropoffItems : defaultItems),
                },
                description: JSON.stringify(allowMultipleWaypoints && isInfiniteWaypointsSelected ? dropoffItems : defaultItems),
                pick_up_instructions: null,
                drop_off_instructions: mergeCustomerAndBrokerNotes(dropoff, brokerDropoffNotes) || null,
                weight: weight || null,
                actual_action: 'dropoff',
                photos: [],
                estimated_arrival_time: null,
                completed_at: null,
            };

            const localShipmentSnapshot = {
                ...shipmentData,
                shipment_id: resolvedShipmentId,
                status: status || 'shipment_ready_for_driver',
                customer_blob: {
                    ...baseCustomerBlob,
                    store_id: storeId || baseCustomerBlob.store_id || null,
                    store_name: (store && store.name) || baseCustomerBlob.store_name || null,
                },
                Orders: Array.isArray(shipmentData && shipmentData.Orders)
                    ? shipmentData.Orders
                    : [pickupOrder, dropoffOrder],
                __actionHost: shipment_urls.shipmentHost,
            };

            const landingSnapshot = {
                shipment_id: resolvedShipmentId,
                status: status || 'shipment_ready_for_driver',
                created_at: Date.now(),
                store_id: storeId || null,
                market_id: storeMarketId || ((store && store.market_id) || null),
                created_by: SUB || null,
                shipment: localShipmentSnapshot,
            };

            localStorage.setItem('merchant_orders_last_created_snapshot', JSON.stringify(landingSnapshot));
            persistRecentShipmentSnapshot(landingSnapshot);
        }
    };

    const forceScrollTop = () => {
        const scrollTargets = [
            window,
            document.documentElement,
            document.body,
            document.scrollingElement,
            document.querySelector('.main-panel'),
            document.querySelector('.content'),
            document.querySelector('.wrapper')
        ].filter(Boolean);

        scrollTargets.forEach(target => {
            if (target === window) {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }

            if (typeof target.scrollTo === 'function') {
                target.scrollTo({ top: 0, behavior: 'smooth' });
            }
            target.scrollTop = 0;
        });
    };

    const handleConfirm = async (shipment, order, isScheduled) => {
        const normalizedVehicleType = normalizeShipmentVehicleTypeForApi(vehicleType);
        const isQuoteOnlyRequest =
            (((vehicleType === '18_wheeler_dry_van' || vehicleType === '18_wheeler_flat_bed' || vehicleType === '18_wheeler_reefer') && !hasTqlRateValue) ||
                vehicleType === 'car_hauler');
        const isSaveForLaterRequest = shipment === 'shipment_saved';
        const savedDraftUpdateRequest = buildSavedDraftUpdateRequest({
            shipmentId: editingSavedShipmentId,
            updateUrl: shipment_urls.updateSavedShipmentWithRate,
            shipmentStatus: shipment,
        });
        // Editing a live (active) order updates it in place and settles the price difference,
        // rather than saving a draft. Pick the endpoint that matches the submitted stop shape.
        if (savedDraftUpdateRequest && isActiveEdit) {
            const activeUpdateUrlFn =
                allowMultipleWaypoints && isInfiniteWaypointsSelected
                    ? shipment_urls.updateActiveMultidropShipment
                    : shipment_urls.updateActiveShipmentWithRate;
            savedDraftUpdateRequest.method = 'put';
            savedDraftUpdateRequest.url = activeUpdateUrlFn(editingSavedShipmentId);
            savedDraftUpdateRequest.saveAgain = false;
        }
        const cachedStoreInvoiceFrequency = storeId ? getStoredStoreInvoiceFrequency(storeId) : null;
        const normalizedPaymentProfileInvoiceFrequency = normalizeInvoiceFrequency(paymentProfileState.invoiceFrequency);
        const resolvedCheckoutInvoiceFrequency = normalizedPaymentProfileInvoiceFrequency && (paymentProfileState.loaded || normalizedPaymentProfileInvoiceFrequency !== 'single')
            ? normalizedPaymentProfileInvoiceFrequency
            : normalizeInvoiceFrequency(cachedStoreInvoiceFrequency || normalizedPaymentProfileInvoiceFrequency);
        const assignedPaymentTerm = resolvedCheckoutInvoiceFrequency && resolvedCheckoutInvoiceFrequency !== 'n/a'
            ? resolvedCheckoutInvoiceFrequency
            : null;
        const paymentTermPolicy = getCompanyPaymentTermPolicy({
            paymentTerm: assignedPaymentTerm,
            isQuoteOnly: isQuoteOnlyRequest,
            isAdminOnBehalf: isAdminOnBehalfMode,
        });
        const effectiveSelectedPaymentMethodId =
            selectedPaymentMethodId || paymentProfileState.selectedPaymentMethodId || paymentProfileState.defaultPaymentMethodId || null;
        const normalizedPaymentMode = isQuoteOnlyRequest
            ? 'quote_required'
            : paymentTermPolicy.isPrepay
                ? 'card'
                : (selectedPaymentMode === 'card' ? 'card' : 'net_terms');
        const isCompanyTermsSelection = normalizedPaymentMode === 'net_terms';
        const submittedPaymentMode = isQuoteOnlyRequest
            ? 'quote_required'
            : normalizedPaymentMode;
        const checkoutUserAuth0Id = SUB;
        const selectedInvoiceFrequency = isCompanyTermsSelection
            ? paymentTermPolicy.normalizedTerm
            : null;
        const resolvedOnBehalfMerchantAuth0Id = onBehalfMerchantAuth0Id || storeMerchantAuth0Id || null;
        const selectedCardBillingAuth0Id = !isCompanyTermsSelection && effectiveSelectedPaymentMethodId
            ? (paymentProfileState.selectedCardBillingAuth0Id || paymentProfileState.cardBillingAuth0Id || null)
            : null;
        const effectiveSelectedCardBillingAuth0Id = isAdminOnBehalfMode
            ? (resolvedOnBehalfMerchantAuth0Id || selectedCardBillingAuth0Id)
            : selectedCardBillingAuth0Id;
        const fallbackCheckoutMerchantAuth0Id = isCompanyTermsSelection
            ? (paymentProfileState.netTermsBillingAuth0Id || paymentProfileState.billingAuth0Id || storeMerchantAuth0Id || SUB)
            : (effectiveSelectedCardBillingAuth0Id || SUB);
        const checkoutMerchantAuth0Id = isAdminOnBehalfMode
            ? (resolvedOnBehalfMerchantAuth0Id || fallbackCheckoutMerchantAuth0Id)
            : fallbackCheckoutMerchantAuth0Id;
        const resolvedStoreMarketId = (store && store.market_id) || storeMarketId;

        if (!storeId || !resolvedStoreMarketId) {
            blockOrderSubmission('Store context is incomplete. Please refresh and try again.');
            return;
        }

        if (!normalizedVehicleType) {
            blockOrderSubmission('Vehicle type is missing. Please reselect a vehicle type and try again.');
            return;
        }

        if (!isActiveEdit && !isQuoteOnlyRequest && !isSaveForLaterRequest) {
            if (!paymentProfileState.loaded) {
                blockOrderSubmission('Payment options are still loading. Please wait a moment and try again.');
                return;
            }

            if (paymentProfileState.fetchFailed) {
                blockOrderSubmission('Unable to load payment options. Please refresh the page and try again.');
                return;
            }

            if (paymentTermPolicy.requiresSavedCardOnFile && !effectiveSelectedPaymentMethodId && !paymentTermPolicy.allowsPendingWithoutCard) {
                blockOrderSubmission('Select a saved card or add a card before submitting this order.');
                return;
            }

            if (normalizedPaymentMode === 'card' && !effectiveSelectedPaymentMethodId && !paymentTermPolicy.allowsPendingWithoutCard) {
                blockOrderSubmission('Select a saved card or add a card before submitting this order.');
                return;
            }

            if (normalizedPaymentMode === 'card' && !selectedCardBillingAuth0Id) {
                blockOrderSubmission('The selected card is still syncing with the checkout profile. Wait a moment, reselect the card, and try again.');
                return;
            }

            if (isCompanyTermsSelection && !paymentTermPolicy.allowsCompanyTermsSelection) {
                blockOrderSubmission('Company payment terms could not be verified for this store. Refresh the page and confirm the store payment terms before submitting.');
                return;
            }
        }

        // Keep submit feedback visible without requiring manual scrolling.
        forceScrollTop();
        localStorage.setItem('merchant_orders_force_scroll_top', '1');
        window.setTimeout(forceScrollTop, 120);

        lockOrderSubmit();
        setShowInlinePaymentRetry(false);
        setInlinePaymentRetryMessage('');
        setSubmissionMessage('Your order is submitting, please wait...');
        let url;
        let scheduledForData = null;
        let numOfDrops = 0;
        if (dropoff === null || dropoff === '') {
            setDropoff('N/A');
        }
        if (scheduledFor) {
            scheduledForData = scheduledFor.toISOString();
        }
        if (savedDraftUpdateRequest) {
            url = savedDraftUpdateRequest.url;
        } else if (isScheduled) {
            const shouldBypassScheduledWithRateForCompanyTerms = false;
            if (allowMultipleWaypoints && isInfiniteWaypointsSelected) {
                url = shouldBypassScheduledWithRateForCompanyTerms
                    ? shipment_urls.createMultidropScheduledShipment
                    : shipment_urls.createMultidropScheduledShipmentWithRate;
            } else {
                url = shouldBypassScheduledWithRateForCompanyTerms
                    ? shipment_urls.createScheduledShipment
                    : shipment_urls.createScheduledShipmentWithRate;
            }
        } else {
            // todo : New url need to be added
            if (shipment === 'shipment_saved') {
                if (allowMultipleWaypoints && isInfiniteWaypointsSelected) {
                    url = shipment_urls.createMultidropSavedShipment;
                } else {
                    url = shipment_urls.createSavedShipmentWithRate;
                }
            } else {
                if (allowMultipleWaypoints && isInfiniteWaypointsSelected) {
                    url = shipment_urls.createMultidropShipmentWithRate;
                } else {
                    if (isLTL) {
                        url = shipment_urls.createLTLShipment;
                    } else {
                        url = shipment_urls.createShipmentWithRate;
                    }
                }
            }
        }
        const pickOrderLocationBlob = buildOrderLocationBlob(pickupInfo, {
            name: `${pickFirstname || ''} ${pickLastname || ''}`.trim() || null,
            firstName: pickFirstname,
            lastName: pickLastname,
            phone: pickPhone,
            fullAddress: pickFullAddress,
            address1: pickAddress1,
            address2: pickAddress2,
            city: pickCity,
            state: pickState,
            zip: pickZip,
            country: pickCountry,
            businessName: pickBusinessName,
            lat: pickLat,
            lng: pickLng,
        });
        const dropOrderLocationBlob = buildOrderLocationBlob(dropoffInfo, {
            name: `${dropFirstname || ''} ${dropLastname || ''}`.trim() || null,
            firstName: dropFirstname,
            lastName: dropLastname,
            phone: dropPhone,
            fullAddress: dropFullAddress,
            address1: dropAddress1,
            address2: dropAddress2,
            city: dropCity,
            state: dropState,
            zip: dropZip,
            country: dropCountry,
            businessName: dropoffBusinessName,
            lat: dropLat,
            lng: dropLng,
        });

        let pickOrder = {
            store_id: storeId,
            // Pickup location
            location_blob: pickOrderLocationBlob,
            weight: weight,
            // description: description,
            description: JSON.stringify(allowMultipleWaypoints && isInfiniteWaypointsSelected ? pickupItems : defaultItems),
            // line_items: hasThirdWaypoint ? pickupItems : defaultItems,
            pick_up_instructions: mergeCustomerAndBrokerNotes(pickup, brokerPickupNotes),
            actions: {
                pick_up: allowMultipleWaypoints && isInfiniteWaypointsSelected ? pickupItems : defaultItems
            }
        };
        let dropOrder = {
            store_id: storeId,
            // Dropoff location
            location_blob: dropOrderLocationBlob,
            weight: weight,
            // description: description,
            description: JSON.stringify(allowMultipleWaypoints && isInfiniteWaypointsSelected ? dropoffItems : defaultItems),
            // line_items: hasThirdWaypoint ? dropoffItems : defaultItems,
            drop_off_instructions: mergeCustomerAndBrokerNotes(dropoff, brokerDropoffNotes),
            actions: {
                drop_off: allowMultipleWaypoints && isInfiniteWaypointsSelected ? dropoffItems : defaultItems
            }
        };

        if (isActiveEdit) {
            // Tie the pickup/dropoff stops to their existing Order rows so the backend updates them
            // (and creates any added waypoint stops) instead of replacing the whole order.
            if (activeEditOrderIdsRef.current.pickupOrderId) pickOrder.order_id = activeEditOrderIdsRef.current.pickupOrderId;
            if (activeEditOrderIdsRef.current.dropoffOrderId) dropOrder.order_id = activeEditOrderIdsRef.current.dropoffOrderId;
        }

        let infinitePickAndDropOrders = [];
        infinitePickAndDropsInfo.forEach((waypoint, index) => {
            // Preserve the stop's existing Order id on active edits so the backend updates it in place.
            const waypointOrderIdPart =
                isActiveEdit && pickAndDropWaypointsArray[index] && pickAndDropWaypointsArray[index].waypointOrderId
                    ? { order_id: pickAndDropWaypointsArray[index].waypointOrderId }
                    : {};
            // Create pickup order if waypoint has pickup
            if (pickAndDropWaypointsArray[index].isWaypointPickup) {
                infinitePickAndDropOrders.push({
                    ...waypointOrderIdPart,
                    store_id: storeId,
                    location_blob: {
                        ...waypoint
                    },
                    weight: weight,
                    description: JSON.stringify(pickAndDropWaypointsArray[index].pickItems),
                    pick_up_instructions: pickAndDropWaypointsArray[index].waypointPickupInstruction,
                    actions: {
                        pick_up: pickAndDropWaypointsArray[index].pickItems
                    }
                });
            }
            // Create dropoff order if waypoint has dropoff (independent of pickup)
            if (pickAndDropWaypointsArray[index].isWaypointDropoff) {
                infinitePickAndDropOrders.push({
                    ...waypointOrderIdPart,
                    store_id: storeId,
                    location_blob: {
                        ...waypoint
                    },
                    weight: weight,
                    description: JSON.stringify(pickAndDropWaypointsArray[index].dropItems),
                    drop_off_instructions: pickAndDropWaypointsArray[index].waypointDropoffInstruction,
                    actions: {
                        drop_off: pickAndDropWaypointsArray[index].dropItems
                    }
                });
            }
        });

        let regularOrdersArray = [pickOrder, dropOrder];
        let infiniteDropsArray = [pickOrder, ...infinitePickAndDropOrders, dropOrder];
        let ordersData;

        let itemsCountPerStop = [];

        if (allowMultipleWaypoints && isInfiniteWaypointsSelected) {
            ordersData = infiniteDropsArray;
            //TODO Need to update
            pickAndDropWaypointsArray.forEach(ar => {
                let count = 0;
                ar.dropItems.forEach(it => (count += it.quantity));
                itemsCountPerStop.push(count);
            });
            numOfDrops = pickAndDropWaypointsArray.length;
        } else {
            ordersData = regularOrdersArray;
            numOfDrops = 1;
        }
        let itemNum = 0;
        if (allowMultipleWaypoints && isInfiniteWaypointsSelected) {
            // Count main pickup items
            pickupItems.forEach(it => {
                itemNum += it.quantity;
            });
            // Count waypoint pickup items
            pickAndDropWaypointsArray.forEach(waypoint => {
                if (waypoint.isWaypointPickup && waypoint.pickItems) {
                    waypoint.pickItems.forEach(it => {
                        itemNum += it.quantity;
                    });
                }
            });
        } else {
            defaultItems.forEach(it => {
                itemNum += it.quantity;
            });
        }

        const scheduledQuotePayload = isSuperExpedited
            ? superExpeditedQuotePayload
            : isExpedited
            ? expeditedQuotePayload
            : standardQuotePayload;
        const selectedCheckoutQuotePayload = isShipTomorrow ? oneDayThreeQuotePayload : isScheduled ? scheduledQuotePayload : null;

        // Range-quote vehicles (18-wheeler/car_hauler) are rate-required and never get a
        // server checkout quote_id, so they must not be gated by the quote_id requirement.
        const selectedVehicleRequiresRangeQuote = requiresRangeQuoteForVehicleType(vehicleType);

        // Multi-drop orders never capture a locked checkout quote_id (the multi-waypoint quote
        // branch only sets display prices, and the scheduled-payload reset can also clear it), and
        // they're priced server-side on create — so any multi-drop-enabled order must not be gated
        // by the quote_id requirement. Either waypoint flag qualifies.
        const isMultiDropOrder = allowMultipleWaypoints || isInfiniteWaypointsSelected;

        if (!selectedVehicleRequiresRangeQuote && !isMultiDropOrder && (isShipTomorrow || (isScheduled && !isLTL)) && (!selectedCheckoutQuotePayload || !selectedCheckoutQuotePayload.quote_id)) {
            const quoteError = {
                errorMessage: 'Your selected checkout quote needs to be refreshed before this order can be submitted.',
                errorCode: 409
            };

            props.handleErrorSubmit(quoteError);
            props.notify('tc', null, quoteError.errorMessage);
            releaseOrderSubmitWhenLockExpires();
            setSubmissionMessage(quoteError.errorMessage);
            return;
        }

        let refreshedQuote = null;
        const shouldRefreshQuoteBeforeSubmit = !isActiveEdit && !isLTL && !isSaveForLaterRequest && !isShipTomorrow && !isScheduled;

        if (shouldRefreshQuoteBeforeSubmit) {
            try {
                refreshedQuote = await refreshQuoteBeforeSubmit({
                    resolvedStoreMarketId,
                    normalizedVehicleType,
                    resolvedRoute: route,
                    resolvedOrders: ordersData,
                    numOfDrops,
                    itemsCountPerStop,
                    itemNum,
                    scheduledForData,
                    resolvedDropOffState: ordersData[ordersData.length - 1].location_blob.state,
                    submitUrl: url,
                });
            } catch (quoteRefreshError) {
                const quoteRefreshDisplayError = buildQuoteError(quoteRefreshError, 10);

                props.handleErrorSubmit(quoteRefreshDisplayError);
                props.notify('tc', null, quoteRefreshDisplayError.errorMessage);
                releaseOrderSubmitWhenLockExpires();
                setSubmissionMessage(quoteRefreshDisplayError.errorMessage || 'Unable to refresh the quote. Please try again.');
                return;
            }
        }

        const submittedQuote = refreshedQuote && refreshedQuote.amount != null ? refreshedQuote.amount : price;
        const submittedQuotePayload = refreshedQuote && refreshedQuote.submittedQuotePayload ? refreshedQuote.submittedQuotePayload : buildSubmittedQuotePayload();
        const shouldIncludeSubmittedQuotePayload = !(typeof url === 'string' && url.includes('/multidrop/create_with_rate'));
        // On an active edit the mapbox route isn't recomputed, so `route` can be the empty
        // default ({}). Resolve a real route (and therefore a numeric quoted_time, which the
        // backend requires) from the current route, then the stored shipment's billed_route /
        // shipping_fee_blob, then a distance/quoted_time-derived snapshot.
        const storedShipment = savedDraftEdit && savedDraftEdit.shipment;
        const calculatedRoute = storedShipment && storedShipment.shipping_fee_blob && storedShipment.shipping_fee_blob.calculated;
        const candidateBilledRoute =
            (route && Number.isFinite(Number(route.duration)) && route) ||
            (storedShipment && storedShipment.billed_route && typeof storedShipment.billed_route === 'object' && storedShipment.billed_route) ||
            (calculatedRoute && (calculatedRoute.billed_route || calculatedRoute.route)) ||
            null;
        let resolvedRouteDurationSeconds = Number(candidateBilledRoute && candidateBilledRoute.duration);
        if (!Number.isFinite(resolvedRouteDurationSeconds)) {
            const quotedHours = Number(storedShipment && storedShipment.quoted_time);
            const distanceMiles = Number(distance);
            resolvedRouteDurationSeconds = Number.isFinite(quotedHours)
                ? quotedHours * 60 * 60
                : (Number.isFinite(distanceMiles) ? (distanceMiles / 45) * 60 * 60 : 0);
        }
        const resolvedQuotedTime = resolvedRouteDurationSeconds / 60 / 60;
        const resolvedBilledRoute = candidateBilledRoute && typeof candidateBilledRoute === 'object'
            ? candidateBilledRoute
            : { distance: Number.isFinite(Number(distance)) ? Number(distance) * 1609.344 : 0, duration: resolvedRouteDurationSeconds };
        let data = {
            status: shipment,
            market_id: resolvedStoreMarketId,
            scheduled_for: scheduledForData,
            weight: Number(weight),
            vehicle_type: normalizedVehicleType,
            is_expedited: isExpedited,
            is_super_expedited: isSuperExpedited,
            is_ship_tomorrow: isShipTomorrow,
            num_of_drops: numOfDrops,
            is_roundtrip: false,
            contains_alcohol: containsAlcohol,
            distance: distance,
            quote: submittedQuote,
            // Multi-drop create_with_rate prices server-side and only accepts a 'multidrop_direct'
            // quote_id; the captured single-drop ('direct') quote_id would 400 as QuoteNotFound, so
            // omit it for multi-drop and let the backend compute the fee fresh.
            ...(shouldIncludeSubmittedQuotePayload && selectedCheckoutQuotePayload ? { quote_id: selectedCheckoutQuotePayload.quote_id } : {}),
            quoted_time: resolvedQuotedTime,
            items_count_per_stop_array: itemsCountPerStop,
            item_count: itemNum,
            num_of_gophrs: numPeople,
            is_fork_lift: isForkLift,
            is_dolly: isDolly,
            is_hazmat: isHazmat,
            is_pallet_jack: isPalletJack,
            is_twic_card: isTWICCard,
            is_liftgate: isLiftgate,
            is_hand_loading: isHandLoadUnload,
            is_weather_protection: isWeatherProtection,
            is_medical: isMedical,
            is_personal_protective_equipment: isPPE,
            is_ltl: isLTL,
            is_fedex: isFedex,
            ...(isFedex && { fedex_selection: fedexSelection }),
            ai_recommendation: AIExplanation,
            recommend_weight: recommendedWeight,
            recommend_vehicle: recommendedVehicleType,
            customer_blob: {
                first_name: storeFirstName,
                last_name: storeLastName,
                store_name: storeName,
                phone: storePhone.trim(),
                address: storeAddress,
                address_1: storeAddress1,
                address_2: null,
                city: storeCity,
                state: storeState,
                zip: storeZip,
                country: storeCountry,
                lat: parseFloat(storeLat),
                lng: parseFloat(storeLng)
            },
            address_1: storeAddress1,
            address_2: null,
            city: storeCity,
            zip: storeZip,
            state: storeState,
            country: storeCountry,
            lat: parseFloat(storeLat),
            lng: parseFloat(storeLng),
            pick_up_instructions: mergeCustomerAndBrokerNotes(pickup, brokerPickupNotes),
            drop_off_instructions: mergeCustomerAndBrokerNotes(dropoff, brokerDropoffNotes),
            // description: description,
            description: allowMultipleWaypoints && isInfiniteWaypointsSelected ? 'A Multiple Drops Shipment' : JSON.stringify(defaultItems),
            merchant_order_id: merchantOrderID === '' || merchantOrderID === null ? null : merchantOrderID,
            billed_route: resolvedBilledRoute,
            ...(preferredDriver && preferredDriver.driver_auth0_id ? { preferred_driver: preferredDriver } : {}),
            ...(shouldIncludeSubmittedQuotePayload && submittedQuotePayload ? { submitted_quote_payload: submittedQuotePayload } : {}),
            actions: {},
            orders: ordersData,
            placed_by: isAdminOnBehalfMode ? checkoutUserAuth0Id : merchantName,
            created_by: isAdminOnBehalfMode ? (resolvedOnBehalfMerchantAuth0Id || checkoutUserAuth0Id) : checkoutUserAuth0Id,
            cust_auth0_id: isAdminOnBehalfMode ? (resolvedOnBehalfMerchantAuth0Id || checkoutUserAuth0Id) : checkoutUserAuth0Id,
            is_onBehalf: isAdminOnBehalfMode,
            ...(isAdminOnBehalfMode && resolvedOnBehalfMerchantAuth0Id ? { onbehalf_of: resolvedOnBehalfMerchantAuth0Id } : {}),
            has_tql_rate_value: isLTL ? false : hasTqlRateValue,
            tql_rate_blob: hasTqlRateValue ? tqlRateBlob : {},
            is_over_size: isOverSize,
            drop_off_state: ordersData[ordersData.length - 1].location_blob.state,
            is_market_base_quote: false,
            payment_method_id: isCompanyTermsSelection ? null : effectiveSelectedPaymentMethodId,
            selected_payment_mode: submittedPaymentMode,
            selected_invoice_frequency: selectedInvoiceFrequency,
            bill_to_auth0_id: checkoutMerchantAuth0Id,
            bill_to_card_auth0_id: effectiveSelectedCardBillingAuth0Id,
            bill_to_store_merchant_auth0_id: resolvedOnBehalfMerchantAuth0Id || null,
            bill_to_store_id: storeId || null,
            company_payment_terms: paymentTermPolicy.normalizedTerm,
            payment_collection_type: paymentTermPolicy.collectionType,
        };

        if (savedDraftUpdateRequest) {
            data.save_again = savedDraftUpdateRequest.saveAgain;
        }

        const submitUrl = url;
        const hostedSubmitUrl = shipment_urls.toHostedShipmentUrl(url);

        const submitRequestStartedAt = Date.now();

        const buildLegacySubmitData = submissionData => {
            if (!submissionData || typeof submissionData !== 'object') {
                return submissionData;
            }

            // Legacy create handlers can fail on newer billing/payment fields.
            const {
                selected_payment_mode,
                selected_invoice_frequency,
                bill_to_auth0_id,
                bill_to_card_auth0_id,
                bill_to_store_merchant_auth0_id,
                bill_to_store_id,
                company_payment_terms,
                payment_collection_type,
                submitted_quote_payload,
                ...legacyCompatibleData
            } = submissionData;

            void selected_payment_mode;
            void selected_invoice_frequency;
            void bill_to_auth0_id;
            void bill_to_card_auth0_id;
            void bill_to_store_merchant_auth0_id;
            void bill_to_store_id;
            void company_payment_terms;
            void payment_collection_type;
            void submitted_quote_payload;

            return legacyCompatibleData;
        };

        const buildPaymentAgnosticSubmitData = submissionData => {
            if (!submissionData || typeof submissionData !== 'object') {
                return submissionData;
            }

            const {
                payment_method_id,
                selected_payment_mode,
                selected_invoice_frequency,
                bill_to_auth0_id,
                bill_to_card_auth0_id,
                bill_to_store_merchant_auth0_id,
                bill_to_store_id,
                company_payment_terms,
                payment_collection_type,
                submitted_quote_payload,
                ...paymentAgnosticData
            } = submissionData;

            void payment_method_id;
            void selected_payment_mode;
            void selected_invoice_frequency;
            void bill_to_auth0_id;
            void bill_to_card_auth0_id;
            void bill_to_store_merchant_auth0_id;
            void bill_to_store_id;
            void company_payment_terms;
            void payment_collection_type;
            void submitted_quote_payload;

            return paymentAgnosticData;
        };

        const isWrappedAxios401 = submitError => {
            const status = submitError && submitError.response ? submitError.response.status : null;
            const message = submitError && submitError.response && submitError.response.data && submitError.response.data.message
                ? String(submitError.response.data.message)
                : '';

            return status === 500 && /status code 401/i.test(message);
        };

        const isTransientNetworkSubmitError = submitError => {
            const hasHttpResponse = Boolean(submitError && submitError.response);
            const code = submitError && submitError.code ? String(submitError.code) : '';
            const message = submitError && submitError.message ? String(submitError.message) : '';

            if (hasHttpResponse) {
                return false;
            }

            return code === 'ERR_NETWORK' || /network error/i.test(message);
        };

        const isQuoteDiffSubmitError = submitError => {
            const responseData = submitError && submitError.response ? submitError.response.data : null;
            const candidates = [
                responseData && responseData.message,
                responseData && responseData.error,
                responseData && responseData.code,
                submitError && submitError.message,
            ];

            return candidates.some(candidate => typeof candidate === 'string' && /QuoteDiffError/i.test(candidate));
        };

        const toSaveSubmissionUrl = requestUrl => {
            if (typeof requestUrl !== 'string') {
                return null;
            }

            if (requestUrl.includes('/schedule') || requestUrl.includes('/save')) {
                return null;
            }

            if (requestUrl.includes('/create_with_rate')) {
                return requestUrl.replace('/create_with_rate', '/create_with_rate/save');
            }

            if (requestUrl.includes('/create')) {
                return requestUrl.replace('/create', '/create/save');
            }

            return null;
        };

        const toLegacySubmissionUrl = requestUrl => {
            if (typeof requestUrl !== 'string') {
                return null;
            }

            if (requestUrl.includes('/schedule')) {
                return null;
            }

            return requestUrl.includes('/create_with_rate')
                ? requestUrl.replace('/create_with_rate', '/create')
                : null;
        };

        const executeSubmitRequest = (requestUrl, requestData = data) => runWithSessionRecovery(() => axios({
            method: savedDraftUpdateRequest ? savedDraftUpdateRequest.method : 'post',
            url: requestUrl,
            withCredentials: true,
            timeout: 30000,
            headers: {
                'content-type': 'application/json',
                Authorization: buildAuthorizationHeader(),
            },
            data: requestData
        }));

        const runSubmitChain = async baseSubmitUrl => {
            const legacySubmitUrl = toLegacySubmissionUrl(baseSubmitUrl);
            const retrySubmitUrl = legacySubmitUrl || baseSubmitUrl;
            const saveSubmitUrl = toSaveSubmissionUrl(retrySubmitUrl);
            const isLocalBaseUrl = typeof baseSubmitUrl === 'string' && /localhost|127\.0\.0\.1/.test(baseSubmitUrl);
            const isScheduledSubmitUrl = typeof baseSubmitUrl === 'string' && baseSubmitUrl.includes('/schedule');

            try {
                return await executeSubmitRequest(baseSubmitUrl);
            } catch (primarySubmitError) {
                if (isLocalBaseUrl && isTransientNetworkSubmitError(primarySubmitError)) {
                    try {
                        return await executeSubmitRequest(baseSubmitUrl);
                    } catch (retryNetworkSubmitError) {
                        if (retrySubmitUrl && retrySubmitUrl !== baseSubmitUrl) {
                            return executeSubmitRequest(retrySubmitUrl);
                        }

                        throw retryNetworkSubmitError;
                    }
                }

                const primaryStatus = primarySubmitError && primarySubmitError.response ? primarySubmitError.response.status : null;
                const legacySubmitData = buildLegacySubmitData(data);
                const paymentAgnosticSubmitData = buildPaymentAgnosticSubmitData(data);
                const canFallbackFromQuoteDiff = primaryStatus === 400 && isQuoteDiffSubmitError(primarySubmitError) && retrySubmitUrl !== baseSubmitUrl;

                // Some schedule endpoints return auth wrappers for billing fields; retry once with payment-agnostic payload.
                if (isScheduledSubmitUrl && (primaryStatus === 401 || primaryStatus === 403) && paymentAgnosticSubmitData !== data) {
                    try {
                        return await executeSubmitRequest(baseSubmitUrl, paymentAgnosticSubmitData);
                    } catch (_) {
                        // Fall through to the existing fallback flow.
                    }
                }

                // If the server responded at all (any status code), do not retry to any create
                // endpoint — the order was likely already created and retrying causes duplicates.
                if (primaryStatus !== null && !(isScheduledSubmitUrl && (primaryStatus === 401 || primaryStatus === 403)) && !canFallbackFromQuoteDiff) {
                    throw primarySubmitError;
                }

                try {
                    return await executeSubmitRequest(retrySubmitUrl);
                } catch (legacyUrlError) {
                    const legacyUrlStatus = legacyUrlError && legacyUrlError.response ? legacyUrlError.response.status : null;

                    if (legacyUrlStatus !== null && legacyUrlStatus < 500) {
                        throw legacyUrlError;
                    }

                    if (legacySubmitData !== data) {
                        try {
                            return await executeSubmitRequest(retrySubmitUrl, legacySubmitData);
                        } catch (legacyDataError) {
                            if (!isWrappedAxios401(legacyDataError) || paymentAgnosticSubmitData === data) {
                                throw legacyDataError;
                            }

                            try {
                                return await executeSubmitRequest(retrySubmitUrl, paymentAgnosticSubmitData);
                            } catch (paymentAgnosticError) {
                                if (!saveSubmitUrl || !isWrappedAxios401(paymentAgnosticError)) {
                                    throw paymentAgnosticError;
                                }

                                return executeSubmitRequest(saveSubmitUrl, paymentAgnosticSubmitData);
                            }
                        }
                    }

                    if (!isWrappedAxios401(legacyUrlError) || paymentAgnosticSubmitData === data) {
                        throw legacyUrlError;
                    }

                    try {
                        return await executeSubmitRequest(retrySubmitUrl, paymentAgnosticSubmitData);
                    } catch (paymentAgnosticError) {
                        if (!saveSubmitUrl || !isWrappedAxios401(paymentAgnosticError)) {
                            throw paymentAgnosticError;
                        }

                        return executeSubmitRequest(saveSubmitUrl, paymentAgnosticSubmitData);
                    }
                }
            }
        };

        const submitOrderRequest = async () => {
            const isLocalDebugMode = process.env.REACT_APP_ENV_MODE === 'local-debug'
                || (clientEnv && clientEnv.mode === 'local-debug')
                || (typeof submitUrl === 'string' && /localhost|127\.0\.0\.1/.test(submitUrl));
            const shouldTryHostedFallback = typeof hostedSubmitUrl === 'string' && hostedSubmitUrl !== submitUrl;

            try {
                return await runSubmitChain(submitUrl);
            } catch (localSubmitError) {
                const localStatus = localSubmitError && localSubmitError.response ? localSubmitError.response.status : null;
                const isLocalTransportError = isTransientNetworkSubmitError(localSubmitError);

                // Any server response (including 5xx) means the request was processed; don't
                // retry on the hosted URL or a duplicate order will be created.
                if ((!isLocalTransportError && isLocalDebugMode) || !shouldTryHostedFallback || (localStatus !== null && localStatus !== 401 && localStatus !== 403)) {
                    throw localSubmitError;
                }

                try {
                    return await runSubmitChain(hostedSubmitUrl);
                } catch (hostedSubmitError) {
                    throw hostedSubmitError;
                }
            }
        };

        submitOrderRequest()
            .then(async response => {
                const responseStatus = response && response.data ? response.data.status : null;
                const payload = response && response.data ? response.data.payload : null;
                const shipmentId = (payload && payload.shipment_id) || (payload && payload.Shipment && payload.Shipment.shipment_id) || null;
                const shipmentFriendlyId =
                    (payload && payload.shipment_friendly_id) ||
                    (payload && payload.Shipment && payload.Shipment.shipment_friendly_id) ||
                    null;

                const payloadShipment = payload && payload.Shipment ? payload.Shipment : null;
                const payloadBillingDetails =
                    (payload && payload.billing_details) ||
                    (payloadShipment && payloadShipment.billing_details) ||
                    null;
                const payloadInvoiceId =
                    (payload && payload.invoice_id) ||
                    (payloadShipment && payloadShipment.invoice_id) ||
                    null;
                const payloadInvoicingErrors =
                    (payload && payload.invoicing_errors) ||
                    (payloadShipment && payloadShipment.invoicing_errors) ||
                    null;

                const shipmentDataBase = payloadShipment || payload || null;
                const shipmentData = shipmentDataBase
                    ? {
                        ...shipmentDataBase,
                        billing_details: shipmentDataBase.billing_details || payloadBillingDetails || payloadInvoicingErrors || null,
                        invoice_id: shipmentDataBase.invoice_id || payloadInvoiceId || null,
                        invoicing_errors: shipmentDataBase.invoicing_errors || payloadInvoicingErrors || null,
                    }
                    : null;
                const hasSuccessStatus = responseStatus === 'successful' || responseStatus === 'success' || responseStatus === 'ok' || responseStatus == null;
                const isSuccessfulResponse = (response.status === 200 || response.status === 201) && hasSuccessStatus && Boolean(shipmentId);

                if (isSuccessfulResponse) {
                    if (savedDraftUpdateRequest) {
                        clearSavedDraftEditState();
                        if (typeof props.onSavedDraftEditClose === 'function') {
                            props.onSavedDraftEditClose();
                        }
                    }

                    // Wait for file upload before payment check
                    if (selectedFile) {
                        setSubmissionMessage('Submission successful! Uploading file...');
                        await uploadFile(shipmentId, shipmentFriendlyId);
                    }

                    // Check if payment failed - backend sets status to shipment_saved_pending_payment_retry
                    const requiresPayment = shipmentData.status === 'shipment_saved_pending_payment_retry';

                    // Handle payment required - show payment modal
                    if (requiresPayment) {
                        if (shipmentId) {
                            try {
                                const paymentStatusResponse = await checkPaymentStatus(shipmentId);
                                const paymentConfirmed =
                                    paymentStatusResponse &&
                                    paymentStatusResponse.status === 'successful' &&
                                    paymentStatusResponse.payload &&
                                    paymentStatusResponse.payload.paid === true;

                                if (paymentConfirmed) {
                                    if (isFedex && shipmentData && shipmentData.fedex_data) {
                                        setCurrentShipmentId(shipmentId);
                                        setShowFedexLabelModal(true);
                                        setSubmissionMessage('Payment successful! Please review your FedEx label.');
                                    } else {
                                        persistMerchantOrderLanding({
                                            ...shipmentData,
                                            status: 'shipment_ready_for_driver',
                                        });
                                        setSubmissionMessage('Payment successful! Redirecting...');
                                        window.location.reload();
                                    }

                                    return;
                                }
                            } catch (paymentCheckError) {
                                void paymentCheckError;
                            }
                        }

                        persistMerchantOrderLanding(shipmentData);
                        setCurrentShipmentId(shipmentId);
                        setCurrentShipmentFriendlyId(shipmentFriendlyId);
                        setCurrentShipmentData(shipmentData);

                        // Store FedEx flag for after payment succeeds
                        if (isFedex && shipmentData && shipmentData.fedex_data) {
                            setHasPendingFedexLabel(true);
                        }

                        setShowInlinePaymentRetry(true);
                        if (extractInvoiceLinks(shipmentData) && extractInvoiceLinks(shipmentData).hostedUrl) {
                            setPaymentRetryModalOpen(true);
                        }
                        setInlinePaymentRetryMessage(getInlinePaymentRetryMessage(shipmentData));
                        setSubmissionMessage('Order created - payment required.');
                        releaseOrderSubmitWhenLockExpires();
                        return;
                    }

                    // Normal flow - payment succeeded or not required
                    if (isFedex && shipmentData && shipmentData.fedex_data) {
                        setCurrentShipmentId(shipmentId);
                        setShowFedexLabelModal(true);
                        setSubmissionMessage('Submission successful! Please review your FedEx label.');
                    } else {
                        persistMerchantOrderLanding(shipmentData);
                        setSubmissionMessage('Submission successful! Redirecting...');
                        window.location.reload();
                    }
                } else {
                    setSubmissionMessage('Order submission did not return a shipment ID. Please try again.');
                    releaseOrderSubmitWhenLockExpires();
                    // setIsOrderSubmitDisabled(false);
                }
            })
            .catch(error => {
                const submitError = buildQuoteError(error, 10);
                props.handleErrorSubmit(submitError);
                props.notify('tc', null, submitError.errorMessage);
                releaseOrderSubmitWhenLockExpires();
                setSubmissionMessage(submitError.errorMessage || 'Submission failed. Please try again.');
            })
            .finally(() => {
                void submitRequestStartedAt;
            });
    };

    const handleConfirmWithFeedback = (...args) => {
        Promise.resolve()
            .then(() => handleConfirm(...args))
            .catch(error => {
                const submitError = buildQuoteError(error, 10);
                props.handleErrorSubmit(submitError);
                props.notify('tc', null, submitError.errorMessage);
                releaseOrderSubmitWhenLockExpires();
                setSubmissionMessage(submitError.errorMessage || 'Submission failed. Please try again.');
            });
    };

    // const handleSelect = (result, lat, lng) => {
    //     var split = result.split(',');
    //     var stateZip = split[2].split(' ');
    //     var state = null;
    //     for (var i = 0; i < states.length; i++) {
    //         if (states[i].label === stateZip[1]) {
    //             state = states[i].val;
    //             break;
    //         }
    //     }
    //     setAddress(result);
    //     setCustomerAddress(split[0]);
    //     setCustomerCity(split[1]);
    //     setCustomerState(state);
    //     setCustomerZip(stateZip[2]);
    //     setCustomerLat(lat);
    //     setCustomerLong(lng);
    // };

    const handleFileReady = file => {
        setSelectedFile(file);
    };

    const SHIPMENT_UPLOAD_DOC_TYPE = 'admin_message_attachment';

    const uploadFile = async (shipmentId, shipmentFriendlyId) => {
        if (!selectedFile) return;

        const uploadHosts = getUploadHostAttempts(config.config.api_files.host);

        if (uploadHosts.length === 0) {
            setSubmissionMessage('File upload failed: missing upload host');
            return;
        }

        let lastAttemptedHost = uploadHosts[0];

        try {
            for (let i = 0; i < uploadHosts.length; i += 1) {
                const uploadHost = uploadHosts[i];
                lastAttemptedHost = uploadHost;

                const formData = new FormData();
                formData.append('bill_of_lading_file', selectedFile);

                const abortController = typeof AbortController !== 'undefined' ? new AbortController() : null;
                const abortTimeoutId = abortController
                    ? window.setTimeout(() => {
                        abortController.abort();
                    }, SHIPMENT_FILE_UPLOAD_ABORT_MS)
                    : null;

                try {
                    const request = axios({
                        method: 'post',
                        headers: {
                            Authorization: buildAuthorizationHeader(),
                            'content-type': 'multipart/form-data'
                        },
                        url: `${uploadHost}/shipments/${shipmentId}/bol/upload?client_env=${clientEnv}&shipment_friendly_id=${shipmentFriendlyId}&doc_type=${encodeURIComponent(SHIPMENT_UPLOAD_DOC_TYPE)}`,
                        data: formData,
                        timeout: SHIPMENT_FILE_UPLOAD_TIMEOUT_MS,
                        signal: abortController ? abortController.signal : undefined,
                    });

                    let hardTimeoutId = null;
                    const hardTimeout = new Promise((_, reject) => {
                        hardTimeoutId = window.setTimeout(() => {
                            reject(Object.assign(new Error('Upload timed out'), { code: 'UPLOAD_TIMEOUT' }));
                        }, SHIPMENT_FILE_UPLOAD_TIMEOUT_MS + 1000);
                    });

                    const uploadResponse = await Promise.race([request, hardTimeout]).finally(() => {
                        if (hardTimeoutId) {
                            window.clearTimeout(hardTimeoutId);
                        }
                    });

                    const addedToShipmentsDb = Boolean(uploadResponse && uploadResponse.data && uploadResponse.data.added_to_shipments_db);

                    if (!addedToShipmentsDb) {
                        throw new Error('Uploaded file did not persist to shipment documents. Please try again.');
                    }

                    setSubmissionMessage('File uploaded successfully! Redirecting...');
                    return;
                } catch (attemptError) {
                    const isNetworkError = attemptError && !attemptError.response;
                    const hasMoreHosts = i < uploadHosts.length - 1;

                    if (!isNetworkError || !hasMoreHosts) {
                        throw attemptError;
                    }
                } finally {
                    if (abortTimeoutId) {
                        window.clearTimeout(abortTimeoutId);
                    }
                }
            }

        } catch (error) {
            const details = extractUploadErrorMessage(error);
            const hostLabel = lastAttemptedHost ? ` [host: ${lastAttemptedHost}]` : '';
            setSubmissionMessage(details.statusCode ? `File upload failed (${details.statusCode}): ${details.message}${hostLabel}` : `File upload failed: ${details.message}${hostLabel}`);
        }
    };

    // Helper function to convert country codes for different APIs
    const getCountryCode = countryCode => {
        if (countryCode === 'US') return 'USA';
        if (countryCode === 'CA') return 'CAN';
        if (countryCode === 'MX') return 'MEX';
        return 'USA'; // default fallback
    };

    const handlePickupSelect = (result, lat, lng, index, country, countryCode) => {
        if (result && typeof result === 'object') {
            setIsPickupPinnedLocation(true);
            setPickFullAddress(result.formattedAddress || result.address1 || '');
            setPickAddress1(result.address1 || result.formattedAddress || '');
            setPickAddress2(result.address2 || '');
            setPickCity(result.city || '');
            setPickState(result.state || '');
            setPickZip(result.zip || '');
            setPickLat(result.lat);
            setPickLng(result.lng);
            setPickCountry(result.countryCode || result.country || 'US');
            return;
        }

        setIsPickupPinnedLocation(false);

        var split = result.split(',');
        var state = null;
        var zip = null;
        var city = null;
        var address1 = null;

        //! Mapbox returns full name of the state
        // for (var i = 0; i < states.length; i++) {
        //   if (states[i].label === stateZip[1]) {
        //     state = states[i].val;
        //     break;
        //   }
        // }
        //! Google returns abbreviated name of the state

        // Handle different countries
        if (countryCode === 'MX') {
            // Mexico format: "Street, City, ZIP ExtendedCity, State, Mexico"
            // Example: "Av Paseo de la Reforma 250, El Campanario, 76146 Santiago de Querétaro, Qro., Mexico"
            address1 = split[0] ? split[0].trim() : '';
            city = split[1] ? split[1].trim() : '';

            // Extract zip from split[2] (should be like " 76146 Santiago de Querétaro")
            if (split[2]) {
                var zipSection = split[2].trim();
                var zipMatch = zipSection.match(/^(\d{5})/); // Extract 5-digit zip at start
                zip = zipMatch ? zipMatch[1] : '';
            }

            // State is in split[3] for Mexico (like " Qro.")
            if (split[3]) {
                var stateSection = split[3].trim();
                // Try to find the state abbreviation in our mexicanStates list
                for (let i = 0; i < mexicanStates.length; i++) {
                    if (mexicanStates[i].val === stateSection || mexicanStates[i].label === stateSection || mexicanStates[i].short === stateSection) {
                        state = mexicanStates[i].val;
                        break;
                    }
                }
                // If not found in our list, use the raw value
                if (!state) state = stateSection;
            }
        } else {
            // US/CA format: "Street, City, STATE ZIP"
            address1 = split[0] ? split[0].trim() : '';
            city = split[1] ? split[1].trim() : '';

            var stateZip = split[2] ? split[2].split(' ') : [];

            if (countryCode === 'US') {
                state = stateZip[1];
                zip = stateZip[2];
            } else if (countryCode === 'CA') {
                // Canadian format: Find province abbreviation
                for (let i = 0; i < canadianProvinces.length; i++) {
                    if (canadianProvinces[i].label === stateZip[1]) {
                        state = canadianProvinces[i].val;
                        break;
                    }
                }
                if (!state) state = stateZip[1]; // fallback to Google's returned value

                // Canadian postal code parsing - join remaining parts for full postal code
                if (stateZip.length > 2) {
                    zip = stateZip.slice(2).join(' ');
                } else {
                    zip = stateZip[2];
                }
            } else {
                state = stateZip[1];
                zip = stateZip[2];
            }
        }

        setPickFullAddress(result);
        setPickAddress1(address1);
        setPickCity(city);
        setPickState(state);
        setPickZip(zip);
        setPickLat(lat);
        setPickLng(lng);
        setPickCountry(countryCode);
    };

    const handleDropoffSelect = (result, lat, lng, index, country, countryCode) => {
        if (result && typeof result === 'object') {
            setIsDropoffPinnedLocation(true);
            setDropFullAddress(result.formattedAddress || result.address1 || '');
            setDropAddress1(result.address1 || result.formattedAddress || '');
            setDropAddress2(result.address2 || '');
            setDropCity(result.city || '');
            setDropState(result.state || '');
            setDropZip(result.zip || '');
            setDropLat(result.lat);
            setDropLng(result.lng);
            setDropCountry(result.countryCode || result.country || 'US');
            return;
        }

        setIsDropoffPinnedLocation(false);

        var split = result.split(',');
        var state = null;
        var zip = null;
        var city = null;
        var address1 = null;

        //! Mapbox returns full name of the state
        // for (var i = 0; i < states.length; i++) {
        //   if (states[i].label === stateZip[1]) {
        //     state = states[i].val;
        //     break;
        //   }
        // }
        //! Google returns abbreviated name of the state

        // Handle different countries
        if (countryCode === 'MX') {
            // Mexico format: "Street, City, ZIP ExtendedCity, State, Mexico"
            // Example: "Av Paseo de la Reforma 250, El Campanario, 76146 Santiago de Querétaro, Qro., Mexico"
            address1 = split[0] ? split[0].trim() : '';
            city = split[1] ? split[1].trim() : '';

            // Extract zip from split[2] (should be like " 76146 Santiago de Querétaro")
            if (split[2]) {
                var zipSection = split[2].trim();
                var zipMatch = zipSection.match(/^(\d{5})/); // Extract 5-digit zip at start
                zip = zipMatch ? zipMatch[1] : '';
            }

            // State is in split[3] for Mexico (like " Qro.")
            if (split[3]) {
                var stateSection = split[3].trim();
                // Try to find the state abbreviation in our mexicanStates list
                for (var j = 0; j < mexicanStates.length; j++) {
                    if (mexicanStates[j].val === stateSection || mexicanStates[j].label === stateSection || mexicanStates[j].short === stateSection) {
                        state = mexicanStates[j].val;
                        break;
                    }
                }
                // If not found in our list, use the raw value
                if (!state) state = stateSection;
            }
        } else {
            // US/CA format: "Street, City, STATE ZIP"
            address1 = split[0] ? split[0].trim() : '';
            city = split[1] ? split[1].trim() : '';

            var stateZip = split[2] ? split[2].split(' ') : [];

            if (countryCode === 'US') {
                state = stateZip[1];
                zip = stateZip[2];
            } else if (countryCode === 'CA') {
                // Canadian format: Find province abbreviation
                for (var k = 0; k < canadianProvinces.length; k++) {
                    if (canadianProvinces[k].label === stateZip[1]) {
                        state = canadianProvinces[k].val;
                        break;
                    }
                }
                if (!state) state = stateZip[1]; // fallback to Google's returned value

                // Canadian postal code parsing - join remaining parts for full postal code
                if (stateZip.length > 2) {
                    zip = stateZip.slice(2).join(' ');
                } else {
                    zip = stateZip[2];
                }
            } else {
                state = stateZip[1];
                zip = stateZip[2];
            }
        }

        setDropFullAddress(result);
        setDropAddress1(address1);
        setDropCity(city);
        setDropState(state);
        setDropZip(zip);
        setDropLat(lat);
        setDropLng(lng);
        setDropCountry(countryCode);
    };

    const currentPickupPinLocation = useMemo(() => {
        if (typeof pickLat !== 'number' || typeof pickLng !== 'number') {
            return null;
        }

        return {
            lat: pickLat,
            lng: pickLng,
            formattedAddress: pickFullAddress || ''
        };
    }, [pickFullAddress, pickLat, pickLng]);

    const currentDropoffPinLocation = useMemo(() => {
        if (typeof dropLat !== 'number' || typeof dropLng !== 'number') {
            return null;
        }

        return {
            lat: dropLat,
            lng: dropLng,
            formattedAddress: dropFullAddress || ''
        };
    }, [dropFullAddress, dropLat, dropLng]);

    const currentWaypointPinLocation = useMemo(() => {
        if (activeWaypointPinIndex < 0) {
            return null;
        }

        const activeWaypoint = pickAndDropWaypointsArray[activeWaypointPinIndex];

        if (!activeWaypoint || typeof activeWaypoint.waypointLat !== 'number' || typeof activeWaypoint.waypointLng !== 'number') {
            return null;
        }

        return {
            lat: activeWaypoint.waypointLat,
            lng: activeWaypoint.waypointLng,
            formattedAddress: activeWaypoint.waypointFullAddress || ''
        };
    }, [activeWaypointPinIndex, pickAndDropWaypointsArray]);

    const handleDropWaypointSelect = (result, lat, lng, index) => {
        const normalizedResult = typeof result === 'string' ? { formattedAddress: result } : result && typeof result === 'object' ? result : null;

        const formattedAddress = normalizedResult ? normalizedResult.formattedAddress || normalizedResult.address || '' : '';
        const address1 = normalizedResult ? normalizedResult.address1 || normalizedResult.address_1 || '' : '';
        const city = normalizedResult ? normalizedResult.city || '' : '';
        const state = normalizedResult ? normalizedResult.state || normalizedResult.stateCode || '' : '';
        const zip = normalizedResult ? normalizedResult.zip || '' : '';

        const fallbackParts = typeof formattedAddress === 'string' ? formattedAddress.split(',') : [];
        const fallbackStateZip = fallbackParts[2] ? fallbackParts[2].trim().split(' ') : [];
        const resolvedState = state || fallbackStateZip[0] || '';
        const resolvedZip = zip || fallbackStateZip[1] || '';

        setPickAndDropWaypointsArray(
            pickAndDropWaypointsArray.map((ar, i) =>
                i === index
                    ? {
                          ...pickAndDropWaypointsArray[index],
                          waypointFullAddress: formattedAddress,
                          waypointAddress1: address1 || fallbackParts[0] || '',
                          waypointCity: city || fallbackParts[1] || '',
                          waypointState: resolvedState,
                          waypointZip: resolvedZip,
                          waypointLat: lat,
                          waypointLng: lng,
                          isPinnedLocation: false
                      }
                    : ar
            )
        );
    };

    const priceChoice = (standardPrice, expeditedPrice, superExpeditedPrice) => {
        return (
            <PriceChoices
                showForm2={props.showForm2 || isActiveEdit}
                address={address}
                standardPrice={standardPrice}
                expeditedPrice={expeditedPrice}
                superExpeditedPrice={superExpeditedPrice}
                oneDayThreePrice={oneDayThreePrice}
                confidencePercent={dynamicPricingInfo ? dynamicPricingInfo.confidence_percent : null}
                totalDriverCount={dynamicPricingInfo ? dynamicPricingInfo.total_driver_count : null}
                nearbyDriverLocked={nearbyDriverLocked}
                weatherCondition={dynamicPricingInfo ? dynamicPricingInfo.weather_condition : null}
                setIsScheduled={setIsScheduled}
                setIsExpedited={setIsExpedited}
                setIsSuperExpedited={setIsSuperExpedited}
                price={price}
                setPrice={setPrice}
                priceChoice={priceChoice}
                setIsProceeded={setIsProceeded}
                errorMessageSubmit={errorMessageSubmit}
                scheduledTime={scheduledTime}
                setScheduledTime={setScheduledTime}
                isScheduled={isScheduled}
                setScheduledDate={setScheduledDate}
                scheduledFor={scheduledFor}
                errors={errors}
                vehicleType={vehicleType}
                isProceeded={isProceeded}
                handleSubmit={handleSubmit}
                standardPriceRangeLow={standardPriceRangeLow}
                standardPriceRangeHigh={standardPriceRangeHigh}
                expeditedPriceRangeLow={expeditedPriceRangeLow}
                expeditedPriceRangeHigh={expeditedPriceRangeHigh}
                superExpeditedPriceRangeLow={superExpeditedPriceRangeLow}
                superExpeditedPriceRangeHigh={superExpeditedPriceRangeHigh}
                priceRangeLow={priceRangeLow}
                hasTqlRateValue={hasTqlRateValue}
                LTLPrice={LTLPrice}
                LTLLeadTime={LTLLeadTime}
                setIsLTL={setIsLTL}
                isLTL={isLTL}
                setVehicleType={setVehicleType}
                recommendedVehicleType={recommendedVehicleType}
                fedexPricesOptions={fedexLocked ? [] : fedexPricesOptions}
                setIsFedex={setIsFedex}
                setFedexSelection={setFedexSelection}
                setIsShipTomorrow={setIsShipTomorrow}
                isActiveEdit={isActiveEdit && !hasActiveEditPricingChanges}
            />
        );
    };
    if (loading) return <Loading hidden={!showForm} />;
    return (
        <>
            {props.showForm && (
                <div id="collapse" style={{ padding: '0 150px' }}>
                <form className="dropship-container" autoComplete="off" style={{ padding: `15px` }}>
                    <input autoComplete="off" hidden={true}></input>
                    <CardTitle id="form-top" tag="h2" style={{ padding: '20px 0', fontWeight: 'bold' }}>
                        {editingSavedShipmentId ? 'Continue Your Delivery' : 'Create a Delivery'}
                    </CardTitle>
                    {/* deal with waypoint error and different items */}
                    <div hidden={allowMultipleWaypoints ? !infiniteStopsError : !twoStopsError}>
                        <p style={{ color: 'red', marginTop: '4px', paddingBottom: 20 }}>Please fix the problems on this form</p>
                    </div>
                    <Divider />

                    <div>
                        <SectionTitle title="Pickup" tooltipType="pickup" />
                        {/* <SectionProgressBar gradient="linear-gradient(90deg, #ff7c1a 0%, #ff7c1a 15%, #1da1f2 45%, #7b2ff2 80%, #7b2ff2 100%)" /> */}
                        {/* <SectionProgressBar gradient="linear-gradient(90deg, #f7941d 0%, #344675 100%)" /> */}
                        <SectionProgressBar gradient="linear-gradient(90deg, #f7941d 0%, #f7941d 20%, #344675 40%, #344675 100%)" />
                        <Row style={{ paddingTop: 0 }}></Row>
                        <Row style={{ paddingTop: 0 }}>
                            <FormGroup style={{ padding: 15 }} check className="form-check-radio" id="store-address">
                                <Label check style={{ fontSize: '1.1em' }}>
                                    <Input
                                        required
                                        type="radio"
                                        checked={isPickupStore}
                                        value="mp"
                                        name="store-address-radios"
                                        id="my-store-address"
                                        onChange={() => {
                                            setIsPickupStore(true);
                                            setIsDropoffStore(false);
                                        }}
                                    />
                                    <span id="radio" className="form-check-sign" style={{ padding: '3px' }} /> My Address
                                </Label>
                            </FormGroup>
                            <FormGroup style={{ padding: 15 }} check className="form-check-radio" id="shipment-weight">
                                <Label check style={{ fontSize: '1.1em' }}>
                                    <Input
                                        required
                                        type="radio"
                                        checked={!isPickupStore}
                                        name="store-address-radios"
                                        value="dp"
                                        id="different-address"
                                        onChange={() => {
                                            setIsPickupStore(false);
                                        }}
                                    />
                                    <span id="radio" className="form-check-sign" style={{ padding: '3px' }} /> Different Address
                                </Label>
                            </FormGroup>
                        </Row>
                        <Row form={true}>
                            <Col style={{ textAlign: 'left' }}>
                                <Label for="storeFirstName" style={{ fontSize: '1.1em' }}>
                                    First Name
                                </Label>
                                <FormGroup className={errors.pickupFirstname ? 'has-danger' : 'thisIsAPlaceholderClass'}>
                                    <PickupCustomerAutoComplete
                                        isPickupStore={isPickupStore}
                                        storeFirstName={storeFirstName}
                                        pickFirstname={pickFirstname}
                                        setPickFirstname={setPickFirstname}
                                        pickupCustomerSuggestions={pickupCustomerSuggestions}
                                        setPickupCustomerSuggestions={setPickupCustomerSuggestions}
                                        storeId={storeId}
                                        setPickLastname={setPickLastname}
                                        setPickPhone={setPickPhone}
                                        pickFullAddress={pickFullAddress}
                                        setPickFullAddress={setPickFullAddress}
                                        setPickup={setPickup}
                                        isPickupAutoFilled={isPickupAutoFilled}
                                        setIsPickupAutoFilled={setIsPickupAutoFilled}
                                        setPickAddress1={setPickAddress1}
                                        setPickAddress2={setPickAddress2}
                                        setPickCity={setPickCity}
                                        setPickState={setPickState}
                                        setPickZip={setPickZip}
                                        setPickCountry={setPickCountry}
                                        setPickLat={setPickLat}
                                        setPickLng={setPickLng}
                                        setPickBusinessName={setPickBusinessName}
                                    />
                                    <div hidden={!errors.pickupFirstname}>
                                        <p style={{ color: 'red', marginTop: '4px' }}>This is a required field</p>
                                    </div>
                                </FormGroup>
                            </Col>
                            <Col style={{ textAlign: 'left' }}>
                                <Label for="storeLastName" style={{ fontSize: '1.1em' }}>
                                    Last Name
                                </Label>
                                <FormGroup className={errors.pickupLastname ? 'has-danger' : 'thisIsAPlaceholderClass'}>
                                    <Input
                                        required
                                        readOnly={isPickupStore}
                                        type="text"
                                        name="storeLastName"
                                        id="store-last-name"
                                        placeholder="Doe"
                                        onChange={e => (isPickupStore ? null : setPickLastname(e.target.value))}
                                        value={isPickupStore ? storeLastName : pickLastname}
                                    />
                                    <div hidden={!errors.pickupLastname}>
                                        <p style={{ color: 'red', marginTop: '4px' }}>This is a required field</p>
                                    </div>
                                </FormGroup>
                            </Col>
                            <Col style={{ textAlign: 'left' }}>
                                <Label for="storePhone" style={{ fontSize: '1.1em' }}>
                                    Phone Number
                                </Label>
                                <FormGroup className={errors.pickupPhone ? 'has-danger' : 'thisIsAPlaceholderClass'}>
                                    <Input
                                        readOnly={isPickupStore}
                                        required
                                        type="text"
                                        name="storePhone"
                                        id="store-phone"
                                        placeholder="555-555-5555"
                                        onChange={e => (isPickupStore ? null : setPickPhone(e.target.value))}
                                        value={isPickupStore ? storePhone : pickPhone}
                                    />
                                    <div hidden={!errors.pickupPhone}>
                                        <p style={{ color: 'red', marginTop: '4px' }}>Please enter a valid phone number (10-13 digits)</p>
                                    </div>
                                </FormGroup>
                            </Col>
                            <Col>
                                <FormGroup>
                                    <Label for="order-pickup" style={{ fontSize: '1.1em' }}>
                                        Business Name
                                    </Label>
                                    <Input
                                        readOnly={isPickupStore}
                                        type="text"
                                        name="pickBusinessName"
                                        id="pick-business-name"
                                        placeholder="Store Name"
                                        onChange={e => (isPickupStore ? null : setPickBusinessName(e.target.value))}
                                        value={isPickupStore ? storeName : pickBusinessName}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col>
                                <FormGroup>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                                        <Label style={{ fontSize: '1.1em', marginBottom: 0 }}>Pickup Address</Label>
                                        <Button color="info" outline size="sm" onClick={() => setIsPickupPinModalOpen(true)} disabled={isPickupStore}>
                                            Drop Pin
                                        </Button>
                                    </div>
                                    {isPickupAutoFilled ? (
                                        <Input
                                            className="autofilled-address-input"
                                            value={isPickupStore ? storeAddress : pickFullAddress}
                                            onChange={e => (isPickupStore ? null : setPickFullAddress(e.target.value))}
                                        />
                                    ) : (
                                        // <MapboxAutocomplete
                                        //   id="mapbox-autocomplete"
                                        //   inputClass={errors.pickupAddress ? 'form-control mb-2' + ' has-danger' : 'form-control mb-2 has-danger'}
                                        //   publicKey={MAPBOX_TOKEN}
                                        //   onSuggestionSelect={handlePickupSelect}
                                        //   country="us"
                                        //   resetSearch={false}
                                        //   readOnly={isPickupStore}
                                        //   defaultValue={isPickupStore ? storeAddress : null}
                                        //   value={isPickupStore ? storeAddress : pickFullAddress}
                                        //   query={pickFullAddress}
                                        //   placeholder="Street Address"
                                        //   proximityLat={vendorLat}
                                        //   proximityLng={vendorLong}
                                        // />
                                        <GoogleMapAutoComplete
                                            inputClass={errors.pickupAddress ? 'form-control mb-2 has-danger' : 'form-control mb-2'}
                                            onSuggestionSelect={(...args) => {
                                                setIsPickupPinnedLocation(false);
                                                handlePickupSelect(...args);
                                            }}
                                            // Adjust this based on your requirements for Google Places Autocomplete
                                            resetSearch={false}
                                            readOnly={isPickupStore}
                                            defaultValue={isPickupStore ? storeAddress : null}
                                            value={isPickupStore ? storeAddress : pickFullAddress}
                                            query={pickFullAddress}
                                            placeholder="Street Address"
                                            proximityLat={vendorLat}
                                            proximityLng={vendorLong}
                                        />
                                    )}

                                    <div hidden={errorMessageDistance.errorCode !== 1}>
                                        <p style={{ color: 'red', marginTop: '4px' }}>{errorMessageDistance.errorMessage}</p>
                                    </div>
                                    <div hidden={!errors.pickupAddress}>
                                        <p style={{ color: 'red', marginTop: '4px' }}>Please choose an address from the dropdown menu</p>
                                    </div>
                                    {isPickupPinnedLocation ? (
                                        <p style={{ color: isCoordinateFallbackLabel(pickFullAddress) ? '#f7941d' : '#6c757d', marginTop: '4px' }}>
                                            Pinned location selected. Reopen Drop Pin to adjust it.
                                        </p>
                                    ) : null}
                                </FormGroup>
                            </Col>
                            <Col>
                                <FormGroup>
                                    <Label for="order-pickup" style={{ fontSize: '1.1em' }}>
                                        Pickup Instructions
                                    </Label>
                                    <Input
                                        type="text"
                                        name="dropoff"
                                        id="pickup-instructions"
                                        placeholder="Park in back, attendant will assist."
                                        onChange={e => {
                                            if (e.target.value.length < 510) {
                                                return setPickup(e.target.value);
                                            } else {
                                                return;
                                            }
                                        }}
                                        value={pickup}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        {allowMultipleWaypoints && isInfiniteWaypointsSelected && (
                            <LineItems
                                title="Pick up Item(s)"
                                items={pickupItems}
                                setItems={setPickupItems}
                                itemsErrorArray={pickupItemsError}
                                setItemsErrorArray={setPickupItemsError}
                                pickList1={pickupItems}
                                pickList2={[]}
                            />
                        )}

                        {allowMultipleWaypoints && (
                            <Row style={{ display: 'flex', justifyContent: 'center' }}>
                                {allowMultipleWaypoints && (
                                    <>
                                        <Button
                                            className={isInfiniteWaypointsSelected ? 'btn btn-danger' : 'btn btn-success'}
                                            id="addWaypointButton"
                                            style={{ margin: '10px 0', ...(multidropLocked ? { opacity: 0.45, filter: 'grayscale(1)', cursor: 'not-allowed' } : {}) }}
                                            onClick={multidropLocked ? undefined : toggleAddWaypoint}
                                            disabled={multidropLocked}
                                        >
                                            {isInfiniteWaypointsSelected ? 'Remove Waypoints' : 'ADD PICKUP/DROPOFF'}
                                        </Button>
                                        {multidropLocked ? <UpgradeHint label="Upgrade to Pro to add multiple pickups/drop-offs" style={{ width: '100%', textAlign: 'center' }} /> : null}
                                        <Tooltip isOpen={tooltipOpen} toggle={toggleTooltip} target="addWaypointButton" placement="top">
                                            <div
                                                style={{
                                                    background: '#fff',
                                                    color: '#222',
                                                    borderRadius: 8,
                                                    padding: '16px 18px',
                                                    fontSize: 13,
                                                    lineHeight: 1.6,
                                                    minWidth: 350,
                                                    width: 350,
                                                    maxWidth: 350,
                                                    border: '1.5px solid #b5d2ee',
                                                    textAlign: 'left',
                                                    boxShadow: 'none'
                                                }}
                                            >
                                                Optional: Add an additional stop between pickup and final delivery — ideal for multi-stop routes or pickups from multiple suppliers.
                                            </div>
                                        </Tooltip>
                                    </>
                                )}
                            </Row>
                        )}

                        {isInfiniteWaypointsSelected &&
                            pickAndDropWaypointsArray.map((waypoint, index) => {
                                const waypointKey = waypoint.waypointKey || `wp-${index}`;
                                return (
                                    <React.Fragment key={waypointKey}>
                                        <Divider />
                                        <Row>
                                            <Col style={{ textAlign: 'left' }}>
                                                <Button
                                                    className="btn btn-info"
                                                    size="sm"
                                                    onClick={() => {
                                                        let updateArray = [];
                                                        let errorsUpdate = [];
                                                        for (let i = 0; i < index; i++) {
                                                            updateArray.push(pickAndDropWaypointsArray[i]);
                                                            errorsUpdate.push(infinitePickAndDropWaypointsErrors[i]);
                                                        }
                                                        updateArray.push({
                                                            waypointKey: `waypoint-${Date.now()}-${index}`,
                                                            waypointFirstname: '',
                                                            waypointLastname: '',
                                                            waypointPhone: '',
                                                            waypointAddress1: null,
                                                            waypointAddress2: null,
                                                            waypointFullAddress: '',
                                                            waypointLat: '',
                                                            waypointLng: '',
                                                            waypointCity: '',
                                                            waypointState: '',
                                                            waypointCountry: '',
                                                            waypointZip: '',
                                                            waypointPickupInstruction: '',
                                                            waypointDropoffInstruction: '',
                                                            waypointBusinessName: '',
                                                            waypointCustomerSuggestions: [],
                                                            isPinnedLocation: false,
                                                            isWaypointAutoFilled: false,
                                                            pickItems: [{ quantity: 1, detail: '', unit: '', weight: 1, length: 1, width: 1, height: 1, nmfc: '' }],
                                                            dropItems: [{ quantity: 1, detail: '', unit: '', weight: 1, length: 1, width: 1, height: 1, nmfc: '' }],
                                                            pickItemsError: [false],
                                                            dropItemsError: [false],
                                                            isWaypointPickup: false,
                                                            isWaypointDropoff: false
                                                        });
                                                        errorsUpdate.push({
                                                            waypointFirstname: false,
                                                            waypointLastname: false,
                                                            waypointPhone: false,
                                                            waypointAddress: false
                                                        });
                                                        for (let i = index; i < pickAndDropWaypointsArray.length; i++) {
                                                            updateArray.push(pickAndDropWaypointsArray[i]);
                                                            errorsUpdate.push(infinitePickAndDropWaypointsErrors[i]);
                                                        }
                                                        setPickAndDropWaypointsArray(updateArray);
                                                        setInfinitePickAndDropWaypointsErrors(errorsUpdate);
                                                    }}
                                                >
                                                    Add Pick/Drop Above{' '}
                                                    <span style={{ display: 'inline' }} role="img" aria-label="up triangle">
                                                        🔺
                                                    </span>
                                                </Button>
                                            </Col>

                                            {pickAndDropWaypointsArray.length > 1 && (
                                                <Col style={{ textAlign: 'right' }}>
                                                    <Button
                                                        className="btn btn-warning"
                                                        size="sm"
                                                        onClick={() => {
                                                            setPickAndDropWaypointsArray(
                                                                pickAndDropWaypointsArray.filter((ar, i) => {
                                                                    return i !== index;
                                                                })
                                                            );
                                                            setInfinitePickAndDropWaypointsErrors(
                                                                infinitePickAndDropWaypointsErrors.filter((ie, i) => {
                                                                    return i !== index;
                                                                })
                                                            );
                                                        }}
                                                    >
                                                        Delete
                                                    </Button>
                                                </Col>
                                            )}
                                        </Row>
                                        <SectionTitle title={`Pickup / Dropoff (${index + 1})`} tooltipType="dropoff" />
                                        {/* <SectionProgressBar gradient="linear-gradient(90deg, #ff7c1a 0%, #ff7c1a 35%, #1da1f2 60%, #7b2ff2 85%, #7b2ff2 100%)" /> */}
                                        {/* <SectionProgressBar gradient="linear-gradient(90deg, #f7941d 25%, #344675 100%)" /> */}
                                        <SectionProgressBar gradient="linear-gradient(90deg, #f7941d 0%, #f7941d 40%, #344675 60%, #344675 100%)" />
                                        <Row>
                                            <FormGroup check className="mt-3" style={{ marginLeft: '15px', marginBottom: '30px' }}>
                                                <Label check style={{ fontSize: '1.1em' }}>
                                                    <Input
                                                        type="checkbox"
                                                        id="pickup-dropoff-check"
                                                        checked={waypoint.isWaypointPickup}
                                                        value={waypoint.isWaypointPickup}
                                                        onChange={() => {
                                                            setPickAndDropWaypointsArray(
                                                                pickAndDropWaypointsArray.map((ar, i) =>
                                                                    i === index
                                                                        ? {
                                                                              ...ar,
                                                                              isWaypointPickup: !ar.isWaypointPickup
                                                                          }
                                                                        : ar
                                                                )
                                                            );
                                                        }}
                                                    />
                                                    <span className="form-check-sign" />
                                                    Pick Up
                                                </Label>
                                            </FormGroup>
                                            <FormGroup check className="mt-3" style={{ marginLeft: '30px' }}>
                                                <Label check style={{ fontSize: '1.1em' }}>
                                                    <Input
                                                        type="checkbox"
                                                        id="pickup-dropoff-check"
                                                        checked={waypoint.isWaypointDropoff}
                                                        value={waypoint.isWaypointDropoff}
                                                        onChange={() => {
                                                            setPickAndDropWaypointsArray(
                                                                pickAndDropWaypointsArray.map((ar, i) =>
                                                                    i === index
                                                                        ? {
                                                                              ...ar,
                                                                              isWaypointDropoff: !ar.isWaypointDropoff
                                                                          }
                                                                        : ar
                                                                )
                                                            );
                                                        }}
                                                    />
                                                    <span className="form-check-sign" />
                                                    Drop Off
                                                </Label>
                                            </FormGroup>
                                            <div hidden={!waypointErrors.pickOrDrop}>
                                                <p style={{ color: 'red', marginTop: '15px', marginLeft: '20px' }}>Choose at least one field</p>
                                            </div>
                                        </Row>
                                        <Row form={true}>
                                            <Col style={{ textAlign: 'left' }}>
                                                <Label for="infiniteDropCustomerFirstName" style={{ fontSize: '1.1em' }}>
                                                    First Name
                                                </Label>
                                                <FormGroup className={infinitePickAndDropWaypointsErrors[index].waypointFirstname ? 'has-danger' : 'thisIsAPlaceholderClass'}>
                                                    <WaypointCustomerOnlyDropAutoComplete
                                                        dropWaypointsArray={pickAndDropWaypointsArray}
                                                        setDropWaypointsArray={setPickAndDropWaypointsArray}
                                                        index={index}
                                                        storeId={storeId}
                                                    />
                                                    <div hidden={!infinitePickAndDropWaypointsErrors[index].waypointFirstname}>
                                                        <p style={{ color: 'red', marginTop: '4px' }}>This is a required field</p>
                                                    </div>
                                                </FormGroup>
                                            </Col>
                                            <Col style={{ textAlign: 'left' }}>
                                                <Label for="customerLastName" style={{ fontSize: '1.1em' }}>
                                                    Last Name
                                                </Label>
                                                <FormGroup className={infinitePickAndDropWaypointsErrors[index].waypointLastname ? 'has-danger' : 'thisIsAPlaceholderClass'}>
                                                    <Input
                                                        required
                                                        type="text"
                                                        name="customerLastName"
                                                        id="customer-last-name"
                                                        placeholder="Doe"
                                                        onChange={e => {
                                                            setPickAndDropWaypointsArray(
                                                                pickAndDropWaypointsArray.map((ar, i) =>
                                                                    i === index
                                                                        ? {
                                                                              ...pickAndDropWaypointsArray[index],
                                                                              waypointLastname: e.target.value
                                                                          }
                                                                        : ar
                                                                )
                                                            );
                                                        }}
                                                        value={pickAndDropWaypointsArray[index].waypointLastname}
                                                    />
                                                    <div hidden={!infinitePickAndDropWaypointsErrors[index].waypointLastname}>
                                                        <p style={{ color: 'red', marginTop: '4px' }}>This is a required field</p>
                                                    </div>
                                                </FormGroup>
                                            </Col>
                                            <Col style={{ textAlign: 'left' }}>
                                                <Label for="customerPhone" style={{ fontSize: '1.1em' }}>
                                                    Phone Number
                                                </Label>
                                                <FormGroup className={infinitePickAndDropWaypointsErrors[index].waypointPhone ? 'has-danger' : 'thisIsAPlaceholderClass'}>
                                                    <Input
                                                        required
                                                        type="text"
                                                        name="customerPhone"
                                                        id="customer-phone"
                                                        placeholder="555-555-5555"
                                                        onChange={e =>
                                                            setPickAndDropWaypointsArray(
                                                                pickAndDropWaypointsArray.map((ar, i) =>
                                                                    i === index
                                                                        ? {
                                                                              ...pickAndDropWaypointsArray[index],
                                                                              waypointPhone: e.target.value
                                                                          }
                                                                        : ar
                                                                )
                                                            )
                                                        }
                                                        value={pickAndDropWaypointsArray[index].waypointPhone}
                                                    />
                                                    <div hidden={!infinitePickAndDropWaypointsErrors[index].waypointPhone}>
                                                        <p style={{ color: 'red', marginTop: '4px' }}>Please enter a valid phone number (10-13 digits)</p>
                                                    </div>
                                                </FormGroup>
                                            </Col>
                                            <Col>
                                                <FormGroup>
                                                    <Label for="order-multidrop" style={{ fontSize: '1.1em' }}>
                                                        Business Name
                                                    </Label>
                                                    <Input
                                                        type="text"
                                                        name="multidropBusinessName"
                                                        id="multidrop-business-name"
                                                        placeholder="Store Name"
                                                        value={pickAndDropWaypointsArray[index].waypointBusinessName}
                                                        onChange={e => {
                                                            setPickAndDropWaypointsArray(
                                                                pickAndDropWaypointsArray.map((ar, i) =>
                                                                    i === index
                                                                        ? {
                                                                              ...pickAndDropWaypointsArray[index],
                                                                              waypointBusinessName: e.target.value
                                                                          }
                                                                        : ar
                                                                )
                                                            );
                                                        }}
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col>
                                                <FormGroup>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                                                        <Label style={{ fontSize: '1.1em', marginBottom: 0 }}>Delivery Address</Label>
                                                        <Button color="info" outline size="sm" onClick={() => setActiveWaypointPinIndex(index)}>
                                                            Drop Pin
                                                        </Button>
                                                    </div>
                                                    <GoogleMapAutoComplete
                                                        inputClass="form-control mb-2"
                                                        index={index}
                                                        onSuggestionSelect={(...args) => {
                                                            setPickAndDropWaypointsArray(
                                                                pickAndDropWaypointsArray.map((candidate, candidateIndex) =>
                                                                    candidateIndex === index
                                                                        ? {
                                                                              ...candidate,
                                                                              isPinnedLocation: false,
                                                                              isWaypointAutoFilled: false
                                                                          }
                                                                        : candidate
                                                                )
                                                            );
                                                            handleDropWaypointSelect(...args);
                                                        }}
                                                        resetSearch={false}
                                                        defaultValue=""
                                                        value={pickAndDropWaypointsArray[index].waypointFullAddress || ''}
                                                        query={pickAndDropWaypointsArray[index].waypointFullAddress || ''}
                                                        placeholder="Street Address"
                                                        proximityLat={vendorLat}
                                                        proximityLng={vendorLong}
                                                    />
                                                    <div hidden={errorMessageDistance.errorCode !== 1}>
                                                        <p style={{ color: 'red', marginTop: '4px' }}>{errorMessageDistance.errorMessage}</p>
                                                    </div>
                                                    <div hidden={!infinitePickAndDropWaypointsErrors[index].waypointAddress}>
                                                        <p style={{ color: 'red', marginTop: '4px' }}>Please choose an address from the dropdown menu</p>
                                                    </div>
                                                </FormGroup>
                                            </Col>
                                            {waypoint.isWaypointPickup && !waypoint.isWaypointDropoff ? (
                                                <Col>
                                                    <FormGroup>
                                                        <Label for="order-pickup" style={{ fontSize: '1.1em' }}>
                                                            Pickup Instructions
                                                        </Label>
                                                        <Input
                                                            type="text"
                                                            name="waypointPickup"
                                                            id="waypoint-pickup-instructions"
                                                            placeholder="Park in back, attendant will assist."
                                                            value={waypoint.waypointPickupInstruction || ''}
                                                            onChange={e => {
                                                                if (e.target.value.length < 510) {
                                                                    setPickAndDropWaypointsArray(
                                                                        pickAndDropWaypointsArray.map((ar, i) =>
                                                                            i === index
                                                                                ? {
                                                                                      ...pickAndDropWaypointsArray[index],
                                                                                      waypointPickupInstruction: e.target.value
                                                                                  }
                                                                                : ar
                                                                        )
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    </FormGroup>
                                                </Col>
                                            ) : !waypoint.isWaypointPickup && waypoint.isWaypointDropoff ? (
                                                <Col>
                                                    <FormGroup>
                                                        <Label for="order-dropoff" style={{ fontSize: '1.1em' }}>
                                                            Dropoff Instructions
                                                        </Label>
                                                        <Input
                                                            type="text"
                                                            name="waypointDropoff"
                                                            id="waypoint-dropoff-instructions"
                                                            placeholder="Gate Code, Apt. #, etc."
                                                            value={waypoint.waypointDropoffInstruction || ''}
                                                            onChange={e => {
                                                                if (e.target.value.length < 510) {
                                                                    setPickAndDropWaypointsArray(
                                                                        pickAndDropWaypointsArray.map((ar, i) =>
                                                                            i === index
                                                                                ? {
                                                                                      ...pickAndDropWaypointsArray[index],
                                                                                      waypointDropoffInstruction: e.target.value
                                                                                  }
                                                                                : ar
                                                                        )
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    </FormGroup>
                                                </Col>
                                            ) : null}
                                        </Row>
                                        {waypoint.isWaypointPickup && waypoint.isWaypointDropoff ? (
                                            <Row>
                                                <Col>
                                                    <FormGroup>
                                                        <Label for="order-pickup" style={{ fontSize: '1.1em' }}>
                                                            Pickup Instructions
                                                        </Label>
                                                        <Input
                                                            type="text"
                                                            name="waypointPickup"
                                                            id="waypoint-pickup-instructions"
                                                            placeholder="Park in back, attendant will assist."
                                                            value={waypoint.waypointPickupInstruction || ''}
                                                            onChange={e => {
                                                                if (e.target.value.length < 510) {
                                                                    setPickAndDropWaypointsArray(
                                                                        pickAndDropWaypointsArray.map((ar, i) =>
                                                                            i === index
                                                                                ? {
                                                                                      ...pickAndDropWaypointsArray[index],
                                                                                      waypointPickupInstruction: e.target.value
                                                                                  }
                                                                                : ar
                                                                        )
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                <Col>
                                                    <FormGroup>
                                                        <Label for="order-dropoff" style={{ fontSize: '1.1em' }}>
                                                            Dropoff Instructions
                                                        </Label>
                                                        <Input
                                                            type="text"
                                                            name="waypointDropoff"
                                                            id="waypoint-dropoff-instructions"
                                                            placeholder="Gate Code, Apt. #, etc."
                                                            value={waypoint.waypointDropoffInstruction || ''}
                                                            onChange={e => {
                                                                if (e.target.value.length < 510) {
                                                                    setPickAndDropWaypointsArray(
                                                                        pickAndDropWaypointsArray.map((ar, i) =>
                                                                            i === index
                                                                                ? {
                                                                                      ...pickAndDropWaypointsArray[index],
                                                                                      waypointDropoffInstruction: e.target.value
                                                                                  }
                                                                                : ar
                                                                        )
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        ) : null}

                                        {waypoint.isWaypointPickup && waypoint.isWaypointDropoff ? (
                                            <>
                                                <LineItems
                                                    title="Pick up Item(s)"
                                                    items={waypoint.pickItems}
                                                    setItems={newItems => {
                                                        setPickAndDropWaypointsArray(prev => prev.map((wp, i) => (i === index ? { ...wp, pickItems: newItems } : wp)));
                                                    }}
                                                    itemsErrorArray={waypoint.pickItemsError}
                                                    setItemsErrorArray={newErrors => {
                                                        setPickAndDropWaypointsArray(prev =>
                                                            prev.map((wp, i) => (i === index ? { ...wp, pickItemsError: newErrors } : wp))
                                                        );
                                                    }}
                                                    pickList1={pickupItems}
                                                    pickList2={waypoint.pickItems}
                                                />
                                                <LineItems
                                                    title="Drop off Item(s)"
                                                    items={waypoint.dropItems}
                                                    setItems={newItems => {
                                                        setPickAndDropWaypointsArray(prev => prev.map((wp, i) => (i === index ? { ...wp, dropItems: newItems } : wp)));
                                                    }}
                                                    itemsErrorArray={waypoint.dropItemsError}
                                                    setItemsErrorArray={newErrors => {
                                                        setPickAndDropWaypointsArray(prev =>
                                                            prev.map((wp, i) => (i === index ? { ...wp, dropItemsError: newErrors } : wp))
                                                        );
                                                    }}
                                                    pickList1={pickupItems}
                                                    pickList2={pickAndDropWaypointsArray
                                                        .slice(0, index + 1)
                                                        .filter(wp => wp.isWaypointPickup)
                                                        .flatMap(wp => wp.pickItems)}
                                                />
                                            </>
                                        ) : waypoint.isWaypointPickup && !waypoint.isWaypointDropoff ? (
                                            <LineItems
                                                title="Pick up Item(s)"
                                                items={waypoint.pickItems}
                                                setItems={newItems => {
                                                    setPickAndDropWaypointsArray(prev => prev.map((wp, i) => (i === index ? { ...wp, pickItems: newItems } : wp)));
                                                }}
                                                itemsErrorArray={waypoint.pickItemsError}
                                                setItemsErrorArray={newErrors => {
                                                    setPickAndDropWaypointsArray(prev =>
                                                        prev.map((wp, i) => (i === index ? { ...wp, pickItemsError: newErrors } : wp))
                                                    );
                                                }}
                                                pickList1={pickupItems}
                                                pickList2={waypoint.pickItems}
                                            />
                                        ) : !waypoint.isWaypointPickup && waypoint.isWaypointDropoff ? (
                                            <LineItems
                                                title="Drop off Item(s)"
                                                items={waypoint.dropItems}
                                                setItems={newItems => {
                                                    setPickAndDropWaypointsArray(prev => prev.map((wp, i) => (i === index ? { ...wp, dropItems: newItems } : wp)));
                                                }}
                                                itemsErrorArray={waypoint.dropItemsError}
                                                setItemsErrorArray={newErrors => {
                                                    setPickAndDropWaypointsArray(prev =>
                                                        prev.map((wp, i) => (i === index ? { ...wp, dropItemsError: newErrors } : wp))
                                                    );
                                                }}
                                                pickList1={pickupItems}
                                                pickList2={pickAndDropWaypointsArray
                                                    .slice(0, index)
                                                    .filter(wp => wp.isWaypointPickup)
                                                    .flatMap(wp => wp.pickItems)}
                                            />
                                        ) : null}
                                        <Row>
                                            <Col style={{ textAlign: 'left' }}>
                                                <Button
                                                    className="btn btn-info"
                                                    size="sm"
                                                    onClick={() => {
                                                        let updateArray = [];
                                                        let errorsUpdate = [];
                                                        for (let i = 0; i < index + 1; i++) {
                                                            updateArray.push(pickAndDropWaypointsArray[i]);
                                                            errorsUpdate.push(infinitePickAndDropWaypointsErrors[i]);
                                                        }

                                                        updateArray.push({
                                                            waypointKey: `waypoint-${Date.now()}-${index + 1}`,
                                                            waypointFirstname: '',
                                                            waypointLastname: '',
                                                            waypointPhone: '',
                                                            waypointAddress1: null,
                                                            waypointAddress2: null,
                                                            waypointFullAddress: '',
                                                            waypointLat: '',
                                                            waypointLng: '',
                                                            waypointCity: '',
                                                            waypointState: '',
                                                            waypointCountry: '',
                                                            waypointZip: '',
                                                            waypointPickupInstruction: '',
                                                            waypointDropoffInstruction: '',
                                                            waypointBusinessName: '',
                                                            waypointCustomerSuggestions: [],
                                                            isPinnedLocation: false,
                                                            isWaypointAutoFilled: false,
                                                            pickItems: [{ quantity: 1, detail: '', unit: '', weight: 1, length: 1, width: 1, height: 1, nmfc: '' }],
                                                            dropItems: [{ quantity: 1, detail: '', unit: '', weight: 1, length: 1, width: 1, height: 1, nmfc: '' }],
                                                            pickItemsError: [false],
                                                            dropItemsError: [false],
                                                            isWaypointPickup: false,
                                                            isWaypointDropoff: false
                                                        });
                                                        errorsUpdate.push({
                                                            waypointFirstname: false,
                                                            waypointLastname: false,
                                                            waypointPhone: false,
                                                            waypointAddress: false
                                                        });
                                                        for (let i = index + 1; i < pickAndDropWaypointsArray.length; i++) {
                                                            updateArray.push(pickAndDropWaypointsArray[i]);
                                                            errorsUpdate.push(infinitePickAndDropWaypointsErrors[i]);
                                                        }
                                                        setPickAndDropWaypointsArray(updateArray);
                                                        setInfinitePickAndDropWaypointsErrors(errorsUpdate);
                                                    }}
                                                >
                                                    Add Pick/Drop Below{' '}
                                                    <span style={{ display: 'inline' }} role="img" aria-label="down triangle">
                                                        🔻
                                                    </span>
                                                </Button>
                                            </Col>
                                        </Row>
                                    </React.Fragment>
                                );
                            })}
                        <Divider />
                        <SectionTitle title="Dropoff" tooltipType="dropoff" />
                        {/* <SectionProgressBar gradient="linear-gradient(90deg, #ff7c1a 0%, #ff7c1a 35%, #1da1f2 60%, #7b2ff2 85%, #7b2ff2 100%)" /> */}
                        {/* <SectionProgressBar gradient="linear-gradient(90deg, #f7941d 25%, #344675 100%)" /> */}
                        <SectionProgressBar gradient="linear-gradient(90deg, #f7941d 0%, #f7941d 40%, #344675 60%, #344675 100%)" />
                        <Row style={{ paddingTop: 0 }}>
                            <FormGroup style={{ padding: 15 }} check className="form-check-radio" id="store-address">
                                <Label check style={{ fontSize: '1.1em' }}>
                                    <Input
                                        required
                                        type="radio"
                                        checked={isDropoffStore}
                                        value="md"
                                        name="store-address-radios1"
                                        id="my-store-address"
                                        onChange={() => {
                                            setIsDropoffStore(true);
                                            setIsPickupStore(false);
                                        }}
                                    />
                                    <span id="radio" className="form-check-sign" style={{ padding: '3px' }} /> My Address
                                </Label>
                            </FormGroup>
                            <FormGroup style={{ padding: 15 }} check className="form-check-radio" id="shipment-weight">
                                <Label check style={{ fontSize: '1.1em' }}>
                                    <Input
                                        required
                                        type="radio"
                                        checked={!isDropoffStore}
                                        name="store-address-radios1"
                                        value="dd"
                                        id="different-address"
                                        onChange={() => {
                                            setIsDropoffStore(false);
                                        }}
                                    />
                                    <span id="radio" className="form-check-sign" style={{ padding: '3px' }} /> Different Address
                                </Label>
                            </FormGroup>
                        </Row>
                        <Row form={true}>
                            <Col style={{ textAlign: 'left' }}>
                                <Label for="customerFirstName" style={{ fontSize: '1.1em' }}>
                                    First Name
                                </Label>
                                <FormGroup className={errors.dropoffFirstname ? 'has-danger' : 'thisIsAPlaceholderClass'}>
                                    <DropOffCustomerAutoComplete
                                        isDropoffStore={isDropoffStore}
                                        storeFirstName={storeFirstName}
                                        dropFirstname={dropFirstname}
                                        setDropFirstname={setDropFirstname}
                                        dropCustomerSuggestions={dropCustomerSuggestions}
                                        setDropCustomerSuggestions={setDropCustomerSuggestions}
                                        storeId={storeId}
                                        setDropLastname={setDropLastname}
                                        setDropPhone={setDropPhone}
                                        dropFullAddress={dropFullAddress}
                                        setDropFullAddress={setDropFullAddress}
                                        setDropoff={setDropoff}
                                        isDropOffAutoFilled={isDropOffAutoFilled}
                                        setIsDropOffAutoFilled={setIsDropOffAutoFilled}
                                        setDropAddress1={setDropAddress1}
                                        setDropAddress2={setDropAddress2}
                                        setDropCity={setDropCity}
                                        setDropState={setDropState}
                                        setDropZip={setDropZip}
                                        setDropCountry={setDropCountry}
                                        setDropLat={setDropLat}
                                        setDropLng={setDropLng}
                                        isRoundTrip={false}
                                        pickFirstname={pickFirstname}
                                        isPickupStore={isPickupStore}
                                        hasThirdWaypoint={false}
                                        setDropoffBusinessName={setDropoffBusinessName}
                                    />
                                    <div hidden={!errors.dropoffFirstname}>
                                        <p style={{ color: 'red', marginTop: '4px' }}>This is a required field</p>
                                    </div>
                                </FormGroup>
                            </Col>
                            <Col style={{ textAlign: 'left' }}>
                                <Label for="customerLastName" style={{ fontSize: '1.1em' }}>
                                    Last Name
                                </Label>
                                <FormGroup className={errors.dropoffLastname ? 'has-danger' : 'thisIsAPlaceholderClass'}>
                                    <Input
                                        required
                                        type="text"
                                        name="customerLastName"
                                        id="customer-last-name"
                                        placeholder="Doe"
                                        onChange={e => (isDropoffStore ? null : setDropLastname(e.target.value))}
                                        value={isDropoffStore ? storeLastName : dropLastname}
                                    />
                                    <div hidden={!errors.dropoffLastname}>
                                        <p style={{ color: 'red', marginTop: '4px' }}>This is a required field</p>
                                    </div>
                                </FormGroup>
                            </Col>
                            <Col style={{ textAlign: 'left' }}>
                                <Label for="customerPhone" style={{ fontSize: '1.1em' }}>
                                    Phone Number
                                </Label>
                                <FormGroup className={errors.dropoffPhone ? 'has-danger' : 'thisIsAPlaceholderClass'}>
                                    <Input
                                        required
                                        type="text"
                                        name="customerPhone"
                                        id="customer-phone"
                                        placeholder="555-555-5555"
                                        onChange={e => (isDropoffStore ? null : setDropPhone(e.target.value))}
                                        value={isDropoffStore ? storePhone : dropPhone}
                                    />
                                    <div hidden={!errors.dropoffPhone}>
                                        <p style={{ color: 'red', marginTop: '4px' }}>Please enter a valid phone number (10-13 digits)</p>
                                    </div>
                                </FormGroup>
                            </Col>
                            <Col>
                                <FormGroup>
                                    <Label for="order-dropoff" style={{ fontSize: '1.1em' }}>
                                        Business Name
                                    </Label>
                                    <Input
                                        type="text"
                                        name="dropoffBusinessName"
                                        id="dropoff-business-name"
                                        placeholder="Store Name"
                                        onChange={e => (isDropoffStore ? null : setDropoffBusinessName(e.target.value))}
                                        value={isDropoffStore ? storeName : dropoffBusinessName}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col>
                                <FormGroup>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                                        <Label style={{ fontSize: '1.1em', marginBottom: 0 }}>Delivery Address</Label>
                                        <Button color="info" outline size="sm" onClick={() => setIsDropoffPinModalOpen(true)} disabled={isDropoffStore}>
                                            Drop Pin
                                        </Button>
                                    </div>
                                    {isDropOffAutoFilled ? (
                                        <Input
                                            className="autofilled-address-input"
                                            value={isDropoffStore ? storeAddress : dropFullAddress}
                                            onChange={e => (isDropoffStore ? null : setDropFullAddress(e.target.value))}
                                        />
                                    ) : (
                                        // <MapboxAutocomplete
                                        //   id="mapbox-autocomplete"
                                        //   inputClass="form-control mb-2"
                                        //   publicKey={MAPBOX_TOKEN}
                                        //   onSuggestionSelect={handleDropoffSelect}
                                        //   country="us"
                                        //   resetSearch={false}
                                        //   readOnly={isDropoffStore}
                                        //   defaultValue={isDropoffStore ? storeAddress : null}
                                        //   value={isDropoffStore ? storeAddress : dropFullAddress}
                                        //   query={dropFullAddress}
                                        //   placeholder="Street Address"
                                        //   proximityLat={vendorLat}
                                        //   proximityLng={vendorLong}
                                        // />
                                        <GoogleMapAutoComplete
                                            inputClass="form-control mb-2"
                                            onSuggestionSelect={(...args) => {
                                                setIsDropoffPinnedLocation(false);
                                                handleDropoffSelect(...args);
                                            }}
                                            resetSearch={false}
                                            readOnly={isDropoffStore}
                                            defaultValue={isDropoffStore ? storeAddress : null}
                                            value={isDropoffStore ? storeAddress : dropFullAddress}
                                            query={dropFullAddress}
                                            placeholder="Street Address"
                                            proximityLat={vendorLat}
                                            proximityLng={vendorLong}
                                        />
                                    )}
                                    <div hidden={errorMessageDistance.errorCode !== 1}>
                                        <p style={{ color: 'red', marginTop: '4px' }}>{errorMessageDistance.errorMessage}</p>
                                    </div>
                                    <div hidden={!errors.dropoffAddress}>
                                        <p style={{ color: 'red', marginTop: '4px' }}>Please choose an address from the dropdown menu</p>
                                    </div>
                                    {isDropoffPinnedLocation ? (
                                        <p style={{ color: isCoordinateFallbackLabel(dropFullAddress) ? '#f7941d' : '#6c757d', marginTop: '4px' }}>
                                            Pinned location selected. Reopen Drop Pin to adjust it.
                                        </p>
                                    ) : null}
                                </FormGroup>
                            </Col>
                            <Col>
                                <FormGroup>
                                    <Label for="order-dropoff" style={{ fontSize: '1.1em' }}>
                                        Dropoff Instructions
                                    </Label>
                                    <Input
                                        type="text"
                                        name="dropoff"
                                        id="dropoff-instructions"
                                        placeholder="Gate Code, Apt. #, etc."
                                        value={dropoff || ''}
                                        onChange={e => {
                                            if (e.target.value.length < 510) {
                                                return setDropoff(e.target.value);
                                            } else {
                                                return;
                                            }
                                        }}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        {allowMultipleWaypoints && isInfiniteWaypointsSelected && (
                            <LineItems
                                title="Drop off Item(s)"
                                items={dropoffItems}
                                setItems={setDropoffItems}
                                itemsErrorArray={dropoffItemsError}
                                setItemsErrorArray={setDropoffItemsError}
                                pickList1={pickupItems}
                                pickList2={pickAndDropWaypointsArray.filter(wp => wp.isWaypointPickup).flatMap(wp => wp.pickItems)}
                            />
                        )}
                        {itemsMatchError && (
                            <p style={{ color: 'red', marginTop: '4px', marginBottom: '5px' }}>
                                Please check to make sure quantity or detail of pick up item(s) matching drop off item(s){' '}
                            </p>
                        )}
                    </div>
                    <Divider />
                    <SectionTitle title="Details" tooltipType="details" />
                    {/* <SectionProgressBar gradient="linear-gradient(90deg, #ff7c1a 0%, #ff7c1a 60%, #1da1f2 80%, #7b2ff2 95%, #7b2ff2 100%)" /> */}
                    {/* <SectionProgressBar gradient="linear-gradient(90deg, #f7941d 50%, #344675 100%)" /> */}
                    <SectionProgressBar gradient="linear-gradient(90deg, #f7941d 0%, #f7941d 60%, #344675 80%, #344675 100%)" />
                    <Row style={{ marginBottom: '5px' }}>
                        <Col className="col-6">
                            <FormGroup style={{ paddingTop: 15 }}>
                                <Label for="merchant-order-id" style={{ fontSize: '1.1em' }}>
                                    Order ID
                                </Label>
                                <Input
                                    type="text"
                                    name="merchant_store_id"
                                    id="merchant-store-id"
                                    placeholder="Purchase order/Ticket number"
                                    onChange={e => {
                                        if (e.target.value.length < 510) {
                                            return setMerchantOrderID(e.target.value);
                                        } else {
                                            return;
                                        }
                                    }}
                                    value={merchantOrderID || ''}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    {!isInfiniteWaypointsSelected ? (
                        <LineItems
                            title="Description"
                            items={defaultItems}
                            setItems={setDefaultItems}
                            itemsErrorArray={defaultItemsError}
                            setItemsErrorArray={setDefaultItemsError}
                            pickList1={pickupItems}
                            pickList2={[]}
                            forceExpandAll={forceExpandAllLineItems}
                        />
                    ) : null}
                    {/* AI stuff */}
                    {!isInfiniteWaypointsSelected ? (
                        <div style={{ textAlign: 'left', marginTop: '20px', marginBottom: '30px' }}>
                            <AIRecommendationButton
                                items={defaultItems}
                                setItems={setDefaultItems}
                                pickupAddress={pickupInfo.address}
                                dropAddress={dropoffInfo.address}
                                marketId={(store && store.market_id) || storeMarketId}
                                pickupInstruction={pickup}
                                dropoffInstruction={dropoff}
                                setPickupInstruction={setPickup}
                                setDropoffInstruction={setDropoff}
                                setBrokerPickupNotes={setBrokerPickupNotes}
                                setBrokerDropoffNotes={setBrokerDropoffNotes}
                                setVehicleType={setVehicleType}
                                setWeight={setWeight}
                                setNumPeople={setNumPeople}
                                setIsHandLoadUnload={setIsHandLoadUnload}
                                setIsLiftgate={setIsLiftgate}
                                setIsPalletJack={setIsPalletJack}
                                setIsDolly={setIsDolly}
                                setIsHazmat={setIsHazmat}
                                setIsTWICCard={setIsTWICCard}
                                setIsWeatherProtection={setIsWeatherProtection}
                                setIsMedical={setIsMedical}
                                setIsPPE={setIsPPE}
                                setAIExplanation={setAIExplanation}
                                setShowExplanation={setShowExplanation}
                                setRecommendedWeight={setRecommendedWeight}
                                setLTLPrice={setLTLPrice}
                                setLTLLeadTime={setLTLLeadTime}
                                setRecommendedVehicleType={setRecommendedVehicleType}
                                onAIRecommendationApplied={() => {
                                    // After AI applies, update the last-triggered name to current item name
                                    // so the auto-trigger doesn't re-fire on AI-enriched item names
                                    const currentName = defaultItems && defaultItems[0] ? (defaultItems[0].detail || '').trim() : '';
                                    lastAiTriggeredName.current = currentName;
                                    setForceExpandAllLineItems(prev => prev + 1);
                                }}
                                autoTrigger={true}
                                autoTriggerCount={aiAutoTriggerCount}
                            />
                        </div>
                    ) : null}
                    {!isInfiniteWaypointsSelected && showExplanation ? (
                        <div style={{ textAlign: 'left' }}>
                            <AIResponseExplanation explanation={AIExplanation} />
                        </div>
                    ) : null}
                    {/* Route Optimization for multi-drop orders - only show when there are at least 2 waypoints to reorder */}
                    {isInfiniteWaypointsSelected && pickAndDropWaypointsArray.length >= 2 ? (
                        <div style={{ textAlign: 'left', marginTop: '20px', marginBottom: '30px' }}>
                            <OptimizeRouteButton
                                pickupInfo={pickupInfo}
                                waypointsArray={pickAndDropWaypointsArray}
                                dropoffInfo={dropoffInfo}
                                pickupItems={pickupItems}
                                dropoffItems={dropoffItems}
                                setWaypointsArray={setPickAndDropWaypointsArray}
                                onOptimized={(reorderedWaypoints, result) => {
                                    // Reorder the errors array to match the new waypoint order (0-based indices)
                                    if (result.optimizedOrder && result.optimizedOrder.length === infinitePickAndDropWaypointsErrors.length) {
                                        const reorderedErrors = result.optimizedOrder.map(index => infinitePickAndDropWaypointsErrors[index]);
                                        setInfinitePickAndDropWaypointsErrors(reorderedErrors);
                                    }
                                }}
                            />
                        </div>
                    ) : null}
                    <Divider />
                    <Row className="justify-content-md-center" style={{ textAlign: 'left', marginBottom: 30, marginLeft: 30, flexWrap: 'nowrap', alignItems: 'flex-start' }}>
                        <WightOptions errors={errors} weight={weight} handleWeight={handleWeight} isOverSize={isOverSize} setIsOverSize={setIsOverSize} />
                        <LoadRequirements
                            isHandLoadUnload={isHandLoadUnload}
                            isLiftgate={isLiftgate}
                            isPalletJack={isPalletJack}
                            isDolly={isDolly}
                            isHazmat={isHazmat}
                            isTWICCard={isTWICCard}
                            isWeatherProtection={isWeatherProtection}
                            isMedical={isMedical}
                            isPPE={isPPE}
                            isHandLoadUnloadDisabled={isHandLoadUnloadDisabled}
                            isLiftgateDisabled={isLiftgateDisabled}
                            vehicleType={vehicleType}
                            setIsHandLoadUnload={setIsHandLoadUnload}
                            setIsLiftgate={setIsLiftgate}
                            setIsPalletJack={setIsPalletJack}
                            setIsDolly={setIsDolly}
                            setIsHazmat={setIsHazmat}
                            setIsTWICCard={setIsTWICCard}
                            setIsWeatherProtection={setIsWeatherProtection}
                            setIsMedical={setIsMedical}
                            setIsPPE={setIsPPE}
                        />
                    </Row>
                    <Row className="justify-content-md-center" style={{ textAlign: 'left', marginBottom: 30, marginLeft: 30 }}>
                        {/* <Col md="auto" style={{ marginTop: 30, marginRight: 100 }}>
                            <Label inline style={{ marginLeft: '20px', marginBottom: '20px' }}>
                                How many Gophrs will be needed to load/unload the shipment?
                            </Label>
                            <Row style={{ textAlign: 'center' }}>
                                <FaUserAlt
                                    id="num1"
                                    size="39"
                                    style={{ cursor: 'pointer', marginLeft: '35', marginTop: '8' }}
                                    color={numPeople === 1 ? '#f7941d' : '#5e72e4'}
                                    onClick={() => setNumPeople(1)}
                                />
                                <FaUserFriends
                                    id="num2"
                                    size="55"
                                    style={{ cursor: 'pointer', marginLeft: '30', marginRight: '30' }}
                                    color={numPeople === 2 ? '#f7941d' : '#5e72e4'}
                                    onClick={() => setNumPeople(2)}
                                />

                                <FaUsers
                                    style={{ cursor: 'pointer', marginLeft: '5', marginRight: '30' }}
                                    id="num3"
                                    size="55"
                                    color={numPeople === 3 ? '#f7941d' : '#5e72e4'}
                                    onClick={() => setNumPeople(3)}
                                />
                                <div style={{ cursor: 'pointer', marginTop: '7px', marginRight: '25px' }} onClick={() => setNumPeople(4)}>
                                    <Gophrs4 id="num4" size="55" fill={numPeople === 4 ? '#f7941d' : '#5e72e4'}></Gophrs4>
                                </div>
                                <div style={{ cursor: 'pointer', marginTop: '7px' }} onClick={() => setNumPeople(5)}>
                                    <Gophrs5 id="num5" size="55" fill={numPeople === 5 ? '#f7941d' : '#5e72e4'}></Gophrs5>
                                </div>
                            </Row>
                        </Col> */}
                        <Col md="auto" style={{ marginTop: 30 }}>
                            <VehicleChoices weight={weight} vehicleType={vehicleType} setVehicleType={setVehicleType} isLTL={isLTL} />
                        </Col>
                    </Row>
                    <div hidden={errorMessageSubmit.errorCode !== 8}>
                        <p style={{ color: 'red', marginTop: '4px' }}>{errorMessageSubmit.errorMessage}</p>
                    </div>
                    {/* {sellsAlcohol ? (
                        <FormGroup check className="mt-3" style={{ marginTop: '10px' }}>
                            <Label check style={{ fontSize: '1.1em' }}>
                                <Input
                                    type="checkbox"
                                    id="schedule-check"
                                    onClick={e => {
                                        setContainsAlcohol(!containsAlcohol);
                                    }}
                                />
                                <span className="form-check-sign" />
                                This shipment contains an alcoholic beverage
                            </Label>
                        </FormGroup>
                    ) : null} */}
                    {priceChoice(standardPrice, expeditedPrice, superExpeditedPrice)}
                    <Row style={{ paddingLeft: `15px`, marginTop: '25px' }}>
                        <FormGroup>
                            <Button className="btn cancel-btn" color="default" onClick={reset}>
                                Cancel
                            </Button>
                        </FormGroup>
                    </Row>
                </form>
                </div>
            )}

            {showForm3 && (
                <div style={{ backgroundColor: '#27293d' }} className="container">
                <div style={{ marginTop: '25px', marginBottom: '15px' }}>
                    <SectionTitle title="Review Order" />
                </div>
                <SectionProgressBar gradient="linear-gradient(90deg, #f7941d 100%, #344675 100%)" />
                <div className="review-layout">
                <div className="review-main-column">
                <div className="review-panel">
                    <div style={{ color: '#fff', fontStyle: 'bold', fontSize: '15px', marginBottom: '5px' }}>Pickup Details</div>
                    <div style={{ color: '#cdcaca', marginLeft: '20px' }}>{pickupInfo.name ? pickupInfo.name : null}</div>
                    <div style={{ color: '#cdcaca', marginLeft: '20px' }}>{pickupInfo.phone ? pickupInfo.phone : null}</div>
                    <div style={{ color: '#cdcaca', marginTop: '10px', marginBottom: '10px', marginLeft: '20px' }}>{pickupInfo.address}</div>
                    <div hidden={pickup !== '' && pickup !== null ? false : true} style={{ color: '#fff', fontStyle: 'bold', fontSize: '14px', marginBottom: '5px' }}>
                        Pickup Instructions
                    </div>
                    <div hidden={pickup !== '' ? false : true} style={{ color: '#cdcaca', marginLeft: '20px' }}>
                        {pickup}
                    </div>
                    <hr className="review-divider" />
                </div>

                {allowMultipleWaypoints &&
                    isInfiniteWaypointsSelected &&
                    pickAndDropWaypointsArray.map((dr, index) => {
                        const waypointTitle = dr.isWaypointPickup && dr.isWaypointDropoff ? 'Pickup/Dropoff' : dr.isWaypointPickup ? 'Pickup' : 'Dropoff';
                        const waypointKey = dr.waypointPlaceId || dr.waypointAddressPlaceId || dr.waypointFullAddress || `${waypointTitle}-${index}`;

                        return (
                            <div key={waypointKey} className="review-panel">
                                <div style={{ color: '#fff', fontStyle: 'bold', fontSize: '15px', marginBottom: '5px' }}>
                                    {waypointTitle} ({index + 1}) Details
                                </div>
                                <div style={{ color: '#cdcaca', marginLeft: '20px' }}>{dr.waypointFirstname ? dr.waypointFirstname + ' ' + dr.waypointLastname : null}</div>
                                <div style={{ color: '#cdcaca', marginLeft: '20px' }}>{dr.waypointPhone ? dr.waypointPhone : null}</div>
                                <div style={{ color: '#cdcaca', marginTop: '10px', marginBottom: '10px', marginLeft: '20px' }}>
                                    {dr.waypointFullAddress ? dr.waypointFullAddress : null}
                                </div>
                                <div
                                    hidden={dr.waypointPickupInstruction !== '' && dr.waypointPickupInstruction !== null ? false : true}
                                    style={{ color: '#fff', fontStyle: 'bold', fontSize: '14px', marginBottom: '5px' }}
                                >
                                    Pickup Instructions
                                </div>
                                <div hidden={dr.waypointPickupInstruction !== '' ? false : true} style={{ color: '#cdcaca', marginLeft: '20px' }}>
                                    {dr.waypointPickupInstruction}
                                </div>

                                <div
                                    hidden={dr.waypointDropoffInstruction !== '' && dr.waypointDropoffInstruction !== null ? false : true}
                                    style={{ color: '#fff', fontStyle: 'bold', fontSize: '14px', marginBottom: '5px', marginTop: '5px' }}
                                >
                                    Dropoff Instructions
                                </div>
                                <div hidden={dr.waypointDropoffInstruction !== '' ? false : true} style={{ color: '#cdcaca', marginLeft: '20px' }}>
                                    {dr.waypointDropoffInstruction}
                                </div>
                                <hr className="review-divider" />
                            </div>
                        );
                    })}

                <div className="review-panel">
                    <div style={{ color: '#fff', fontStyle: 'bold', fontSize: '15px', marginBottom: '5px' }}>Dropoff Details</div>
                    <div style={{ color: '#cdcaca', marginLeft: '20px' }}>{dropoffInfo.name ? dropoffInfo.name : null}</div>
                    <div style={{ color: '#cdcaca', marginLeft: '20px' }}>{dropoffInfo.phone ? dropoffInfo.phone : null}</div>
                    <div style={{ color: '#cdcaca', marginTop: '10px', marginBottom: '10px', marginLeft: '20px' }}>{dropoffInfo.address ? dropoffInfo.address : null}</div>
                    <div hidden={dropoff !== '' && dropoff !== null ? false : true} style={{ color: '#fff', fontStyle: 'bold', fontSize: '14px', marginBottom: '5px' }}>
                        Dropoff Instructions
                    </div>
                    <div hidden={dropoff !== 'N/A' ? false : true} style={{ color: '#cdcaca', marginLeft: '20px' }}>
                        {dropoff}
                    </div>

                    <hr className="review-divider" />
                </div>

                <div className="review-panel">
                    <div style={{ color: '#fff', fontStyle: 'bold', fontSize: '13px', marginBottom: '5px' }}>Shipment Description</div>
                    <div style={{ color: '#cdcaca', marginLeft: '12px', marginBottom: '5px' }}>
                        {(!allowMultipleWaypoints || (allowMultipleWaypoints && !isInfiniteWaypointsSelected)) &&
                            defaultItems.length > 0 &&
                            defaultItems.map((item, idx) => {
                                return (
                                    <p key={item.id || item.item_id || `${item.detail || 'default-item'}-${idx}`} style={{ marginLeft: '8px' }}>
                                        {item.quantity} -- {item.detail}
                                    </p>
                                );
                            })}

                        {allowMultipleWaypoints &&
                            isInfiniteWaypointsSelected &&
                            pickupItems.length > 0 &&
                            pickupItems.map((item, idx) => {
                                return (
                                    <p key={item.id || item.item_id || `${item.detail || 'pickup-item'}-${idx}`} style={{ marginLeft: '8px' }}>
                                        {item.quantity} -- {item.detail}
                                    </p>
                                );
                            })}
                        {allowMultipleWaypoints &&
                            isInfiniteWaypointsSelected &&
                            pickAndDropWaypointsArray.map((waypoint, waypointIdx) => {
                                if (waypoint.isWaypointPickup && waypoint.pickItems && waypoint.pickItems.length > 0) {
                                    return waypoint.pickItems.map((item, itemIdx) => {
                                        if (item.detail !== '') {
                                            return (
                                                <p key={`waypoint-${waypointIdx}-item-${itemIdx}`} style={{ marginLeft: '8px' }}>
                                                    {item.quantity} -- {item.detail}
                                                </p>
                                            );
                                        }
                                        return null;
                                    });
                                }
                                return null;
                            })}
                    </div>

                    <div hidden={!merchantOrderID} style={{ color: '#fff', fontStyle: 'bold', fontSize: '14px', marginBottom: '5px' }}>
                        Store Order ID
                    </div>
                    <div hidden={!merchantOrderID} style={{ color: '#cdcaca', marginLeft: '20px', marginBottom: '5px' }}>
                        {merchantOrderID}
                    </div>
                    <div style={{ color: '#fff', fontStyle: 'bold', fontSize: '13px', marginBottom: '5px' }}>Appropriate Vehicle Type</div>
                    <div style={{ color: '#cdcaca', marginLeft: '20px', marginBottom: '5px' }}>{formatShipmentVehicleTypeLabel(vehicleType)}</div>

                    {/* <div style={{ color: '#fff', fontStyle: 'bold', fontSize: '14px', marginBottom: '5px' }}>Number of Gophrs Needed</div>
                    <div style={{ color: '#cdcaca', marginLeft: '20px', marginBottom: '5px' }}>{numPeople}</div> */}
                    {scheduledFor ? (
                        <div>
                            <div style={{ color: '#fff', fontStyle: 'bold', fontSize: '13px', marginBottom: '5px' }}>Scheduled For</div>
                            <div style={{ color: '#cdcaca', marginLeft: '20px', marginBottom: '5px' }}>{formatTimestamp(scheduledFor)}</div>
                        </div>
                    ) : null}

                    <hr className="review-divider" />
                </div>

                {/* Documents Upload - Full Width */}
                {!isInfiniteWaypointsSelected && (
                    <div className="review-panel">
                        <div style={{ color: '#fff', fontStyle: 'bold', fontSize: '14px', marginBottom: '5px' }}>Documents Upload(Optional)</div>
                        <div style={{ maxWidth: '420px' }}>
                            <FileUploadBeforeShipment onFileReadyForUpload={handleFileReady} />
                        </div>
                    </div>
                )}
                </div>

                <div className="review-side-column">
                    <div className="review-sidebar-stack">
                        <div className="review-panel">
                            <div className="review-section-heading">Payment & Shipping Details</div>
                            <div className="review-summary-row">
                                <div style={{ color: '#fff', fontStyle: 'bold', fontSize: '14px' }}>Shipping Type:</div>
                                <div style={{ color: '#fff', fontSize: '14px', textAlign: 'right' }}>
                                    {isSuperExpedited ? 'Super Expedited' : isExpedited ? 'Expedited' : 'Standard'}
                                </div>
                            </div>

                            <div className="review-summary-row">
                                <div style={{ color: '#fff', fontStyle: 'bold', fontSize: '14px' }}>Total Shipping Cost:</div>
                                <div>
                                    {(!(isActiveEdit && !hasActiveEditPricingChanges) && (((vehicleType === '18_wheeler_dry_van' || vehicleType === '18_wheeler_flat_bed' || vehicleType === '18_wheeler_reefer') && !hasTqlRateValue) ||
                                    vehicleType === 'car_hauler')) ? (
                                        priceRangeLow && (
                                            <div style={{ color: '#f7941d', fontStyle: 'bold', fontSize: '16px' }}>
                                                ${parseFloat(priceRangeLow).toFixed(2)} - ${parseFloat(priceRangeHigh).toFixed(2)}
                                            </div>
                                        )
                                    ) : (
                                        <div style={{ color: '#f7941d', fontStyle: 'bold', fontSize: '16px' }}>{price ? '$' + price.toFixed(2) : null}</div>
                                    )}
                                </div>
                            </div>
                            {isActiveEdit && Number.isFinite(activeEditOriginalPrice) && Number.isFinite(Number(price)) && (
                                <>
                                    <div className="review-summary-row">
                                        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>Original price:</div>
                                        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px', textAlign: 'right' }}>${activeEditOriginalPrice.toFixed(2)}</div>
                                    </div>
                                    <div className="review-summary-row">
                                        <div
                                            style={{
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                color: Number(price) - activeEditOriginalPrice > 0 ? '#ff6b6b' : Number(price) - activeEditOriginalPrice < 0 ? '#28a745' : '#fff',
                                            }}
                                        >
                                            {Number(price) - activeEditOriginalPrice > 0 ? 'Additional charge:' : Number(price) - activeEditOriginalPrice < 0 ? 'Refund:' : 'No price change'}
                                        </div>
                                        <div
                                            style={{
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                textAlign: 'right',
                                                color: Number(price) - activeEditOriginalPrice > 0 ? '#ff6b6b' : Number(price) - activeEditOriginalPrice < 0 ? '#28a745' : '#fff',
                                            }}
                                        >
                                            {Number(price) - activeEditOriginalPrice > 0 ? '+' : Number(price) - activeEditOriginalPrice < 0 ? '-' : ''}${Math.abs(Number(price) - activeEditOriginalPrice).toFixed(2)}
                                        </div>
                                    </div>
                                </>
                            )}
                            {quoteVolumeDiscountPercent > 0 && quoteVolumeDiscountAmount > 0 && (
                                <div className="review-summary-row">
                                    <div style={{ color: '#28a745', fontSize: '13px' }}>
                                        Volume Discount {quoteVolumeDiscountPercent}% applied
                                    </div>
                                    <div style={{ color: '#28a745', fontSize: '13px', fontWeight: 600 }}>
                                        -${Number(quoteVolumeDiscountAmount).toFixed(2)} saved
                                    </div>
                                </div>
                            )}

                            <hr className="review-divider" />

                            <div style={preferredDriverLocked ? { opacity: 0.45, filter: 'grayscale(1)', pointerEvents: 'none' } : undefined}>
                                <PreferredDriverSelect
                                    pickupLat={pickLat != null ? pickLat : storeLat}
                                    pickupLng={pickLng != null ? pickLng : storeLng}
                                    marketId={storeMarketId || (store && store.market_id)}
                                    vehicleType={vehicleType}
                                    value={preferredDriver}
                                    onChange={setPreferredDriver}
                                />
                            </div>
                            {preferredDriverLocked ? <UpgradeHint label="Upgrade to Pro to choose a preferred driver" /> : null}

                            <hr className="review-divider" />

                            <PaymentMethodDisplay
                                shouldFetch={showForm3}
                                storeId={storeId}
                                adminStoreProfileLookup={true}
                                cardAuth0Id={onBehalfMerchantAuth0Id || storeMerchantAuth0Id || null}
                                merchantAuth0Ids={storeMerchantAuth0Ids}
                                merchantProfileSeed={{
                                    auth0Id: storeMerchantAuth0Id,
                                    auth0Ids: storeMerchantAuth0Id ? [storeMerchantAuth0Id] : [],
                                    first_name: storeFirstName || '',
                                    last_name: storeLastName || '',
                                    email: storeEmail || '',
                                    billing_email: storeEmail || '',
                                    store_id: storeId || '',
                                    store_name: storeName || '',
                                }}
                                onPaymentMethodSelect={setSelectedPaymentMethodId}
                                selectedPaymentMode={selectedPaymentMode}
                                onPaymentModeChange={setSelectedPaymentMode}
                                netTermsLocked={netTermsLocked}
                                onPaymentProfileStateChange={setPaymentProfileState}
                                paymentProfileState={paymentProfileState}
                                onError={() => null}
                            />
                        </div>

                        {showInlinePaymentRetry && currentShipmentId && (
                            <div
                                className="review-panel"
                                style={{
                                    border: '1px solid #ff6b6b',
                                    backgroundColor: 'rgba(220, 53, 69, 0.15)',
                                    color: '#ffdede',
                                }}
                            >
                                <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>Payment failed for this order</div>
                                <div style={{ marginBottom: '10px' }}>{inlinePaymentRetryMessage}</div>
                                <Button
                                    className="btn"
                                    color="danger"
                                    onClick={() => setPaymentRetryModalOpen(true)}
                                    style={{ marginRight: '10px' }}
                                >
                                    Retry Payment
                                </Button>
                                {currentInvoiceLinks && currentInvoiceLinks.hostedUrl ? (
                                    <Button
                                        className="btn"
                                        color="primary"
                                        onClick={() => window.open(currentInvoiceLinks.hostedUrl, '_blank', 'noopener,noreferrer')}
                                        style={{ marginRight: '10px' }}
                                    >
                                        Pay Invoice
                                    </Button>
                                ) : null}
                                {currentShipmentFriendlyId ? (
                                    <span style={{ color: '#ffc9c9' }}>Order ID: {currentShipmentFriendlyId}</span>
                                ) : null}
                            </div>
                        )}

                        <div className="review-panel">
                            <div className="review-action-grid">
                                <div className="review-action-column">
                                    <Button className="btn review-action-button" color="default" onClick={() => handleBack()}>
                                        Go Back
                                    </Button>
                                    <Button className="btn cancel-btn review-action-button" color="default" onClick={reset}>
                                        Cancel Order
                                    </Button>
                                </div>

                                <div className="review-action-column">
                                    {isScheduled ? (
                                        <Button
                                            className="btn submit-btn review-action-button"
                                            color="danger"
                                            onClick={e => {
                                                handleConfirmWithFeedback('shipment_scheduled', 'order_scheduled', isScheduled);
                                            }}
                                            disabled={isOrderSubmitDisabled}
                                        >
                                            {((vehicleType === '18_wheeler_dry_van' || vehicleType === '18_wheeler_flat_bed' || vehicleType === '18_wheeler_reefer') && !hasTqlRateValue) ||
                                            vehicleType === 'car_hauler'
                                                ? 'Get Quote and Schedule'
                                                : 'Schedule This Order'}
                                        </Button>
                                    ) : (
                                        <>
                                            <Button
                                                className="btn submit-btn review-action-button"
                                                color="danger"
                                                onClick={e => {
                                                    handleConfirmWithFeedback('shipment_ready_for_driver', 'awaiting_driver', isScheduled);
                                                }}
                                                disabled={isOrderSubmitDisabled}
                                            >
                                                {((vehicleType === '18_wheeler_dry_van' || vehicleType === '18_wheeler_flat_bed' || vehicleType === '18_wheeler_reefer') && !hasTqlRateValue) ||
                                                vehicleType === 'car_hauler'
                                                    ? 'Get Quote'
                                                    : 'Ready for Pick Up'}
                                            </Button>
                                            {vehicleType === '18_wheeler_dry_van' ||
                                            vehicleType === '18_wheeler_flat_bed' ||
                                            vehicleType === '18_wheeler_reefer' ||
                                            vehicleType === 'car_hauler' ||
                                            isFedex ? null : (
                                                <Button
                                                    className="btn cancel-btn review-action-button"
                                                    color="default"
                                                    onClick={e => {
                                                        setIsSaved(true);
                                                        handleConfirmWithFeedback('shipment_saved', 'order_saved', isScheduled);
                                                    }}
                                                    disabled={isOrderSubmitDisabled}
                                                >
                                                    Save for Later
                                                </Button>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

                            <div style={{ marginTop: '12px' }}>
                                <FeedbackMessage message={submissionMessage} showSpinner={submissionMessage.includes('...')} />
                            </div>
                        </div>
                    </div>
                </div>
                </div>
                </div>
            )}
            <LocationMapPickerModal
                isOpen={activeWaypointPinIndex >= 0}
                toggle={() => setActiveWaypointPinIndex(-1)}
                title="Select Additional Stop Location"
                initialLocation={currentWaypointPinLocation}
                onConfirm={location => {
                    if (activeWaypointPinIndex < 0) {
                        return;
                    }

                    handleDropWaypointSelect(location, location.lat, location.lng, activeWaypointPinIndex);
                    setPickAndDropWaypointsArray(
                        pickAndDropWaypointsArray.map((wp, i) => (i === activeWaypointPinIndex ? { ...wp, isPinnedLocation: true, isWaypointAutoFilled: false } : wp))
                    );
                    setActiveWaypointPinIndex(-1);
                }}
            />

            <LocationMapPickerModal
                isOpen={isPickupPinModalOpen}
                toggle={() => setIsPickupPinModalOpen(open => !open)}
                title="Select Pickup Location"
                initialLocation={currentPickupPinLocation}
                onConfirm={location => {
                    handlePickupSelect(location);
                    setIsPickupAutoFilled(false);
                }}
            />

            <LocationMapPickerModal
                isOpen={isDropoffPinModalOpen}
                toggle={() => setIsDropoffPinModalOpen(open => !open)}
                title="Select Dropoff Location"
                initialLocation={currentDropoffPinLocation}
                onConfirm={location => {
                    handleDropoffSelect(location);
                    setIsDropOffAutoFilled(false);
                }}
            />

            <FedexLabelModal
                isOpen={showFedexLabelModal}
                toggle={() => {
                    setShowFedexLabelModal(false);
                    // Only reload after the modal is closed
                    setTimeout(() => {
                        window.location.reload();
                    }, 500);
                }}
                shipmentId={currentShipmentId}
            />

            <PaymentRetryModal
                isOpen={paymentRetryModalOpen}
                toggle={() => setPaymentRetryModalOpen(false)}
                shipmentId={currentShipmentId}
                shipmentFriendlyId={currentShipmentFriendlyId}
                shipmentData={currentShipmentData}
                fromOrderCreation={true}
                onSuccess={data => {
                    void data;
                    setPaymentRetryModalOpen(false);
                    setShowInlinePaymentRetry(false);
                    setInlinePaymentRetryMessage('');

                    // Check if there's a pending FedEx label to show
                    if (hasPendingFedexLabel) {
                        setShowFedexLabelModal(true);
                        setSubmissionMessage('Payment successful! Please review your FedEx label.');
                    } else {
                        props.notifySuccess('tc', 'Payment successful! Your order is now active.');
                        setSubmissionMessage('Payment successful! Your order is now active.');
                        setTimeout(() => window.location.reload(), 2000);
                    }
                }}
                onClose={() => {
                    // User closed modal without paying - order remains in pending payment status
                    return null;
                }}
            />
        </>
    );
}
